// PRISM API Service Layer
import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  UnifiedProject,
  ProjectStandard,
  CreateProjectRequest,
  UpdateProjectRequest,
  AddStandardRequest,
  ModuleType,
} from '../types/unifiedProject';
import type { JourneyState, JourneyStepDetail, AdvanceResult } from '../components/project/journey/types';
import type {
  DecideResult,
  HeldResolution,
  ItemDecision,
  ProposalListResponse,
  ProposalRow,
  ResolveResult,
} from '../types/architectureProposals';
import type {
  AllocationResponse,
  AssetKind,
  AssetKindsResponse,
  ConsolidationResponse,
  SpendCategory,
  SuggestProgress,
} from '../types/costOptimization';
import type { RatingScale } from '../shared/ratingScales';
import { normalizeBlobErrorResponse } from './blobError';
import type { DocPenaltyWire, CtlPenaltyWire } from '../types/lifecyclePenalty';
import {
  ENDED_NOTICE,
  READ_ONLY_COPY,
  clearImpersonationStash,
  currentTokenIsViewAs,
  hasImpersonationStash,
  impersonationRestoreStarted,
  restoreImpersonator,
} from './impersonationSession';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/** How the current session was signed in; decides whether sign-out also ends the Entra session. */
const SIGNED_IN_WITH_KEY = 'prism_signed_in_with';

// Create axios instance with defaults
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds for most requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('prism_token') || localStorage.getItem('prism_token');
    // A sign-in call carries no session: a stored view-as token would make the guard refuse it as a change.
    if (token && !isUnauthenticatedAuthUrl(config.url || '')) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
// Tracks whether we've already begun the redirect for a 401 in this tab,
// so multiple in-flight requests don't each trigger their own redirect
// + overwrite the error message (would race to the least-friendly one).
let sessionExpiredHandled = false;

/** Sign-in endpoints: a refusal there is the sign-in's own answer, never an expiry of an existing session. */
function isSignInUrl(url: string): boolean {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/bridge') ||
    url.includes('/auth/appliance') ||
    url.includes('/auth/oidc/complete')
  );
}

/**
 * Calls made WITHOUT a session (sign-in, redeem, reset, activate). /auth/register is deliberately absent:
 * it is an authenticated admin call and needs its bearer.
 */
function isUnauthenticatedAuthUrl(url: string): boolean {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/bridge') ||
    url.includes('/auth/appliance') ||
    url.includes('/auth/oidc/complete') ||
    url.includes('/auth/reset-password') ||
    url.includes('/auth/activate')
  );
}

/** Keep the machine code on `code`; put the client's words on `error`, which is what components display. */
function rewriteRefusal(error: AxiosError, code: string, copy: string): void {
  const data = error.response?.data;
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    d.code = code;
    d.error = copy;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Downloads are requested with `responseType: 'blob'`, so their error
    // bodies arrive as a Blob and every read below would miss. Parse it back
    // into JSON first, so one code path serves downloads and everything else.
    await normalizeBlobErrorResponse(error);

    // Session-expired detection: 401 (anywhere) OR 403 specifically with the
    // backend's "Invalid or expired token" payload from `middleware/auth.ts`.
    // The 403+message check is narrow on purpose â€” generic 403s (viewer
    // trying admin actions, permission denied, etc.) must NOT log the user
    // out. Cloud 7 (2026-05-15) reported the raw "Invalid or expired token"
    // surfacing because JWT-verify in the backend returns 403, not 401.
    const status = error.response?.status;
    const errorMessage = (error.response?.data as any)?.error;
    let isSessionExpired =
      status === 401 ||
      (status === 403 && errorMessage === 'Invalid or expired token');

    // View-as (spec 2026-09-29-impersonation-read-only-and-audit §2): the backend guard refuses with its own
    // codes, and neither is a session expiry. A read-only refusal keeps the session. An ended view-as — or an
    // expiry while the superadmin's own session is stashed — restores that session instead of signing out.
    if (status === 403 && errorMessage === 'impersonation_read_only') {
      rewriteRefusal(error, 'impersonation_read_only', READ_ONLY_COPY);
      return Promise.reject(error);
    }
    // An expiry restores ONLY when the token that expired is a view-as token: a stash left behind by a
    // closed tab must never hand the superadmin's session to whoever signed in next on this browser.
    // impersonation_ended is sent only to operator view-as tokens, so it restores without that check.
    const endedRefusal = status === 403 && errorMessage === 'impersonation_ended';
    const viewAsOver =
      endedRefusal ||
      (isSessionExpired &&
        !isSignInUrl(error.config?.url || '') &&
        ((hasImpersonationStash() && currentTokenIsViewAs()) || impersonationRestoreStarted()));
    if (viewAsOver) {
      rewriteRefusal(error, 'impersonation_ended', ENDED_NOTICE);
      if (restoreImpersonator(ENDED_NOTICE)) {
        return Promise.reject(error);
      }
      // No stash to restore (e.g. a platform-bridge imp token): while the stored token is still a view-as
      // token it is dead, so take the normal session-expired path below. If it is not (another tab already
      // restored), there is nothing to clear.
      if (endedRefusal && currentTokenIsViewAs()) {
        isSessionExpired = true;
      } else {
        return Promise.reject(error);
      }
    }

    if (isSessionExpired) {
      // Don't redirect on login/register/bridge endpoints â€” let the component show the error
      const url = error.config?.url || '';
      // /auth/appliance: a refused one-time link must show its own message, not bounce to /login.
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/bridge') || url.includes('/auth/appliance');
      if (!isAuthEndpoint) {
        // Replace the raw backend error ("Invalid or expired token") with
        // user-friendly copy BEFORE the component's catch block reads
        // err.response.data.error. Components that show the message in a
        // snackbar will display this instead of the technical string.
        try {
          if (error.response?.data && typeof error.response.data === 'object') {
            (error.response.data as any).error = 'Your session expired. Redirecting to loginâ€¦';
          }
        } catch {
          // fallback: data wasn't an object; component falls back to its own default message
        }

        if (!sessionExpiredHandled) {
          sessionExpiredHandled = true;
          // Read before clearing: after an Entra sign-in, a refused session (expired, or the account
          // deactivated) must also end the Entra session, exactly as sign-out does.
          const viaEntra = localStorage.getItem(SIGNED_IN_WITH_KEY) === 'entra';
          localStorage.removeItem(SIGNED_IN_WITH_KEY);
          sessionStorage.removeItem('prism_token');
          sessionStorage.removeItem('prism_user');
          localStorage.removeItem('prism_token');
          localStorage.removeItem('prism_user');
          localStorage.removeItem('token');
          localStorage.removeItem('userRole');
          localStorage.removeItem('organizationId');
          // 800ms delay so the user actually sees the session-expired
          // snackbar before the page reloads. Otherwise window.location
          // fires before React renders the error state and the message
          // is invisible.
          setTimeout(() => {
            window.location.href = viaEntra ? `${API_BASE_URL}/auth/oidc/logout` : '/login';
          }, 800);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Type definitions
/** One crosswalk edge touching a control (GET /v2/compliance/crosswalk/control). */
export interface ControlCrosswalkRow {
  standard_id: string;
  standard_name: string;
  requirement_code: string;
  strm_relationship: string | null;
  mapping_strength: number | null;
  /** 'Owner' / 'Non-Owner' / 'Derived' / 'Authored' / null — a plain string; see crosswalkAuthority.ts. */
  source_authority: string | null;
  source_olir_id: number | null;
  /** 'in' = the edge was authored towards this control (the reverse arm). */
  direction: 'out' | 'in';
}

export interface ClosePackageJob {
  jobId: string;
  status: 'building' | 'ready' | 'failed' | 'expired' | 'confirmed';
  sizeBytes: number | null;
  error: string | null;
  createdAt: string;
  readyAt: string | null;
  expiresAt: string;
  confirmedAt: string | null;
}

export interface EngagementTombstone {
  id: number;
  project_id: number;
  state: 'purging' | 'complete' | 'incomplete';
  closed_by_email: string;
  confirmed_at: string;
  purge_completed_at: string | null;
  package_sha256: string;
  backups_expire_after: string | null;
  backups_note: string;
  counts: Record<string, unknown>;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'lead' | 'analyst' | 'viewer';
  organization_id: number;
  is_superadmin?: boolean;
}

export interface Project {
  id: number;
  name: string;
  description: string;
  mission_statement: string;
  architecture_type: string;
  analysis_phase: string;
  status: string;
  created_at: string;
  updated_at: string;
  // v2 Unified Project fields
  module_architecture?: boolean;
  module_compliance?: boolean;
  module_threat_model?: boolean;
  module_evidence?: boolean;
  engagement_type?: string;
  industry_sector?: string;
  equipment_type?: string;
  client_name?: string;
  deadline?: string;
  start_date?: string;
  target_completion?: string;
  organization_name?: string;
  // Computed counts
  standard_count?: number;
  asset_count?: number;
  scored_techniques_count?: number;
}

export interface Asset {
  id: string;
  project_id: number;
  name: string;
  type: 'server' | 'workstation' | 'network-device' | 'application' | 'database' | 'cloud-service' | 'external' | string;
  description?: string;
  ip_address?: string;
  operating_system?: string;
  criticality: 'low' | 'medium' | 'high' | 'critical' | string;
  tier: string;
  /** DoD network tier ('Tier 0'..'Tier 4'); blank makes every connected flow "Needs Review" */
  dod_tier?: string | null;
  is_external?: boolean;
  annual_cost?: number;
  annual_license_cost?: number;
  annual_maintenance_cost?: number;
  fte_allocation?: number;
  // Contract lifecycle
  vendor_name?: string;
  contract_vehicle?: string;
  contract_start_date?: string;
  contract_expiry?: string;
  contract_renewal_date?: string;
  renewal_type?: 'auto' | 'manual' | 'one_time';
  pom_cycle_year?: number;
  contract_term_months?: number;
  replacement_lead_time?: number;
  created_at: string;
  updated_at: string;
}

export interface DataFlow {
  id: string;
  project_id: number;
  source_asset_id: string;
  destination_asset_id: string;
  /** ports_protocols_services — the GET route aliases it AS protocol */
  protocol: string;
  port?: number;
  direction: 'inbound' | 'outbound' | 'bidirectional';
  data_classification: 'unclassified' | 'cui' | 'secret' | 'top-secret' | string;
  description?: string;
  name?: string | null;
  // Tier review (migration 439) — computed by the DB trigger, never written by the UI.
  tier_compliance_status?: 'compliant' | 'violation' | 'needs_review' | 'approved_exception' | null;
  tier_violation_reason?: string | null;
  source_dod_tier?: string | null;
  target_dod_tier?: string | null;
  exception_reason?: string | null;
  exception_approved_by?: number | null;
  exception_approved_by_name?: string | null;
  exception_approved_at?: string | null;
}

/** The fields PUT /system/data-flows/:id accepts; anything else is ignored by the server. */
export interface DataFlowEditFields {
  name?: string;
  description?: string | null;
  ports_protocols_services?: string | null;
  direction?: DataFlow['direction'];
  data_classification?: string;
}

/** A MITRE D3FEND defensive technique this countermeasure's capability is anchored to. */
export interface D3fendCitation {
  d3fendId: string;
  d3fendLabel: string | null;
  /** The capability carrying the anchor — rendered as "via EPP-002". */
  viaCapability: string;
}

export interface Countermeasure {
  id: string;
  project_id: number;
  name: string;
  type: 'preventive' | 'detective' | 'corrective' | 'deterrent' | 'compensating';
  tier: string;
  description?: string;
  effectiveness_rating: number;
  implementation_status: string;
  cost_estimate?: number;
  nist_controls?: string[];
  /**
   * Empty is a real state, not a missing fetch: a countermeasure that resolves to no
   * detective capability (a SIEM is an analyst surface, not a sensor) correctly has none.
   * ⛔ Only MITRE-authored anchors appear here — PRISM-inferred ones are excluded server-side.
   */
  d3fend_citations?: D3fendCitation[];
  /**
   * Decision 4.5 — which of the three (now four) empty-card situations applies when
   * `d3fend_citations` is empty. `cited` is the normal non-empty case; the other three
   * distinguish a genuine absence, an un-attributable scorer credit, and an ordinary
   * preventive control from each other, where the card used to render all of them as one
   * sentence. See `D3fendCitations.tsx`.
   */
  citation_state?: 'cited' | 'not_mapped' | 'credited_not_attributable' | 'protective';
  /**
   * Strongest attestation across every capability this countermeasure resolved to (not just
   * the ones cited) — `product` when a named vendor/product string matched, `category` when
   * only a generic synonym (EDR, NGFW, MFA, …) matched, `null` when nothing resolved.
   * `category` is NOT a weak control — it is no product named in this field.
   */
  attestation?: 'product' | 'category' | null;
}

// Authentication API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await apiClient.post('/auth/login', { email, password });
    // OTP-gated path returns { success, step: 'otp_sent', ... } and no token â€”
    // the caller handles the second step. Direct path (local dev, no SES)
    // still returns a token here.
    if (response.data.data?.token) {
      // A new sign-in owns this browser: a stash left by an unfinished view-as must never outlive it.
      clearImpersonationStash();
      localStorage.setItem('prism_token', response.data.data.token);
      localStorage.removeItem(SIGNED_IN_WITH_KEY);
      localStorage.setItem('prism_user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  },

  loginVerifyOtp: async (email: string, otp: string) => {
    const response = await apiClient.post('/auth/login/verify', { email, otp });
    if (response.data.data?.token) {
      // A new sign-in owns this browser: a stash left by an unfinished view-as must never outlive it.
      clearImpersonationStash();
      localStorage.setItem('prism_token', response.data.data.token);
      localStorage.removeItem(SIGNED_IN_WITH_KEY);
      localStorage.setItem('prism_user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  },

  register: async (userData: { email: string; password: string; first_name: string; last_name: string; organization_id?: number }) => {
    const response = await apiClient.post('/auth/register', userData);
    if (response.data.data?.token) {
      // A new sign-in owns this browser: a stash left by an unfinished view-as must never outlive it.
      clearImpersonationStash();
      localStorage.setItem('prism_token', response.data.data.token);
      localStorage.removeItem(SIGNED_IN_WITH_KEY);
      localStorage.setItem('prism_user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  },

  logout: () => {
    const token = sessionStorage.getItem('prism_token') || localStorage.getItem('prism_token');
    const viaEntra = localStorage.getItem(SIGNED_IN_WITH_KEY) === 'entra';
    // Tell the server first, so the sign-out is recorded. `keepalive` lets the request finish while
    // the page navigates away; a failure must never keep anyone signed in.
    // Signing out mid-view-as also closes the view-as session, so its token stops working now (best-effort).
    if (token && currentTokenIsViewAs()) {
      try {
        // Explicit token + keepalive: the local token is cleared below, before an interceptor could read it.
        void fetch(`${API_BASE_URL}/admin/impersonation/end`, {
          method: 'POST',
          keepalive: true,
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => undefined);
      } catch {
        /* sign out regardless */
      }
    }
    if (token) {
      try {
        void fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          keepalive: true,
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => undefined);
      } catch {
        /* sign out regardless */
      }
    }
    // A stash left behind by a sign-out mid-view-as would be "restored" by the next session's first expiry.
    clearImpersonationStash();
    localStorage.removeItem(SIGNED_IN_WITH_KEY);
    sessionStorage.removeItem('prism_token');
    sessionStorage.removeItem('prism_user');
    localStorage.removeItem('prism_token');
    localStorage.removeItem('prism_user');
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('organizationId');
    // After an Entra sign-in, clearing PRISM's token is not enough: Entra's own session would sign
    // the person straight back in on the next "Sign in with Entra". End that session too.
    window.location.href = viaEntra ? `${API_BASE_URL}/auth/oidc/logout` : '/login';
  },

  getCurrentUser: (): User | null => {
    const userStr = sessionStorage.getItem('prism_user') || localStorage.getItem('prism_user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Self-service password change. Used by Settings â†’ Change Password card.
  // Admin-provisioned users land here after their first OTP-gated login to
  // swap their temp password for one only they know. JWT remains valid;
  // future logins are OTP-gated anyway.
  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },
};

/** Which login the backend is offering. Runtime, so a flag flip needs no frontend rebuild. */
export const getAuthMode = async (): Promise<{ oidc: boolean; password: boolean }> => {
  try {
    const response = await apiClient.get('/auth/mode');
    const data = response.data?.data;
    return {
      oidc: data?.oidc === true,
      // Only an explicit `false` hides the password form. The backend enforces
      // PRISM_PASSWORD_LOGIN regardless; this decides what the page offers.
      password: data?.password !== false,
    };
  } catch {
    // Degrade to the password form. An unreachable mode endpoint must never hide the only
    // login that is known to work.
    return { oidc: false, password: true };
  }
};

/**
 * Is this PRISM a customer-hosted appliance? (spec 2026-09-29-appliance-administration §2.1) Runtime, from
 * /auth/mode. Only an explicit `appliance: true` counts: an older backend, an error or anything else keeps
 * today's hosted pages.
 */
export const getApplianceMode = async (): Promise<boolean> => {
  try {
    const response = await apiClient.get('/auth/mode');
    return response.data?.data?.appliance === true;
  } catch {
    return false;
  }
};

/**
 * Redeem the opaque completion code minted by the OIDC callback.
 * Persists the session here, matching the sibling `authAPI` login functions above — the storage
 * keys are `prism_token` / `prism_user`, which is what the request interceptor reads.
 */
export const completeOidcLogin = async (code: string) => {
  const response = await apiClient.post('/auth/oidc/complete', { code });
  // A new sign-in owns this browser: a stash left by an unfinished view-as must never outlive it.
  clearImpersonationStash();
  localStorage.setItem('prism_token', response.data.data.token);
  localStorage.setItem('prism_user', JSON.stringify(response.data.data.user));
  localStorage.setItem(SIGNED_IN_WITH_KEY, 'entra');
  return response.data.data;
};

/** Customer-hosted appliance only — the route 404s on hosted PRISM. */
export const redeemApplianceToken = async (token: string) => {
  const response = await apiClient.post('/auth/appliance/redeem', { token });
  // A new sign-in owns this browser: a stash left by an unfinished view-as must never outlive it.
  clearImpersonationStash();
  localStorage.setItem('prism_token', response.data.data.token);
  localStorage.removeItem(SIGNED_IN_WITH_KEY);
  localStorage.setItem('prism_user', JSON.stringify(response.data.data.user));
  return response.data.data;
};

// ---------- Admin Console API (superadmin-gated) ----------
export const adminAPI = {
  listOrganizations: async () => {
    const r = await apiClient.get('/admin/organizations');
    return r.data.data;
  },
  frameworkRequests: async (): Promise<{ ranked: any[]; recent: any[] }> => {
    const res = await apiClient.get('/admin/framework-requests');
    return { ranked: res.data?.ranked ?? [], recent: res.data?.recent ?? [] };
  },
  createOrganization: async (body: {
    name: string;
    domain?: string;
    industry?: string;
    size?: string;
    entitlement_tier?: string;
  }) => {
    const r = await apiClient.post('/admin/organizations', body);
    return r.data.data;
  },
  getOrganization: async (orgId: number) => {
    const r = await apiClient.get(`/admin/organizations/${orgId}`);
    return r.data.data;
  },
  createUser: async (
    orgId: number,
    body: { email: string; first_name: string; last_name: string; role?: string }
  ) => {
    const r = await apiClient.post(`/admin/organizations/${orgId}/users`, body);
    return r.data.data; // includes temp_password
  },
  resetPassword: async (userId: number) => {
    const r = await apiClient.post(`/admin/users/${userId}/reset-password`);
    return r.data.data; // includes temp_password
  },
  deactivateUser: async (userId: number) => {
    const r = await apiClient.put(`/admin/users/${userId}/deactivate`);
    return r.data.data;
  },
  activateUser: async (userId: number) => {
    const r = await apiClient.put(`/admin/users/${userId}/activate`);
    return r.data.data;
  },
  createProject: async (orgId: number, body: { name: string; created_by?: number }) => {
    const r = await apiClient.post(`/admin/organizations/${orgId}/projects`, body);
    return r.data.data;
  },
  listTeamMembers: async (projectId: number) => {
    const r = await apiClient.get(`/admin/projects/${projectId}/team-members`);
    return r.data.data;
  },
  addTeamMember: async (projectId: number, body: { user_id: number; role?: string }) => {
    const r = await apiClient.post(`/admin/projects/${projectId}/team-members`, body);
    return r.data.data;
  },
  removeTeamMember: async (projectId: number, userId: number) => {
    const r = await apiClient.delete(`/admin/projects/${projectId}/team-members/${userId}`);
    return r.data;
  },
  updateOrgNotes: async (orgId: number, internal_notes: string) => {
    const r = await apiClient.put(`/admin/organizations/${orgId}/notes`, { internal_notes });
    return r.data.data;
  },
  updateOrgEntitlements: async (
    orgId: number,
    body: { entitlement_tier?: string; max_frameworks?: number | null }
  ) => {
    const r = await apiClient.put(`/admin/organizations/${orgId}/entitlements`, body);
    return r.data.data;
  },
  // reason: why the session is being started (NOVEC S8) — required by the server.
  impersonate: async (userId: number, reason: string) => {
    const r = await apiClient.post(`/admin/users/${userId}/impersonate`, { reason });
    return r.data.data; // { token, user, impersonation_session_id }
  },
  // Closes the view-as session server-side (ended_at set) so its token stops working now, not at its
  // 1 h expiry (spec §2.2, D3). Sent WITH the view-as token — the guard lets exactly this POST through.
  endImpersonation: async (): Promise<{ impersonation_session_id: string; ended: boolean }> => {
    const r = await apiClient.post('/admin/impersonation/end');
    return r.data.data;
  },
  listAuditLogs: async (params?: {
    project_id?: number;
    user_id?: number;
    action?: string;
    limit?: number;
  }) => {
    const r = await apiClient.get('/admin/audit-logs', { params });
    return r.data.data as Array<{
      id: string;
      user_id: number | null;
      user_email: string | null;
      project_id: number | null;
      project_name: string | null;
      action: string;
      entity_type: string | null;
      entity_id: string | null;
      changes: any;
      created_at: string;
      ripple_count: number;
    }>;
  },
  getAuditLogTree: async (rootId: string) => {
    const r = await apiClient.get(`/admin/audit-logs/${rootId}/tree`);
    return r.data.data as Array<{
      id: string;
      parent_audit_id: string | null;
      ripple_source_event_id: number | null;
      user_id: number | null;
      user_email: string | null;
      project_id: number | null;
      action: string;
      entity_type: string | null;
      entity_id: string | null;
      changes: any;
      created_at: string;
      source_event_type: string | null;
      source_event_emitted_at: string | null;
    }>;
  },
};

// Projects API
export const projectsAPI = {
  getAll: async () => {
    const response = await apiClient.get('/projects');
    return response.data.data.projects;
  },

  getById: async (id: number) => {
    const response = await apiClient.get(`/projects/${id}`);
    return response.data.data;
  },

  create: async (projectData: Partial<Project>) => {
    const response = await apiClient.post('/projects', projectData);
    return response.data.data;
  },

  update: async (id: number, projectData: Partial<Project>) => {
    const response = await apiClient.put(`/projects/${id}`, projectData);
    return response.data.data;
  },
  // No delete: the route answers 410. Closing an engagement is unifiedProjectsAPI.startClosePackage.
};

// System Modeling API
export const systemAPI = {
  // Assets
  getAssets: async (projectId: number) => {
    const response = await apiClient.get(`/system/projects/${projectId}/assets`);
    return response.data.assets;
  },

  createAsset: async (assetData: Omit<Asset, 'id' | 'created_at' | 'updated_at'>) => {
    const response = await apiClient.post('/system/assets', assetData);
    return response.data;
  },

  updateAsset: async (assetId: string, assetData: Partial<Asset>) => {
    const response = await apiClient.put(`/system/assets/${assetId}`, assetData);
    return response.data;
  },

  deleteAsset: async (assetId: string) => {
    const response = await apiClient.delete(`/system/assets/${assetId}`);
    return response.data;
  },

  // Data Flows
  getDataFlows: async (projectId: number) => {
    const response = await apiClient.get(`/system/projects/${projectId}/data-flows`);
    return response.data.dataFlows;
  },

  createDataFlow: async (flowData: Omit<DataFlow, 'id'>) => {
    const response = await apiClient.post('/system/data-flows', flowData);
    return response.data;
  },

  deleteDataFlow: async (flowId: string) => {
    const response = await apiClient.delete(`/system/data-flows/${flowId}`);
    return response.data;
  },

  /** Edit name / description / protocol / direction / classification. Returns the recomputed flow. */
  updateDataFlow: async (flowId: string, fields: DataFlowEditFields): Promise<DataFlow> => {
    const response = await apiClient.put(`/system/data-flows/${flowId}`, fields);
    return response.data.dataFlow;
  },

  /** Approve a violation / needs-review flow as an exception (reason required). */
  approveFlowException: async (flowId: string, reason: string): Promise<DataFlow> => {
    const response = await apiClient.post(`/system/data-flows/${flowId}/exception`, { reason });
    return response.data.dataFlow;
  },

  revokeFlowException: async (flowId: string): Promise<DataFlow> => {
    const response = await apiClient.delete(`/system/data-flows/${flowId}/exception`);
    return response.data.dataFlow;
  },

  // Countermeasures
  getCountermeasures: async (projectId: number) => {
    const response = await apiClient.get(`/system/projects/${projectId}/countermeasures`);
    return response.data.countermeasures;
  },

  createCountermeasure: async (measureData: Omit<Countermeasure, 'id'>) => {
    const response = await apiClient.post('/system/countermeasures', measureData);
    return response.data;
  },

  updateCountermeasure: async (measureId: string, measureData: Partial<Countermeasure>) => {
    const response = await apiClient.put(`/system/countermeasures/${measureId}`, measureData);
    return response.data;
  },

  deleteCountermeasure: async (measureId: string) => {
    const response = await apiClient.delete(`/system/countermeasures/${measureId}`);
    return response.data;
  },

  // Architecture & Statistics
  getArchitecture: async (projectId: number, includeCosts = false) => {
    const response = await apiClient.get(`/system/projects/${projectId}/architecture`, {
      params: { includeCosts }
    });
    return response.data;
  },

  getStatistics: async (projectId: number) => {
    const response = await apiClient.get(`/system/projects/${projectId}/statistics`);
    return response.data;
  },

  // Update single asset position (for drag-drop)
  updateAssetPosition: async (assetId: number, position_x: number, position_y: number) => {
    const response = await apiClient.patch(`/system/assets/${assetId}/position`, {
      position_x,
      position_y
    });
    return response.data;
  },

  // Bulk update asset positions (for saving multiple drags at once)
  updateAssetPositions: async (positions: Array<{ id: number; position_x: number; position_y: number }>) => {
    const response = await apiClient.patch('/system/assets/positions', { positions });
    return response.data;
  },

  // Export system model to Excel (auditor-friendly)
  exportToExcel: async (projectId: number): Promise<Blob> => {
    const response = await apiClient.get(`/system/projects/${projectId}/export/excel`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Export system model to PDF (auditor-friendly)
  exportToPDF: async (projectId: number): Promise<Blob> => {
    const response = await apiClient.get(`/system/projects/${projectId}/export/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

// Threat Intelligence API
export const threatAPI = {
  // Get MITRE ATT&CK techniques
  getTechniques: async (filters?: { tactic?: string; severity?: string; asset_type?: string; project_id?: string }) => {
    const response = await apiClient.get('/threat-intelligence/techniques', { params: filters });
    return response.data.techniques;
  },

  // Get APT groups
  getAPTGroups: async (threatLevel?: string, projectId?: string) => {
    const params: Record<string, string> = {};
    if (threatLevel) params.threat_level = threatLevel;
    if (projectId) params.project_id = projectId;
    const response = await apiClient.get('/threat-intelligence/apt-groups', { params });
    return response.data.apt_groups;
  },

  // Get attack scenarios
  getScenarios: async (projectId?: string, severity?: string) => {
    const params: Record<string, string> = {};
    if (projectId) params.project_id = projectId;
    if (severity) params.severity = severity;

    const response = await apiClient.get('/threat-intelligence/scenarios', { params });
    return response.data; // Return full response including source
  },

  // Get threat assessment for project
  getThreatAssessment: async (projectId: number) => {
    const response = await apiClient.get(`/threat-intelligence/projects/${projectId}/threat-assessment`);
    return response.data.threat_assessment;
  },

  // Get attack-scenario triangulation drill-down.
  // Pass `techniques` to bypass DB lookup (works for fallback scenarios that lack a
  // dodcar.threat_vectors row).
  getScenarioTriangulation: async (
    projectId: number,
    scenarioId: number | string,
    techniques?: string[]
  ) => {
    const params: Record<string, string> = {};
    if (techniques && techniques.length > 0) {
      params.techniques = techniques.join(',');
    }
    const response = await apiClient.get(
      `/threat-intelligence/projects/${projectId}/scenarios/${scenarioId}/triangulation`,
      { params }
    );
    return response.data;
  },
};

// CMMC Assessment API
export const cmmcAPI = {
  // Get all CMMC domains
  getDomains: async () => {
    const response = await apiClient.get('/cmmc/domains');
    return response.data.data;
  },

  // Get practices for a specific domain
  getDomainPractices: async (domainCode: string, level?: number) => {
    const response = await apiClient.get(`/cmmc/domains/${domainCode}/practices`, {
      params: level ? { level } : {}
    });
    return response.data.data;
  },

  // Create CMMC assessment for project
  createAssessment: async (projectId: number, assessmentData: { level?: number; target_level?: number; scope?: string; assessment_type?: string; domain_assessments?: Record<string, { score: number; notes?: string }> }) => {
    const response = await apiClient.post(`/cmmc/projects/${projectId}/assessment`, assessmentData);
    return response.data.data;
  },

  // Get CMMC assessment for project
  getAssessment: async (projectId: number) => {
    const response = await apiClient.get(`/cmmc/projects/${projectId}/assessment`);
    return response.data.data;
  },

  // Generate CMMC compliance report
  getComplianceReport: async (projectId: number, format = 'summary') => {
    const response = await apiClient.get(`/cmmc/projects/${projectId}/report`, {
      params: { format }
    });
    return response.data.data;
  },
};

// P/D/R Scoring API
export const pdrAPI = {
  // Get threat profile for project (applicable ATT&CK techniques)
  getThreatProfile: async (projectId: number) => {
    const response = await apiClient.get(`/pdr/projects/${projectId}/threat-profile`);
    return response.data.threatProfile || [];
  },

  // Get countermeasures for project
  getCountermeasures: async (projectId: number) => {
    const response = await apiClient.get(`/pdr/projects/${projectId}/countermeasures`);
    return response.data.data;
  },

  // Get P/D/R scores for project
  getScores: async (projectId: number, sortBy?: string, order?: string) => {
    const params = new URLSearchParams();
    if (sortBy) params.append('sortBy', sortBy);
    if (order) params.append('order', order);
    const queryString = params.toString();
    const url = `/pdr/projects/${projectId}/scores${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response.data.scores || response.data.data || [];
  },

  // Run automated P/D/R scoring
  runAutoScore: async (projectId: number) => {
    const response = await apiClient.post(`/pdr/projects/${projectId}/auto-score`);
    return response.data.data;
  },

  // Get coverage gap analysis
  getGapAnalysis: async (projectId: number) => {
    const response = await apiClient.get(`/pdr/projects/${projectId}/gap-analysis`);
    return response.data.gapAnalysis || response.data.data || {};
  },

  // Get optimization recommendations
  getOptimizationRecommendations: async (projectId: number) => {
    const response = await apiClient.get(`/pdr/projects/${projectId}/optimization-recommendations`);
    return response.data.data;
  },

  // Get maturity assessment
  getMaturityAssessment: async (projectId: number) => {
    const response = await apiClient.get(`/pdr/projects/${projectId}/maturity-assessment`);
    return response.data.maturityAssessment || response.data.data || {};
  },

  // Update individual technique P/D/R score (manual override)
  updateTechniqueScore: async (projectId: number, techniqueId: string, scores: { protect_score: number; detect_score: number; respond_score: number }) => {
    const response = await apiClient.put(`/pdr/projects/${projectId}/techniques/${techniqueId}/score`, scores);
    return response.data;
  },

  // Get attack surface coverage analysis. Returns full payload so callers
  // can read both the per-technique array and the project-level
  // attackSurfaceMitigatedPct headline (DoDCAR Student Guide ASM% per
  // PRISM_DODCAR_FIDELITY_SPEC.md Â§Phase 1).
  getAttackSurfaceCoverage: async (projectId: number) => {
    const response = await apiClient.get(`/pdr/projects/${projectId}/attack-surface-coverage`);
    return response.data || {};
  },

  // MITRE ATT&CK matrix data â€” tactics + per-technique scores + heat + applicable flag.
  // Powers the AttackMatrix component (DoDCAR Fidelity Phase 2 Slice 2A).
  getCoverageMatrix: async (projectId: number) => {
    const response = await apiClient.get(`/pdr/projects/${projectId}/coverage-matrix`);
    return response.data || {};
  },

  // Per-technique scored-countermeasure breakdown (Slice 2A.5). Returns each cm
  // scored against the technique with raw P/D/R, justifications, axioms, confidence,
  // scored_by, and pre-computed individual_wlom (0.4Â·P + 0.3Â·D + 0.3Â·R per cm).
  getScoredCountermeasures: async (projectId: number, techniqueId: string) => {
    const response = await apiClient.get(
      `/pdr/projects/${projectId}/techniques/${encodeURIComponent(techniqueId)}/scored-countermeasures`
    );
    return response.data || {};
  },

  // Export the matrix as SVG or PDF (Slice 2C). Returns a Blob ready to
  // download via URL.createObjectURL.
  exportCoverageMatrix: async (
    projectId: number,
    format: 'svg' | 'pdf',
    overlay: 'profile' | 'heat' | 'coverage',
    isolate: 'rolled' | 'protect' | 'detect' | 'respond' | 'recover'
  ): Promise<Blob> => {
    const response = await apiClient.get(
      `/pdr/projects/${projectId}/coverage-matrix.${format}?overlay=${overlay}&isolate=${isolate}`,
      { responseType: 'blob' }
    );
    return new Blob([response.data], {
      type: format === 'pdf' ? 'application/pdf' : 'image/svg+xml',
    });
  },

  // Slice 2B â€” list of countermeasures scored on a project, used to populate
  // the Coverage Change Map's removal multi-select.
  getScoredCountermeasureSummary: async (projectId: number) => {
    const response = await apiClient.get(
      `/pdr/projects/${projectId}/scored-countermeasures-summary`
    );
    return response.data || {};
  },

  // Slice 2B â€” simulate removing countermeasures and return current+simulated
  // LoMs per technique + ASM% delta. Pure-JS computation server-side.
  simulateCoverageMatrix: async (projectId: number, removeCountermeasureIds: number[]) => {
    const response = await apiClient.post(
      `/pdr/projects/${projectId}/coverage-matrix/simulate`,
      { remove_countermeasure_ids: removeCountermeasureIds }
    );
    return response.data || {};
  },

  // Phase 3 Slice 3A â€” Capability Relative Value per the DoD Developed
  // Methodology (handbook pp. 61, 94): RV(cm) = ASM(all) - ASM(all - cm).
  // Returns sorted desc by contribution_pp.
  getRelativeValues: async (projectId: number) => {
    const response = await apiClient.get(
      `/pdr/projects/${projectId}/coverage-matrix/relative-values`
    );
    return response.data || {};
  },

  // Get unscored techniques for Pending Review tab
  getUnscoredTechniques: async (projectId: number) => {
    const response = await apiClient.get(`/pdr/projects/${projectId}/unscored-techniques`);
    return response.data.unscoredTechniques || [];
  },

  // Organizational NIST CSF maturity; null when nothing has been scored
  getOrganizationalMaturity: async (projectId: number) => {
    const response = await apiClient.get(`/nist-csf/projects/${projectId}/organizational-maturity`);
    return response.data.nist_csf_maturity || null;
  },
};

// Over-Investment Detection API
export const overInvestmentAPI = {
  // Run over-investment analysis
  analyzeOverInvestment: async (projectId: number) => {
    const response = await apiClient.post(`/over-investment/projects/${projectId}/analyze`);
    return response.data;
  },

  // Get over-investment summary
  getSummary: async (projectId: number) => {
    const response = await apiClient.get(`/over-investment/projects/${projectId}/summary`);
    return response.data;
  },

  // Get detailed over-investment breakdown
  getDetails: async (projectId: number, sortBy?: string, order?: string, minSavings?: number) => {
    const params = new URLSearchParams();
    if (sortBy) params.append('sort_by', sortBy);
    if (order) params.append('order', order);
    if (minSavings) params.append('min_savings', minSavings.toString());
    const queryString = params.toString();
    const url = `/over-investment/projects/${projectId}/details${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  // Get technique-specific over-investment details
  getTechniqueDetails: async (projectId: number, techniqueId: string) => {
    const response = await apiClient.get(`/over-investment/projects/${projectId}/technique/${techniqueId}`);
    return response.data;
  },

  // Get consolidation recommendations
  getRecommendations: async (projectId: number) => {
    const response = await apiClient.get(`/over-investment/projects/${projectId}/recommendations`);
    return response.data;
  },

  // Get optimal thresholds reference
  getOptimalThresholds: async () => {
    const response = await apiClient.get('/over-investment/optimal-thresholds');
    return response.data;
  },
};

// Cost Optimization Engine API
export const costOptimizationAPI = {
  getReport: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization`);
    return response.data;
  },

  analyze: async (projectId: number) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/cost-optimization/analyze`);
    return response.data;
  },

  getCoverageMatrix: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization/coverage-matrix`);
    return response.data;
  },

  getSummary: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization/summary`);
    return response.data;
  },

  simulate: async (
    projectId: number,
    removedAssets: Array<{ id: number; removal_year?: number | null }>
  ) => {
    // 60s timeout (vs default 30s) â€” Iris cold-start can push the server-side
    // narrative call past 25s. Server falls back to template, but we want a
    // safety margin so the chart still renders even if Iris takes longer.
    const response = await apiClient.post(
      `/v2/projects/${projectId}/cost-optimization/simulate`,
      { removedAssets },
      { timeout: 60000 }
    );
    return response.data;
  },

  getFoundationsContext: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization/foundations`);
    return response.data;
  },

  getMaturity: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization/maturity`);
    return response.data;
  },

  getMaturityRoadmap: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization/maturity-roadmap`);
    return response.data;
  },

  autoMapCoverage: async (projectId: number) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/cost-optimization/auto-map`);
    return response.data;
  },

  getRiskROI: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization/risk-roi`);
    return response.data;
  },

  getTCOAnalysis: async (projectId: number, options?: { burdenedRate?: number }) => {
    const params = options?.burdenedRate ? `?burdened_rate=${options.burdenedRate}` : '';
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization/tco${params}`);
    return response.data;
  },

  getReportedFTE: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization/reported-fte`);
    return response.data;
  },

  setReportedFTE: async (projectId: number, reportedFte: number) => {
    const response = await apiClient.put(`/v2/projects/${projectId}/cost-optimization/reported-fte`, {
      reported_fte: reportedFte,
    });
    return response.data;
  },

  exportToPDF: async (projectId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization/export/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  generateRFP: async (
    projectId: number,
    removedAssetIds: number[],
    format: 'pdf' | 'docx' = 'docx'
  ): Promise<Blob> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/cost-optimization/generate-rfp`,
      { removedAssetIds, format },
      { responseType: 'blob', timeout: 60000 }
    );
    return response.data;
  },

  generateSOW: async (projectId: number, removedAssetIds: number[]): Promise<Blob> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/cost-optimization/generate-sow`,
      { removedAssetIds, format: 'docx' },
      { responseType: 'blob', timeout: 60000 }
    );
    return response.data;
  },

  getDocumentHistory: async (projectId: number): Promise<GeneratedDocument[]> => {
    const response = await apiClient.get(
      `/v2/projects/${projectId}/cost-optimization/documents`
    );
    return response.data;
  },

  downloadDocument: async (projectId: number, docId: number, format: 'pdf' | 'docx' = 'docx'): Promise<Blob> => {
    const response = await apiClient.get(
      `/v2/projects/${projectId}/cost-optimization/documents/${docId}/download?format=${format}`,
      { responseType: 'blob', timeout: 60000 }
    );
    return response.data;
  },

  previewSOW: async (projectId: number, removedAssetIds: number[]): Promise<DocumentPreview> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/cost-optimization/generate-sow`,
      { removedAssetIds, preview: true },
      { timeout: 60000 }
    );
    return response.data;
  },

  previewRFP: async (projectId: number, removedAssetIds: number[]): Promise<DocumentPreview> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/cost-optimization/generate-rfp`,
      { removedAssetIds, preview: true },
      { timeout: 60000 }
    );
    return response.data;
  },

  previewDocument: async (projectId: number, docId: number): Promise<DocumentPreview> => {
    const response = await apiClient.get(
      `/v2/projects/${projectId}/cost-optimization/documents/${docId}/preview`
    );
    return response.data;
  },

  // ---- Allocation rebuild (#217): every read answers { enabled: false } when the flag is off ----
  getAllocation: async (projectId: number): Promise<AllocationResponse> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization/allocation`);
    return response.data;
  },

  getConsolidation: async (projectId: number): Promise<ConsolidationResponse> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization/consolidation`);
    return response.data;
  },

  listAssetKinds: async (projectId: number): Promise<AssetKindsResponse> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cost-optimization/asset-kinds`);
    return response.data;
  },

  confirmAssetKinds: async (projectId: number, assetIds: number[]): Promise<{ confirmed: number }> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/cost-optimization/asset-kinds/confirm`,
      { assetIds }
    );
    return response.data;
  },

  setAssetKind: async (
    projectId: number,
    assetId: number,
    kind: AssetKind,
    category: SpendCategory | null
  ): Promise<{ updated: boolean }> => {
    const response = await apiClient.patch(
      `/v2/projects/${projectId}/cost-optimization/asset-kinds/${assetId}`,
      { kind, category }
    );
    return response.data;
  },

  suggestAssetKinds: async (projectId: number): Promise<{ started: boolean; alreadyRunning?: boolean }> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/cost-optimization/asset-kinds/suggest`
    );
    return response.data;
  },

  getSuggestProgress: async (projectId: number): Promise<SuggestProgress> => {
    const response = await apiClient.get(
      `/v2/projects/${projectId}/cost-optimization/asset-kinds/suggest/progress`
    );
    return response.data;
  },
};

// Posture → Prioritization (slice 1) — ranked capability list by threat coverage
// per dollar. Flag-driven: `enabled: false` when POSTURE_PRIORITIZATION_ENABLED is off.
export const prioritizationAPI = {
  get: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/prioritization`);
    return response.data as {
      enabled: boolean;
      resourceTier: 'constrained' | 'moderate' | 'well-resourced' | null;
      rows: Array<{
        capabilityId: string; name: string; category: string;
        threatWeight: number; costBand: string | null; costOrdinal: number | null;
        cadence: string | null; skillBurden: number | null;
        aiBadge: 'AI-drafted' | 'AI-assisted' | 'manual';
        rankScore: number | null; call: 'do-now' | 'phase' | 'deferred' | 'unpriced';
        baselineDollar?: number; assistedDollar?: number; agenticDollar?: number; crossesLine?: boolean;
      }>;
      rollup?: { baselineTotal: number; assistedTotal: number; agenticTotal: number; doNowCount: number };
    };
  },
};

// Asset Deduplication API
export const assetDedupAPI = {
  check: async (projectId: number | string, name: string, assetType?: string) => {
    const response = await apiClient.post('/system/dedup/check', {
      projectId,
      name,
      asset_type: assetType,
    });
    return response.data;
  },

  resolve: async (
    projectId: number | string,
    decisions: Array<{
      inputName: string;
      action: 'use_existing' | 'create_new' | 'skip';
      existingAssetId?: string;
      assetInput?: any;
    }>
  ) => {
    const response = await apiClient.post('/system/dedup/resolve', {
      projectId,
      decisions,
    });
    return response.data;
  },
};

// Architecture proposals (spec 2026-09-30): a diagram becomes a proposal a person accepts, edits or
// rejects. Item keys come from the server (proposal.keys); decisions send them back verbatim.
export const architectureProposalsAPI = {
  list: (projectId: number) =>
    apiClient
      .get(`/v2/projects/${projectId}/architecture-proposals`)
      .then(r => r.data as ProposalListResponse),
  decide: (projectId: number, proposalId: number, decisions: ItemDecision[]) =>
    apiClient
      .post(`/v2/projects/${projectId}/architecture-proposals/${proposalId}/decide`, { decisions })
      .then(r => r.data as DecideResult),
  /** F4: resolve held components — "Same as <existing>" or "Create as new". */
  resolve: (projectId: number, proposalId: number, resolutions: HeldResolution[]) =>
    apiClient
      .post(`/v2/projects/${projectId}/architecture-proposals/${proposalId}/resolve`, { resolutions })
      .then(r => r.data as ResolveResult),
  /** "Read diagram": returns the (new or existing pending) proposal for one evidence file. */
  fromEvidence: (projectId: number, evidenceId: number) =>
    apiClient
      .post(`/v2/projects/${projectId}/architecture-proposals/from-evidence/${evidenceId}`)
      .then(r => (r.data as { proposal: ProposalRow }).proposal),
};

// Crosscheck (spec 2026-10-03): compare a client's exports; a person approves before citing.
/**
 * Where a role's rows come from (document reads spec §4.1). Absent from the payload = an uploaded file, as before.
 * `read`: accepted reads of the listed kinds (one, or several when `multiple`). `typed`: the claims typed in the dialog.
 */
export type CrosscheckRoleSource =
  | { kind: 'file' }
  | { kind: 'read'; readKinds: ReadKindId[]; table: string; tableFor?: Partial<Record<ReadKindId, string>>; multiple: boolean }
  | { kind: 'typed'; columns: string[]; param?: 'claims' | 'statements' };
export interface CrosscheckRole {
  role: string; label: string; requiredColumns: string[]; optional: boolean;
  /** Absent = a file role. */
  source?: CrosscheckRoleSource;
}
export type CrosscheckCheckType = 'link' | 'blank' | 'flag' | 'list' | 'currency' | 'claims';
export interface CrosscheckCheckInfo {
  id: string; label: string; kind: 'finding' | 'informational'; type: CrosscheckCheckType;
  /** A claims check's scope confirmation, as the template words it. */
  confirm?: { label: string; choices: CrosscheckResolution[] };
  /** A question check (section 9): the approver answers it from these choices; code never does. */
  question?: { choices: { value: 'yes' | 'no' | 'unclear'; label: string }[] };
}
/**
 * What a person still owes before approval, as the server counts it (ruling R18: the same counts approval refuses on).
 * Absent when the run is not open, or when the server could not count: Approve then stays disabled.
 */
export interface CrosscheckOwed { nameMatches: number; scopeConfirmations: number; questions?: number }
export interface CrosscheckRunDetail {
  run: CrosscheckRun; results: CrosscheckResult[]; citation: string | null;
  owed?: CrosscheckOwed;
  /** The run's own checks that take a scope confirmation (its claims checks), from the server. */
  confirmChecks?: string[];
  /** The run's own question checks (section 9), from the server. */
  questionChecks?: string[];
}
/** The kinds a claimed certification can have (backend services/crosscheck/types.ts CLAIM_KINDS). */
export type CrosscheckClaimKind = 'soc2-type2' | 'soc2-type1' | 'iso27001' | 'cyber-essentials-plus' | 'other';
export interface CrosscheckClaim { name: string; kind: CrosscheckClaimKind; where_seen: string }
/** A file role takes an evidence id; a read role a TAGGED read, or a list of them for a `multiple` role. */
export type CrosscheckRunInput = number | { read_id: number } | { read_id: number }[];
/** A name-match decision, or a scope confirmation on a claim (document reads spec §4.2). */
export type CrosscheckResolution = 'confirmed' | 'rejected' | 'covers' | 'does_not_cover' | 'unclear' | 'yes' | 'no';
/** A run parameter a register template declares (registers spec §3.5). */
export type CrosscheckParamName = 'evidenceDate' | 'ratingScale' | 'payTerms' | 'relatedRun';
/** A derived display column (registers spec §3.6): presentation only, never a check's result. */
export interface CrosscheckDisplaySpec { label: string; kind: 'percent'; numerator: string; denominator: string }
/** Ruling R1: one definition, shared/ratingScales.ts — a label scale or a numeric one (rating-scales spec §3). */
export type CrosscheckRatingScale = RatingScale;
/** The parameters stored on a run; a run without any carries `params: null`. */
export interface CrosscheckRunParams {
  evidenceDate: string | null; ratingScale: CrosscheckRatingScale | null;
  /** 4.1's typed claims; absent on every run without them. */
  claims?: CrosscheckClaim[] | null;
  /** Section 9: the pay-process terms, typed statements, and the approved 9.1 run a 9.2 run builds on. */
  payTerms?: string[] | null;
  statements?: { statement: string; where_seen: string }[] | null;
  relatedRun?: { runId: number; templateId: string; approvedOn: string } | null;
}
/** The run request's parameter fields; only the ones the template declares are sent. */
export interface CrosscheckRunParamsBody { evidence_date?: string; rating_scale?: CrosscheckRatingScale; claims?: CrosscheckClaim[];
  pay_terms?: string[]; statements?: { statement: string; where_seen: string }[]; related_run_id?: number }
/** At most 50 values, the most common first; `truncated: true` only when the server cut the list. */
export interface CrosscheckProfile { rowsRead: number; values: { value: string; count: number }[]; truncated?: boolean }
export interface CrosscheckTemplateInfo {
  id: string; version: number; title: string; scopeRef: string;
  /** The help panel's "Why run it" paragraph. */
  purpose: string; qualifier: string;
  roles: CrosscheckRole[]; checks: CrosscheckCheckInfo[];
  /** Absent from a server older than the registers: read as none. */
  params?: CrosscheckParamName[];
  display?: CrosscheckDisplaySpec[];
  /** True when a link check matches on a composite key: the matched column holds a value, not an id. */
  compositeKey?: boolean;
  /** The file role and column the rating scale is read from (profiled for the orderer); null when none. */
  ratingSource?: { role: string; column: string } | null;
  /** Roles that read the same documents and must hold the same reads (section 9); absent when none. */
  readGroups?: string[][];
}
/** Per-check coverage figures as stored on the run (rules v2). */
export interface CrosscheckCoverage {
  label: string; kind: 'finding' | 'informational'; status: 'run' | 'not_run'; notRunReason: string | null;
  considered: number; notApplicable: number; excluded: number; unreadable: number; covered: number; missing: number; pending: number;
}
export interface CrosscheckRun {
  id: number; status: 'computed' | 'approved' | 'rejected' | 'failed'; failureReason: string | null;
  templateId: string; templateVersion: number; rulesVersion: number; counts: Record<string, number>; createdAt: string;
  /** Counts after the person's name-match decisions; written on approve, null before (ruling R13). */
  effectiveCounts: Record<string, number> | null;
  decidedAt: string | null; decisionNote: string | null; supersededBy: number | null;
  /** Per-check coverage (rules v2); null on a v1 run. effectiveCoverage is written on approve. */
  coverage: Record<string, CrosscheckCoverage> | null;
  effectiveCoverage: Record<string, CrosscheckCoverage> | null;
  /** One server-formatted line per check id; null on a v1 run, which renders as before. */
  coverageLines: Record<string, string> | null;
  /** The run parameters as entered; null for a run without any (absent from an older server). */
  params?: CrosscheckRunParams | null;
  /** rowsConsidered is absent when the run failed before comparing: not recorded, never zero. */
  inputs: { role: string; evidenceId: number; fileName: string; sha256: string; rowsRead: number; rowsConsidered?: number }[];
  /** The accepted reads the run used, one entry per role (migration 436); null or absent on a run without reads. */
  reads?: CrosscheckRunRead[] | null;
}
export interface CrosscheckRunRead {
  role: string; readId: number; fileName: string; sha256: string; readKind: ReadKindId; readKindVersion: number;
  coverage: ReadCoverage | null;
}
export interface CrosscheckResult {
  id: number; bucket: string; sourceRole: string; sourceRow: number; name: string;
  /** The check this row belongs to; null for file-level rows and for every row of a v1 run. */
  checkId: string | null;
  linkValue: string | null; matchedSystemId: string | null; detail: string | null; resolution: CrosscheckResolution | null;
  /** The source row's cells, as read; feeds the derived display columns. */
  row?: Record<string, string>;
}
/** The server's per-request cap on results. */
export const CROSSCHECK_PAGE = 1000;
export const crosscheckAPI = {
  templates: (projectId: number) =>
    apiClient.get(`/v2/projects/${projectId}/crosscheck/templates`).then(r => r.data as { enabled: boolean; templates: CrosscheckTemplateInfo[] }),
  /** `params` only for a template that declares some; without it the body is exactly as before. */
  run: (projectId: number, templateId: string, inputs: Record<string, CrosscheckRunInput>, params?: CrosscheckRunParamsBody) =>
    apiClient
      .post(`/v2/projects/${projectId}/crosscheck/runs`, { template_id: templateId, inputs, ...(params ?? {}) })
      .then(r => r.data as { run: CrosscheckRun }),
  /** The distinct values of one column of one file (registers spec §5); read-only, stores nothing. */
  profile: (projectId: number, body: { template_id: string; role: string; evidence_id: number; column: string }) =>
    apiClient.post(`/v2/projects/${projectId}/crosscheck/profile`, body).then(r => r.data as CrosscheckProfile),
  list: (projectId: number) => apiClient.get(`/v2/projects/${projectId}/crosscheck/runs`).then(r => r.data as { runs: CrosscheckRun[] }),
  get: (projectId: number, runId: number, opts: { bucket?: string; check?: string; offset?: number; limit?: number } = {}) =>
    apiClient
      .get(`/v2/projects/${projectId}/crosscheck/runs/${runId}`, {
        params: {
          limit: opts.limit ?? CROSSCHECK_PAGE,
          offset: opts.offset ?? 0,
          ...(opts.bucket ? { bucket: opts.bucket } : {}),
          ...(opts.check ? { check: opts.check } : {}),
        },
      })
      .then(r => r.data as CrosscheckRunDetail),
  resolve: (projectId: number, runId: number, resolutions: { result_id: number; resolution: CrosscheckResolution }[]) =>
    apiClient.post(`/v2/projects/${projectId}/crosscheck/runs/${runId}/resolve`, { resolutions }).then(r => r.data as { resolved: number }),
  approve: (projectId: number, runId: number, note: string) =>
    apiClient.post(`/v2/projects/${projectId}/crosscheck/runs/${runId}/approve`, { note }).then(r => r.data as { run: CrosscheckRun }),
  reject: (projectId: number, runId: number, note: string) =>
    apiClient.post(`/v2/projects/${projectId}/crosscheck/runs/${runId}/reject`, { note }).then(r => r.data as { run: CrosscheckRun }),
  // Downloads go through apiClient so the Bearer token is sent; the caller saves the blob.
  exportXlsx: (projectId: number, runId: number) =>
    apiClient.get(`/v2/projects/${projectId}/crosscheck/runs/${runId}/export.xlsx`, { responseType: 'blob' }).then(r => r.data as Blob),
  blankCsv: (projectId: number, templateId: string, role: string) =>
    apiClient.get(`/v2/projects/${projectId}/crosscheck/templates/${templateId}/blank/${role}`, { responseType: 'blob' }).then(r => r.data as Blob),
};

// Document reads (spec 2026-10-04, Schedule 5 section 4): Iris reads a PDF in pieces, a person reviews and accepts the
// rows, and accepted reads feed Crosscheck. Shapes mirror backend services/documentReads/{types,store}.ts.
export type ReadKindId =
  | 'soc2-report'
  | 'iso27001-certificate'
  | 'iso27001-soa'
  | 'iso27001-audit-report'
  | 'other-certificate'
  | 'closure-records'
  | 'status-notice'
  | 'bcp-dr-plan'
  | 'business-impact-analysis'
  | 'dependency-map'
  | 'pay-run-procedure'
  | 'dr-test-report'
  | 'ransomware-playbook'
  | 'tabletop-report';
export interface ReadTableInfo { name: string; columns: string[]; single: boolean; dateColumns: string[] }
export interface ReadKindInfo { id: ReadKindId; label: string; version: number; tables: ReadTableInfo[] }
/** Every value is a string as written, or null. */
export type ReadCells = Record<string, string | null>;
export interface ReadCoverage {
  piecesTotal: number; piecesOk: number;
  failed: { piece: number; pageFrom: number; pageTo: number; reason: string }[];
  pagesTotal: number; ocrTruncated: boolean; ocrTimedOut: boolean;
  unreadPages: [number, number][];
  pagesWithoutText: { pageFrom: number; pageTo: number; reason: string }[];
  /** Only when every piece was read and nothing was cut. */
  complete: boolean;
}
export interface ReadConflict {
  table: string; column: string;
  values: { value: string; pageFrom: number; pageTo: number }[];
  /** Set once a person chose or typed the value to keep. */
  resolved?: string; resolved_by?: number; resolved_at?: string;
}
export type ReadStatus = 'reading' | 'review' | 'accepted' | 'failed';
export interface DocumentRead {
  id: number; projectId: number; evidenceId: number | null; evidenceSha256: string; fileName: string;
  readKind: ReadKindId; readKindVersion: number; status: ReadStatus; failureReason: string | null;
  coverage: ReadCoverage | null; conflicts: ReadConflict[]; stale: boolean;
  createdBy: number | null; createdAt: string; acceptedBy: number | null; acceptedAt: string | null;
}
export interface ReadRow {
  id: number; table: string; rowIndex: number; value: ReadCells; irisValue: ReadCells | null;
  origin: 'iris' | 'person'; removed: boolean; pageFrom: number | null; pageTo: number | null; quote: string | null;
  /** An edited or removed Iris row that a retry no longer produced: kept, and shown as such. */
  irisMissing: boolean;
  /** Date columns holding a written value the checks cannot read (ruling R19). Computed by the server; never here. */
  unreadableDates?: string[];
}
export interface StoredDocumentRead { read: DocumentRead; rows: ReadRow[] }
export interface ReadProgress { status: ReadStatus; done: number; total: number; error?: string }
export const documentReadsAPI = {
  /** `enabled: false` when DOCUMENT_READS_ENABLED is off; every other route then 404s. */
  kinds: (projectId: number) =>
    apiClient.get(`/v2/projects/${projectId}/document-reads/kinds`).then(r => r.data as { enabled: boolean; kinds: ReadKindInfo[] }),
  list: (projectId: number) =>
    apiClient.get(`/v2/projects/${projectId}/document-reads`).then(r => r.data as { reads: DocumentRead[] }),
  /** 202: the read runs in the background; poll progress. */
  start: (projectId: number, evidenceId: number, readKind: ReadKindId) =>
    apiClient
      .post(`/v2/projects/${projectId}/document-reads`, { evidence_id: evidenceId, read_kind: readKind })
      .then(r => r.data as { read: DocumentRead }),
  progress: (projectId: number, readId: number) =>
    apiClient.get(`/v2/projects/${projectId}/document-reads/${readId}/progress`).then(r => r.data as ReadProgress),
  get: (projectId: number, readId: number) =>
    apiClient.get(`/v2/projects/${projectId}/document-reads/${readId}`).then(r => r.data as StoredDocumentRead),
  /** The whole row: each of the table's columns, as text or null. */
  editRow: (projectId: number, readId: number, rowId: number, value: ReadCells) =>
    apiClient
      .patch(`/v2/projects/${projectId}/document-reads/${readId}/rows/${rowId}`, { value })
      .then(r => r.data as StoredDocumentRead),
  /** Marks the row removed; it is kept. */
  removeRow: (projectId: number, readId: number, rowId: number) =>
    apiClient.delete(`/v2/projects/${projectId}/document-reads/${readId}/rows/${rowId}`).then(r => r.data as StoredDocumentRead),
  addRow: (projectId: number, readId: number, table: string, value: ReadCells) =>
    apiClient
      .post(`/v2/projects/${projectId}/document-reads/${readId}/rows`, { table, value })
      .then(r => r.data as StoredDocumentRead),
  resolveConflict: (projectId: number, readId: number, table: string, column: string, value: string) =>
    apiClient
      .post(`/v2/projects/${projectId}/document-reads/${readId}/conflicts`, { table, column, value })
      .then(r => r.data as StoredDocumentRead),
  /** 202: re-reads only the failed pieces; review work is kept. */
  retry: (projectId: number, readId: number) =>
    apiClient.post(`/v2/projects/${projectId}/document-reads/${readId}/retry`).then(r => r.data as { read: DocumentRead }),
  /** Organisation admins only; anyone else gets the server's refusal sentence. */
  accept: (projectId: number, readId: number) =>
    apiClient.post(`/v2/projects/${projectId}/document-reads/${readId}/accept`).then(r => r.data as StoredDocumentRead),
};

// Sampler (spec 2026-10-04): a recorded, reproducible sample of up to 25 entries; a person approves before citing.
export interface SamplerTemplateInfo {
  id: string; version: number; title: string; scopeRef: string;
  /** The help panel's "Why run it" paragraph. */
  purpose: string; qualifier: string;
  /** Null: the population has no period (10.6), so no dates are asked for. */
  periodColumn: string | null;
  /** Null: no category checklist. */
  categoryColumn: string | null;
  stratifyBy: string | null;
  requiredColumns: string[];
}
/** What the chosen file holds, read before any filter (spec §5). Read-only. */
export interface SamplerProfile {
  rowsRead: number;
  dateRange: { from: string; to: string } | null;
  categories: { value: string; count: number }[];
  duplicateIds: string[];
  /** Rows unreadable in the file, before any filter (ruling R9). */
  rowErrors: number;
}
export type SamplerResult = 'no_exception' | 'exception' | 'could_not_examine';
export interface SamplerRun {
  id: number; projectId: number; templateId: string; templateVersion: number; rulesVersion: number;
  status: 'computed' | 'approved' | 'rejected' | 'failed'; failureReason: string | null;
  /** rowsRead is null when the run failed before the file was read as rows: unknown, never zero. */
  input: { evidenceId: number; fileName: string; sha256: string; rowsRead: number | null };
  params: { periodFrom: string | null; periodTo: string | null; categories: string[]; stratifyBy: string | null };
  seed: string; seedSource: 'generated' | 'entered'; seedEnteredBy: number | null;
  /** Null on a failed run: nothing was drawn. */
  method: string | null;
  population: {
    rowsRead: number; inPopulation: number; outsidePeriod: number; otherCategory: number;
    /** Unreadable rows not otherwise excluded; the rows are stored as row_error entries. */
    rowErrors: number;
    groups: Record<string, { size: number; drawn: number }> | null;
  } | null;
  sampleSize: number | null;
  /** Written on approval; null otherwise. */
  resultCounts: Record<SamplerResult, number> | null;
  scopeRef: string | null;
  createdBy: number; createdAt: string;
  decidedBy: number | null; decidedAt: string | null; decisionNote: string | null; supersededBy: number | null;
  /** Draw k of n among the non-failed runs of the same template and file; null for a failed run. */
  drawIndex: number | null; drawTotal: number | null;
}
export interface SamplerEntry {
  /** The stored entry id: what a PATCH names. */
  id: number;
  kind: 'drawn' | 'row_error';
  /** Draw order, 1-based; null for a row error. */
  ordinal: number | null;
  stratum: string | null; entryId: string; occurredAt: string | null; sourceRow: number; detail: string | null;
  row: Record<string, string>;
  result: SamplerResult | null; note: string | null; examinedBy: number | null; examinedAt: string | null;
}
export interface SamplerDrawBody {
  template_id: string; evidence_id: number; period_from: string | null; period_to: string | null;
  categories?: string[]; seed?: string;
}
export const samplerAPI = {
  templates: (projectId: number) =>
    apiClient.get(`/v2/projects/${projectId}/sampler/templates`).then(r => r.data as { enabled: boolean; templates: SamplerTemplateInfo[] }),
  profile: (projectId: number, templateId: string, evidenceId: number) =>
    apiClient
      .post(`/v2/projects/${projectId}/sampler/profile`, { template_id: templateId, evidence_id: evidenceId })
      .then(r => r.data as { file: { evidenceId: number; fileName: string; sha256: string }; profile: SamplerProfile }),
  draw: (projectId: number, body: SamplerDrawBody) =>
    apiClient.post(`/v2/projects/${projectId}/sampler/runs`, body).then(r => r.data as { run: SamplerRun }),
  list: (projectId: number) => apiClient.get(`/v2/projects/${projectId}/sampler/runs`).then(r => r.data as { runs: SamplerRun[] }),
  /** The run with every drawn entry (at most 25), the run's row-error count and, once approved, the citation. */
  get: (projectId: number, runId: number) =>
    apiClient
      .get(`/v2/projects/${projectId}/sampler/runs/${runId}`)
      .then(r => r.data as { run: SamplerRun; entries: SamplerEntry[]; rowErrorCount: number; citation: string | null }),
  /** A page of entries (the server caps limit at 1000); kind 'row_error' lists the unreadable rows. */
  entries: (projectId: number, runId: number, opts: { kind?: 'drawn' | 'row_error'; offset?: number; limit?: number } = {}) =>
    apiClient
      .get(`/v2/projects/${projectId}/sampler/runs/${runId}/entries`, {
        params: { limit: opts.limit ?? 200, offset: opts.offset ?? 0, ...(opts.kind ? { kind: opts.kind } : {}) },
      })
      .then(r => r.data as { entries: SamplerEntry[] }),
  record: (projectId: number, runId: number, results: { id: number; result: SamplerResult; note: string | null }[]) =>
    apiClient.patch(`/v2/projects/${projectId}/sampler/runs/${runId}/entries`, { results }).then(r => r.data as { recorded: number }),
  approve: (projectId: number, runId: number, note: string) =>
    apiClient.post(`/v2/projects/${projectId}/sampler/runs/${runId}/approve`, { note }).then(r => r.data as { run: SamplerRun }),
  reject: (projectId: number, runId: number, note: string) =>
    apiClient.post(`/v2/projects/${projectId}/sampler/runs/${runId}/reject`, { note }).then(r => r.data as { run: SamplerRun }),
  // Downloads go through apiClient so the Bearer token is sent; the caller saves the blob.
  exportXlsx: (projectId: number, runId: number) =>
    apiClient.get(`/v2/projects/${projectId}/sampler/runs/${runId}/export`, { responseType: 'blob' }).then(r => r.data as Blob),
  blankCsv: (projectId: number, templateId: string) =>
    apiClient.get(`/v2/projects/${projectId}/sampler/templates/${templateId}/blank`, { responseType: 'blob' }).then(r => r.data as Blob),
};

// Recommendations Import API
export const recommendationsImportAPI = {
  // Import recommendations from Foundations assessment
  importRecommendations: async (projectId: number, foundationsSessionId: string, foundationsCompanyId?: number) => {
    const response = await apiClient.post(`/recommendations-import/projects/${projectId}/import`, {
      foundationsSessionId,
      foundationsCompanyId
    });
    return response.data;
  },

  // Get import summary
  getSummary: async (projectId: number) => {
    const response = await apiClient.get(`/recommendations-import/projects/${projectId}/summary`);
    return response.data;
  },

  // Get Foundations link status for a project
  getFoundationsLink: async (projectId: number) => {
    const response = await apiClient.get(`/projects/${projectId}/foundations-link`);
    return response.data;
  },

  // Get detailed imported recommendations with validation
  getDetails: async (projectId: number, filter?: 'all' | 'deployed' | 'pending' | 'gaps') => {
    const params = new URLSearchParams();
    if (filter) params.append('filter', filter);
    const queryString = params.toString();
    const url = `/recommendations-import/projects/${projectId}/details${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  // Get Major Incident Scenarios from Foundations
  getMajorIncidentScenarios: async (projectId: number) => {
    const response = await apiClient.get(`/foundations/projects/${projectId}/major-incidents`);
    return response.data;
  },
};

// Zero Trust API
export const zeroTrustAPI = {
  // Get all Zero Trust pillars
  getPillars: async () => {
    const response = await apiClient.get('/zero-trust/pillars');
    return response.data;
  },

  // Get capabilities for a specific pillar
  getCapabilities: async (pillarId: number) => {
    const response = await apiClient.get(`/zero-trust/pillars/${pillarId}/capabilities`);
    return response.data;
  },

  // Get or create assessment for a project
  getAssessment: async (projectId: string) => {
    const response = await apiClient.get(`/zero-trust/projects/${projectId}/assessment`);
    return response.data;
  },

  // Update capability score
  updateCapabilityScore: async (
    assessmentId: number,
    capabilityId: number,
    maturityLevel: number,
    implementationStatus: string,
    evidence: string,
    gaps: string,
    remediationPlan: string,
    targetMaturityLevel: number,
    targetDate: string | null
  ) => {
    const response = await apiClient.put(
      `/zero-trust/assessments/${assessmentId}/capabilities/${capabilityId}`,
      {
        maturity_level: maturityLevel,
        implementation_status: implementationStatus,
        evidence,
        gaps,
        remediation_plan: remediationPlan,
        target_maturity_level: targetMaturityLevel,
        target_date: targetDate,
      }
    );
    return response.data;
  },

  // Recalculate maturity levels
  recalculateMaturity: async (assessmentId: number) => {
    const response = await apiClient.post(`/zero-trust/assessments/${assessmentId}/calculate`);
    return response.data;
  },

  // Get gap analysis
  getGaps: async (assessmentId: number) => {
    const response = await apiClient.get(`/zero-trust/assessments/${assessmentId}/gaps`);
    return response.data;
  },

  // Generate implementation roadmap
  generateRoadmap: async (assessmentId: number) => {
    const response = await apiClient.post(`/zero-trust/assessments/${assessmentId}/roadmap`);
    return response.data;
  },

  // Run automated Zero Trust scoring
  runAutoScore: async (projectId: string) => {
    const response = await apiClient.post(`/zero-trust/projects/${projectId}/auto-score`);
    return response.data;
  },
};

// ============================================================
// OSCAL Import API
// ============================================================
export const oscalImportAPI = {
  // Validate OSCAL file without importing
  validate: async (file: File) => {
    const formData = new FormData();
    formData.append('oscalFile', file);

    const response = await apiClient.post('/oscal/validate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Import OSCAL file to a project
  importFile: async (projectId: number, file: File, options?: { [key: string]: boolean | string | number }) => {
    const formData = new FormData();
    formData.append('oscalFile', file);
    formData.append('userId', '1'); // TODO: Get from auth context

    // Add options to form data
    if (options) {
      Object.keys(options).forEach((key) => {
        formData.append(key, String(options[key]));
      });
    }

    const response = await apiClient.post(`/oscal/projects/${projectId}/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 second timeout for file uploads
    });
    return response.data;
  },

  // Import OSCAL from URL
  importFromUrl: async (projectId: number, url: string, options?: { [key: string]: boolean | string | number }) => {
    const response = await apiClient.post(`/oscal/projects/${projectId}/import-url`, {
      url,
      userId: 1, // TODO: Get from auth context
      ...options,
    });
    return response.data;
  },

  // Get import history for a project
  getImportHistory: async (projectId: number) => {
    const response = await apiClient.get(`/oscal/projects/${projectId}/import-history`);
    return response.data;
  },
};

// ============================================================
// Unified Projects API (v2)
// ============================================================

export const unifiedProjectsAPI = {
  // Get all projects with optional filters
  getAll: async (filters?: {
    module?: 'architecture' | 'compliance' | 'threat_model' | 'threat_intel' | 'foundations';
    engagement_type?: string;
    status?: string;
    organization_id?: number;
  }): Promise<UnifiedProject[]> => {
    const params = new URLSearchParams();
    if (filters?.module) params.append('module', filters.module);
    if (filters?.engagement_type) params.append('engagement_type', filters.engagement_type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.organization_id) params.append('organization_id', filters.organization_id.toString());

    const queryString = params.toString();
    const url = `/v2/projects${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response.data.data;
  },

  // Get single project with full details
  getById: async (id: number): Promise<UnifiedProject> => {
    const response = await apiClient.get(`/v2/projects/${id}`);
    return response.data.data;
  },

  // Create new project
  create: async (projectData: CreateProjectRequest): Promise<UnifiedProject> => {
    const response = await apiClient.post('/v2/projects', projectData);
    return response.data.data;
  },

  // Update project
  update: async (id: number, updates: UpdateProjectRequest): Promise<UnifiedProject> => {
    const response = await apiClient.put(`/v2/projects/${id}`, updates);
    return response.data.data;
  },

  // Close an engagement — the one path that removes a project (backend routes/engagementClose.ts).
  // Build a records package, download it, send back the SHA-256 of what was received, then purge.
  startClosePackage: async (projectId: number): Promise<ClosePackageJob> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/close/package`);
    return response.data;
  },

  getClosePackage: async (projectId: number, jobId: string): Promise<ClosePackageJob> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/close/package/${jobId}`);
    return response.data;
  },

  downloadClosePackage: async (projectId: number, jobId: string): Promise<Blob> => {
    const response = await apiClient.get(
      `/v2/projects/${projectId}/close/package/${jobId}/download`,
      { responseType: 'blob', timeout: 0 }
    );
    return response.data;
  },

  confirmClose: async (
    projectId: number,
    jobId: string,
    sha256: string
  ): Promise<{ tombstoneId: number; projectId: number }> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/close/confirm`, {
      jobId,
      sha256,
    });
    return response.data;
  },

  getEngagementTombstone: async (projectId: number): Promise<EngagementTombstone> => {
    const response = await apiClient.get(`/v2/engagement-tombstones/${projectId}`);
    return response.data;
  },

  // Enable a module for a project
  enableModule: async (projectId: number, module: ModuleType): Promise<UnifiedProject> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/modules/${module}/enable`);
    return response.data.data;
  },

  // C7 #13 â€” disable a module (pure flag flip; data preserved, re-enable shows it again)
  disableModule: async (projectId: number, module: ModuleType): Promise<UnifiedProject> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/modules/${module}/disable`);
    return response.data.data;
  },

  // Get project standards
  getStandards: async (projectId: number): Promise<ProjectStandard[]> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/standards`);
    return response.data.data;
  },

  // Add a standard to project
  addStandard: async (projectId: number, standardData: AddStandardRequest): Promise<ProjectStandard> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/standards`, standardData);
    return response.data.data;
  },

  // Remove a standard from project. Backend cascades the delete:
  //   1. compliance_assessments rows for this (project_id, standard_id) are deleted
  //   2. dodcar.project_standards link is deleted
  // Iris-suggested mappings in evidence_requirement_mappings are NOT touched
  // (they orphan to standard_id and re-attach if the standard is added back later).
  // Path note: backend lives at /v2/compliance/... not /v2/projects/... â€” historical
  // route placement, intentionally left alone to avoid duplicating the cascade logic.
  removeStandard: async (projectId: number, standardId: string): Promise<void> => {
    await apiClient.delete(`/v2/compliance/projects/${projectId}/standards/${standardId}`);
  },

  // Get Foundations return URL for bridge-auth'd projects
  getFoundationsReturn: async (projectId: number): Promise<{
    linked: boolean;
    foundationsUrl?: string;
    sessionId?: string;
    orgName?: string;
    lastSynced?: string;
  }> => {
    try {
      const response = await apiClient.get(`/v2/projects/${projectId}/foundations-return`);
      return response.data;
    } catch {
      return { linked: false };
    }
  },

  // ==================== V2 COMPLIANCE REPORTS ====================
  // These use dodcar.project_standards for real compliance data

  // Get compliance status report data
  getComplianceStatusReport: async (projectId: number): Promise<any> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/reports/compliance-status`);
    return response.data.data;
  },

  // Export compliance status as Excel
  exportComplianceStatusReport: async (projectId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/reports/compliance-status/export`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Get gap analysis report data
  getGapAnalysisReport: async (projectId: number, standardId?: string): Promise<any> => {
    const params = standardId ? `?standard_id=${standardId}` : '';
    const response = await apiClient.get(`/v2/projects/${projectId}/reports/gap-analysis${params}`);
    return response.data.data;
  },

  // Export gap analysis as Excel
  exportGapAnalysisReport: async (projectId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/reports/gap-analysis/export`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Export risk matrix as Excel
  exportRiskMatrixReport: async (projectId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/reports/risk-matrix/export`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Foundations onboarding wizard
  getOnboardingContext: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/onboarding-context`);
    return response.data;
  },

  completeOnboarding: async (projectId: number, data?: { standard_id?: string }) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/onboarding-complete`, data || {});
    return response.data;
  },

  /**
   * A coarse "lens" standard (CSF 2.0 by default) scored from the hub the project actually
   * assessed against. Read-only and derived — the server writes nothing, and the response is
   * a derivation the UI must render as one, never as an assessment.
   */
  getLensPosture: async (projectId: number, lens?: string, hub?: string) => {
    const params = new URLSearchParams();
    if (lens) params.set('lens', lens);
    if (hub) params.set('hub', hub);
    const qs = params.toString();
    const response = await apiClient.get(
      `/v2/projects/${projectId}/lens-posture${qs ? `?${qs}` : ''}`
    );
    return response.data?.data;
  },
};

// ============================================================
// Compliance Management API
// ============================================================
export interface ComplianceStandard {
  id: number;
  standard_id: string;
  standard_name: string;
  version: string;
  effective_date: string;
  jurisdiction: string;
  description: string;
  applicable_sectors: string[];
  equipment_types: Record<string, any>;
  total_requirements: number;
  mandatory_requirements: number;
  optional_requirements: number;
  // Distinct min_level values present in the framework's requirements.
  // Populated by GET /v2/compliance/standards (2026-05-20). Length > 1 = the
  // framework has multiple levels (e.g., cmmc-2.0 = [1,2,3]); the picker
  // should fan it out into N tiles, one per level. Empty array or length 1
  // = single tile.
  available_levels?: number[];
  created_at: string;
  updated_at: string;
}

export interface ComplianceRequirement {
  id: number;
  standard_id: string;
  requirement_id: string;
  requirement_name: string;
  category: string;
  description: string;
  applicability_matrix: Record<string, boolean>;
  reference_standards: Record<string, string>;
  auto_category: 'POLICY' | 'TECHNICAL' | 'HYBRID' | 'N/A';
  auto_category_reasoning: string;
  prism_mapping: string;
  critical_risk: string;
  created_at: string;
  updated_at: string;
}

export interface ComplianceProject {
  id: number;
  project_id?: number;
  organization_id?: number;
  standard_id: string;
  equipment_type?: string;
  project_name: string;
  description?: string;
  deadline?: string;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD';
  created_by?: number;
  created_by_name?: string;
  standard_name?: string;
  standard_version?: string;
  total_requirements?: number;
  created_at: string;
  updated_at: string;
}

export interface LinkedEvidence {
  id: number;
  evidence_id: number;
  file_name: string;
  document_type: string;
  relevance_score: number;
  reasoning?: string;
  confirmed_at?: string;
  // Option C (mig 312): provenance flag distinguishing direct Iris/user
  // measurements from objective-row inheritance (v2.1 path with
  // target_granularity='objective'). NULL on pre-mig rows; UI treats
  // NULL as 'measured' for back-compat.
  inference_source?: 'measured' | 'inherited_from_practice' | 'vendor_asserted' | null;
  // Evidence lifecycle step 2: present only when the server flag is on. relevance_score stays the stored value.
  lifecycle_penalty?: DocPenaltyWire;
}

export interface ComplianceAssessment {
  id: number;
  project_standard_id: number;
  requirement_id: string;
  status: 'YES' | 'NO' | 'PARTIAL' | 'UNKNOWN' | 'POLICY' | 'N/A';
  evidence?: string;
  document_section?: string;
  notes?: string;
  // Task 5 (2026-07-27): the SSP §13 implementation narrative column (mig 359).
  // `notes` remains the machine scratchpad the narrative used to share with
  // machine-write markers before this column existed.
  impl_narrative?: string;
  priority?: 'SHOWSTOPPER' | 'HIGH' | 'MEDIUM' | 'LOW';
  confidence_score?: number;
  assessed_by?: number;
  assessed_by_name?: string;
  assessed_at?: string;
  requirement_name?: string;
  category?: string;
  description?: string;
  auto_category?: string;
  auto_category_reasoning?: string;
  critical_risk?: string;
  linked_evidence?: LinkedEvidence[];
  // Evidence lifecycle step 2: control-level penalty; present only when the server flag is on.
  lifecycle_penalty?: CtlPenaltyWire;
  created_at: string;
  updated_at: string;
}

export interface ComplianceSummary {
  project_id: number;
  project_name: string;
  standard_id: string;
  equipment_type?: string;
  total_requirements: number;
  applicable_requirements: number;
  na_requirements: number;
  compliant_count: number;
  compliance_percentage: number;
  status_breakdown: {
    YES: number;
    POLICY: number;
    PARTIAL: number;
    NO: number;
    UNKNOWN: number;
    'N/A': number;
  };
}

/**
 * Legacy v1 compliance API â€” being migrated to complianceV2API.
 * Remaining methods: unified assessments (Phase 3).
 * Comparison methods now use v2 endpoints.
 * Do NOT add new methods here.
 */
export const complianceAPI = {
  // ==================== MULTI-STANDARD COMPARISON (now on v2 endpoints) ====================

  compareStandards: async (standards: string[]) => {
    const params = new URLSearchParams();
    params.append('standards', standards.join(','));
    const response = await apiClient.get(`/v2/compliance/compare?${params.toString()}`, {
      timeout: 600000,
    });
    return response.data;
  },

  suggestMappings: async (sourceStandardId: string, targetStandardId: string, limit: number = 10) => {
    const response = await apiClient.post('/v2/compliance/suggest-mappings', {
      source_standard_id: sourceStandardId,
      target_standard_id: targetStandardId,
      limit
    }, {
      timeout: 600000,
    });
    return response.data;
  },

  applySuggestedMappings: async (mappings: Array<{
    source_standard_id: string;
    source_requirement_id: string;
    target_standard_id: string;
    target_requirement_id: string;
    mapping_type: string;
    mapping_strength: number;
    reasoning: string;
  }>) => {
    const response = await apiClient.post('/v2/compliance/apply-suggestions', { mappings });
    return response.data;
  },

  // ==================== UNIFIED ASSESSMENTS ====================
  // Get assessments for a project+standard with linked evidence from Evidence Library

  // Get unified assessments with linked evidence
  /**
   * The OT lens (NIST SP 800-82r3) for one control.
   *
   * ⚠ `derived` is load-bearing, not decoration. False means the overlay annotates THIS
   * control — NIST said this about this control. True means it annotates the 800-53
   * control this one maps to, which is a composition NIST did not make. The UI must show
   * the hop rather than implying NIST wrote OT guidance about a CMMC practice.
   */
  getControlOverlay: async (
    standardId: string,
    requirementId: string
  ): Promise<{
    success: boolean;
    data: {
      applicable: boolean;
      derived: boolean;
      baselines: string[];
      withGuidance: number;
      rows: Array<{
        requirement_code: string;
        baseline: string | null;
        guidance: string | null;
        source_authority: string | null;
        source_olir_id: number | null;
        source_hash_verified: boolean;
        via_requirement_id: string | null;
      }>;
    };
  }> => {
    const params = new URLSearchParams({
      standard_id: standardId,
      requirement_id: requirementId,
    });
    const response = await apiClient.get(`/v2/compliance/overlays/control?${params}`);
    return response.data;
  },

  /**
   * Cross-references for one control, from the crosswalk table in both directions, for any
   * standard. `source_authority` is carried so an Owner row and a Non-Owner row can be shown
   * differently — never collapse it.
   */
  getControlCrosswalk: async (
    standardId: string,
    requirementId: string
  ): Promise<{
    success: boolean;
    data: { rows: ControlCrosswalkRow[] };
  }> => {
    const params = new URLSearchParams({
      standard_id: standardId,
      requirement_id: requirementId,
    });
    const response = await apiClient.get(`/v2/compliance/crosswalk/control?${params}`);
    return response.data;
  },

  getUnifiedAssessments: async (projectId: number, standardId: string): Promise<{
    success: boolean;
    data: ComplianceAssessment[];
    count: number;
    project_standard_id?: number;
  }> => {
    const response = await apiClient.get(
      `/v2/compliance/unified/projects/${projectId}/standards/${standardId}/assessments`
    );
    return response.data;
  },

  // CloudSeven F4 #2 â€” set a per-objective verdict (met/not_met/not_verified).
  // Server immediately recomputes the parent practice's status + cached score.
  setObjectiveVerdict: async (
    projectStandardId: number,
    objectiveRequirementId: number,
    status: 'met' | 'not_met' | 'not_verified'
  ): Promise<{
    success: boolean;
    data: unknown;
    practice: { requirement_id: string; status: string | null };
  }> => {
    const response = await apiClient.post(
      `/v2/compliance/assessments/${projectStandardId}/objectives/${objectiveRequirementId}/verdict`,
      { status }
    );
    return response.data;
  },

  // Bulk save assessments for a project+standard
  saveUnifiedAssessments: async (
    projectId: number,
    standardId: string,
    assessments: Array<{
      requirement_id: string;
      status: string;
      notes: string;
      // assigned_to: Cloud 7 Notes slice 2. Optional â€” omit to leave unchanged
      // server-side, `null` to clear, number to assign. Server-side discriminator
      // is `hasAssignedTo` so undefined ≠ null.
      assigned_to?: number | null;
      // CloudSeven P0 (2026-05-20): persist Iris confidence to the queryable
      // confidence_score column. Same omit/null/number convention as assigned_to.
      confidence_score?: number | null;
      // Task #11 Slice 3a (2026-05-24): reviewer + workflow state.
      // Same omit/null/value convention. Server-side same-org validation +
      // CHECK-constraint enum (unassigned | assigned | in_progress |
      // review_pending | signed_off | returned_for_rework). Auto-transition:
      // assigning a user with no current review_status auto-sets 'assigned'.
      reviewer_id?: number | null;
      review_status?:
        | 'unassigned'
        | 'assigned'
        | 'in_progress'
        | 'review_pending'
        | 'signed_off'
        | 'returned_for_rework'
        | null;
      // Task #11 Slice 3b (2026-05-24): due date. ISO 8601 string in
      // ('2026-06-15T00:00:00Z' or '2026-06-15'); null clears; undefined
      // omits. Server defensively normalizes via Date parse â€” invalid
      // strings silently clear so a bad value doesn't 400 the autosave.
      due_at?: string | null;
    }>
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      project_standard_id: number;
      saved_count: number;
      overall_score: number;
    };
  }> => {
    const response = await apiClient.put(
      `/v2/compliance/unified/projects/${projectId}/standards/${standardId}/assessments`,
      { assessments }
    );
    return response.data;
  },
};

// ==================== EVIDENCE API (v2) ====================
// Project-level evidence management with AI analysis

export interface EvidenceDocument {
  id: number;
  project_id: number;
  file_name: string;
  original_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  document_type: string;
  title: string;
  description?: string;
  summary?: string;
  categories?: string[];
  key_findings?: string[];
  compliance_controls?: string[];
  extracted_text?: string;
  ai_analyzed: boolean;
  /** JSONB bag; `lifecycle` is present only for policy docs when EVIDENCE_LIFECYCLE_ENABLED. */
  ai_extracted_metadata?: {
    lifecycle?: import('../components/evidence/documentControl').DocumentLifecycle;
    /** Diagram vision status record (spec 2026-10-10); absent until a read is attempted. */
    diagram?: {
      state: 'reading' | 'proposed' | 'reviewed' | 'empty' | 'not_read' | 'unavailable' | 'failed';
      proposal_id?: number | null;
      items?: number;
      pages_read?: number;
      pages_skipped?: number;
      truncated?: boolean;
      error_code?: string | null;
      at?: string;
    };
    [k: string]: unknown;
  } | null;
  /** F6: server 4 KB sniff of an .xml (migration 429); null when not sniffed or the flag is off. */
  is_drawio_xml?: boolean | null;
  analysis_date?: string;
  ai_analysis_status?:
    | 'pending'
    | 'analyzing'
    | 'complete'
    | 'complete_image'
    | 'failed_unavailable'
    | 'failed_ocr_unavailable'
    | 'failed_file_missing'
    | 'failed_unsupported'
    | 'failed_encrypted'
    | 'failed_corrupt'
    | 'failed_empty'
    | 'failed_iris';
  /** User-facing friendly guidance when ai_analysis_status is a `failed_*` value. */
  ai_analysis_error?: string | null;
  /**
   * Timestamp set by `autoMapEvidenceToRequirements` at the end of every run.
   * NULL = auto-map has never been attempted (UI should show "Run Auto-Map" CTA).
   * NOT NULL + 0 mapping rows = Iris was asked and had nothing confident to
   * cite (UI should show a neutral "No direct mappings" indicator, NOT the
   * CTA â€” re-running won't produce different results and wastes GPU time).
   * NOT NULL + N mapping rows = normal N-mapped state.
   */
  auto_map_attempted_at?: string | null;
  /**
   * ⚠ DENORMALIZED and drifts. Kept because other callers still read it, but it must not
   * feed any count the UI renders — measured on staging 2026-08-12 it held 88 entries for
   * a project with 3 real mappings, and it stores two different formats (a requirement
   * code in some rows, a serial PK in others). Use `mapping_count` instead.
   */
  linked_requirements?: string[];
  /** PRISM-analysed mappings for this document, from `evidence_requirement_mappings`. */
  mapping_count?: number;
  /** Distinct frameworks those mappings land in — the ripple. */
  mapping_standard_count?: number;
  mapping_by_standard?: Array<{ standard_id: string; count: number }>;
  /**
   * Vendor assertions (e.g. Vanta), reported SEPARATELY and never folded into
   * `mapping_count`. PRISM records that the customer accepted another tool's mapping; it
   * does not attest to it.
   */
  vendor_asserted_count?: number;
  /**
   * Mappings whose stored `requirement_code` disagrees with what `requirement_id` resolves
   * to now — i.e. the row was repointed. Non-zero means this document's mappings are
   * citing controls they were not authored against. Migration 377 records the witness;
   * this is what reads it.
   */
  mapping_drift_count?: number;
  linked_assets?: number[];
  version?: number;
  content_hash?: string;
  created_at: string;
  updated_at: string;
}

/** Live progress returned by /evidence/:id/coverage/progress. */
export interface EvidenceCoverageProgress {
  in_flight: boolean;
  evidence_id?: number;
  phase?: 'initializing' | 'running' | 'storing' | 'done' | 'failed' | 'skipped';
  total_batches?: number;
  completed_batches?: number;
  failed_batches?: number;
  total_requirements?: number;
  started_at?: number;
  message?: string;
}

export interface EvidenceCoverageFailure {
  id: number;
  standard_id: string | null;
  batch_start_idx: number;
  batch_end_idx: number;
  requirement_ids: string[];
  reason: string;
  retry_of: number | null;
  failed_at: string;
}

export interface EvidenceVersion {
  id: number | null;
  evidence_id: number;
  version: number;
  file_name: string;
  original_name: string;
  file_size: number;
  mime_type: string;
  content_hash?: string;
  uploaded_by?: number;
  uploaded_by_email?: string;
  created_at: string;
  is_current: boolean;
}

export interface EvidenceStats {
  total_documents: number;
  analyzed_documents: number;
  unanalyzed_documents: number;
  by_type: {
    policy: number;
    architecture: number;
    config: number;
    screenshot: number;
    certificate: number;
    audit_log: number;
  };
  total_size_bytes: number;
  total_size_mb: string;
}

export const evidenceAPI = {
  // Get all evidence for a project
  getEvidence: async (
    projectId: number,
    filters?: {
      document_type?: string;
      ai_analyzed?: boolean;
      search?: string;
    }
  ): Promise<{ success: boolean; data: EvidenceDocument[]; count: number }> => {
    const params = new URLSearchParams();
    if (filters?.document_type) params.append('document_type', filters.document_type);
    if (filters?.ai_analyzed !== undefined) params.append('ai_analyzed', String(filters.ai_analyzed));
    if (filters?.search) params.append('search', filters.search);

    const response = await apiClient.get(`/v2/projects/${projectId}/evidence?${params.toString()}`);
    return response.data;
  },

  // Upload new evidence
  uploadEvidence: async (
    projectId: number,
    file: File,
    options?: {
      title?: string;
      description?: string;
      document_type?: string;
      auto_analyze?: boolean;
      replace_evidence_id?: number;
    }
  ): Promise<{ success: boolean; data: EvidenceDocument; versioned?: boolean; previous_version?: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.title) formData.append('title', options.title);
    if (options?.description) formData.append('description', options.description);
    if (options?.document_type) formData.append('document_type', options.document_type);
    if (options?.auto_analyze !== undefined) formData.append('auto_analyze', String(options.auto_analyze));
    if (options?.replace_evidence_id) formData.append('replace_evidence_id', String(options.replace_evidence_id));

    const response = await apiClient.post(`/v2/projects/${projectId}/evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Get single evidence document
  getEvidenceById: async (evidenceId: number): Promise<{ success: boolean; data: EvidenceDocument }> => {
    const response = await apiClient.get(`/v2/evidence/${evidenceId}`);
    return response.data;
  },

  // Run AI analysis on evidence
  /**
   * Kick off analysis. Returns 202 immediately — analysis runs in the background.
   *
   * ⚠ This used to block until analysis finished and return the summary inline. It now returns
   * as soon as the job is accepted, because the server-side pipeline runs auto-mapping (N Iris
   * calls) and coverage evaluation inline; holding the connection through that blew the axios
   * and nginx timeout ceilings. Progress is `ai_analysis_status` on the document.
   *
   * Callers that need the RESULT (not just the acknowledgement) must follow this with
   * `waitForAnalysis()` — notably anything that reads the summary or extracted text afterwards.
   */
  analyzeEvidence: async (evidenceId: number): Promise<{
    success: boolean;
    message: string;
    data: { evidence_id: number; status: string };
  }> => {
    const response = await apiClient.post(`/v2/evidence/${evidenceId}/analyze`, {});
    return response.data;
  },

  /**
   * Poll until analysis reaches a terminal state, then return the final document.
   *
   * Terminal = `complete` | `complete_image` | any `failed_*`. Everything else — including
   * `pending` and `analyzing` — keeps waiting. `pending` MUST NOT be treated as terminal: the
   * POST returns 202 before the background job has stamped `analyzing`, so a fast first poll
   * legitimately sees the pre-run status and would otherwise exit immediately.
   *
   * Resolves (never throws) on timeout so a caller can decide; check the returned status.
   */
  waitForAnalysis: async (
    evidenceId: number,
    opts: { timeoutMs?: number; intervalMs?: number; onTick?: (doc: EvidenceDocument) => void } = {}
  ): Promise<{ timedOut: boolean; document: EvidenceDocument | null }> => {
    const timeoutMs = opts.timeoutMs ?? 10 * 60 * 1000;
    const intervalMs = opts.intervalMs ?? 3000;
    const isTerminal = (s?: string | null) =>
      !!s && (s === 'complete' || s === 'complete_image' || s.startsWith('failed'));

    const started = Date.now();
    let last: EvidenceDocument | null = null;
    while (Date.now() - started < timeoutMs) {
      await new Promise(r => setTimeout(r, intervalMs));
      try {
        const res = await evidenceAPI.getEvidenceById(evidenceId);
        last = res.data;
        opts.onTick?.(last);
        if (isTerminal(last?.ai_analysis_status)) return { timedOut: false, document: last };
      } catch {
        // Transient read failure — keep polling; the job is server-side and unaffected.
      }
    }
    return { timedOut: true, document: last };
  },

  // Delete evidence
  deleteEvidence: async (
    evidenceId: number
  ): Promise<{ success: boolean; message: string; data?: { affected_controls?: string[] } }> => {
    const response = await apiClient.delete(`/v2/evidence/${evidenceId}`);
    return response.data;
  },

  // Update editable metadata on an evidence document (does not re-analyze)
  updateEvidenceMetadata: async (
    evidenceId: number,
    updates: { document_type?: string; title?: string; description?: string }
  ): Promise<{ success: boolean; data: EvidenceDocument }> => {
    const response = await apiClient.put(`/v2/evidence/${evidenceId}`, updates);
    return response.data;
  },

  // Download evidence file
  downloadEvidence: async (evidenceId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v2/evidence/${evidenceId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Poll-friendly: returns {in_flight:false} when no coverage eval is
  // currently running. Safe to call every 2s while the user is looking at
  // a document â€” no GPU touch.
  getCoverageProgress: async (
    evidenceId: number
  ): Promise<{ success: boolean; data: EvidenceCoverageProgress }> => {
    const response = await apiClient.get(`/v2/evidence/${evidenceId}/coverage/progress`);
    return response.data;
  },

  // Per-doc list of coverage eval batch failures (Iris parse failures,
  // timeouts). UI surfaces these as "N reqs failed â€” retry?"
  getCoverageFailures: async (
    evidenceId: number
  ): Promise<{ success: boolean; data: EvidenceCoverageFailure[] }> => {
    const response = await apiClient.get(`/v2/evidence/${evidenceId}/coverage/failures`);
    return response.data;
  },

  // Get version history for an evidence document
  getVersionHistory: async (evidenceId: number): Promise<{
    success: boolean;
    data: { current_version: number; versions: EvidenceVersion[] };
  }> => {
    const response = await apiClient.get(`/v2/evidence/${evidenceId}/versions`);
    return response.data;
  },

  // Download a specific version of an evidence file
  downloadVersion: async (evidenceId: number, version: number): Promise<Blob> => {
    const response = await apiClient.get(`/v2/evidence/${evidenceId}/versions/${version}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Link evidence to compliance requirements
  linkRequirements: async (
    evidenceId: number,
    requirementIds: string[]
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post(`/v2/evidence/${evidenceId}/link-requirements`, {
      requirement_ids: requirementIds,
    });
    return response.data;
  },

  // AI suggest compliance controls
  suggestControls: async (
    evidenceId: number,
    standardId?: string
  ): Promise<{
    success: boolean;
    data: {
      document_id: number;
      standard_id: string;
      suggestions: Array<{
        control_id: string;
        control_name: string;
        relevance: 'HIGH' | 'MEDIUM' | 'LOW';
        evidence_type: 'DIRECT' | 'INDIRECT' | 'SUPPORTING';
        reasoning: string;
      }>;
      coverage_summary: string;
      processingTime: number;
    };
  }> => {
    const response = await apiClient.post(`/v2/evidence/${evidenceId}/suggest-controls`, {
      standard_id: standardId,
    }, {
      timeout: 600000, // 5 minutes for AI processing
    });
    return response.data;
  },

  // Get evidence statistics
  getStats: async (projectId: number): Promise<{ success: boolean; data: EvidenceStats }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/evidence/stats`);
    return response.data;
  },

  // Auto-map evidence to compliance requirements
  autoMap: async (evidenceId: number, options?: { standard_id?: string; max_mappings?: number }): Promise<{
    success: boolean;
    data: {
      evidence_id: number;
      standards_processed: string[];
      total_mappings: number;
      mappings: EvidenceMapping[];
    };
  }> => {
    const response = await apiClient.post(`/v2/evidence/${evidenceId}/auto-map`, options || {}, {
      timeout: 600000, // 5 minutes for AI processing
    });
    return response.data;
  },

  // Get all mappings for an evidence document
  getMappings: async (evidenceId: number): Promise<{
    success: boolean;
    data: EvidenceMapping[];
    count: number;
  }> => {
    const response = await apiClient.get(`/v2/evidence/${evidenceId}/mappings`);
    return response.data;
  },

  // Add or confirm a manual mapping
  addMapping: async (evidenceId: number, mapping: {
    standard_id: string;
    requirement_id: string;
    evidence_type?: 'DIRECT' | 'INDIRECT' | 'SUPPORTING';
    reasoning?: string;
  }): Promise<{ success: boolean; data: EvidenceMapping; assessment?: ControlRegrade | null }> => {
    const response = await apiClient.post(`/v2/evidence/${evidenceId}/mappings`, mapping);
    return response.data;
  },

  // Remove a mapping
  removeMapping: async (evidenceId: number, mappingId: number): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/v2/evidence/${evidenceId}/mappings/${mappingId}`);
    return response.data;
  },

  // CloudSeven Feedback 4 (#4) â€” targeted, focused Iris mapping of ONE doc against
  // ONE control. Never wipes other mappings; preserves a confirmed mapping. Returns
  // mapped:false (with reasoning) when Iris judges the doc not to be direct evidence.
  mapRequirement: async (
    evidenceId: number,
    body: { standard_id: string; requirement_id: string }
  ): Promise<{
    success: boolean;
    mapped: boolean;
    already_confirmed?: boolean;
    score?: number;
    reasoning?: string;
    data?: EvidenceMapping | null;
    assessment?: ControlRegrade | null;
  }> => {
    const response = await apiClient.post(
      `/v2/evidence/${evidenceId}/map-requirement`,
      body,
      { timeout: 120000 } // single-pair Iris call; cold GPU can take ~60s
    );
    return response.data;
  },

  // Confirm an AI-suggested mapping
  confirmMapping: async (evidenceId: number, mappingId: number): Promise<{ success: boolean; data: EvidenceMapping }> => {
    const response = await apiClient.put(`/v2/evidence/${evidenceId}/mappings/${mappingId}/confirm`);
    return response.data;
  },

  // Reject an AI-suggested mapping
  rejectMapping: async (evidenceId: number, mappingId: number): Promise<{ success: boolean }> => {
    const response = await apiClient.put(`/v2/evidence/${evidenceId}/mappings/${mappingId}/reject`);
    return response.data;
  },

  // Bulk confirm suggested mappings above a confidence threshold
  confirmAllMappings: async (evidenceId: number, minConfidence?: number): Promise<{ success: boolean; count: number }> => {
    const response = await apiClient.put(`/v2/evidence/${evidenceId}/mappings/confirm-all`, {
      min_confidence: minConfidence || 0
    });
    return response.data;
  },

  // Get mapping coverage for a project
  getMappingCoverage: async (projectId: number, standardId?: string): Promise<{
    success: boolean;
    data: Array<{
      standard_id: string;
      total_requirements: number;
      mapped_requirements: number;
      coverage_percent: number;
    }>;
  }> => {
    const params = standardId ? { standard_id: standardId } : {};
    const response = await apiClient.get(`/v2/projects/${projectId}/mapping-coverage`, { params });
    return response.data;
  },

  // Get evidence mapped to a specific requirement
  getEvidenceForRequirement: async (standardId: string, requirementId: string): Promise<{
    success: boolean;
    data: EvidenceDocument[];
    count: number;
  }> => {
    const response = await apiClient.get(`/v2/requirements/${standardId}/${requirementId}/evidence`);
    return response.data;
  },

  // Extract architecture from evidence document and create System Model entries
  extractArchitecture: async (evidenceId: number): Promise<{
    success: boolean;
    message: string;
    data: {
      assets_created: number;
      assets_skipped: number;
      data_flows_created: number;
      data_flows_skipped: number;
      countermeasures_created: number;
      countermeasures_skipped: number;
      extraction_confidence: number;
      processing_time_ms: number;
      created: {
        assets: Array<{ id: string; name: string }>;
        data_flows: Array<{ id: string; name: string }>;
        countermeasures: Array<{ id: string; name: string }>;
      };
      skipped: {
        assets: string[];
        data_flows: string[];
        countermeasures: string[];
      };
      needs_review?: Array<{
        input: { name: string; asset_type?: string; [key: string]: any };
        candidates: Array<{
          asset_id: string;
          asset_name: string;
          asset_type: string;
          confidence: 'medium' | 'low';
          score: number;
          reason: string;
        }>;
      }>;
      notes?: string;
      model?: string;
    };
  }> => {
    const response = await apiClient.post(`/v2/evidence/${evidenceId}/extract-architecture`, {}, {
      timeout: 600000, // 10 minutes for AI extraction
    });
    return response.data;
  },

  // ---- Batch Operations ----

  batchUpload: async (
    projectId: number,
    files: File[],
    options?: { document_type?: string; auto_analyze?: boolean }
  ): Promise<{ success: boolean; data: { evidence_ids: number[]; file_count: number; batch_id?: string } }> => {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    if (options?.document_type) formData.append('document_type', options.document_type);
    if (options?.auto_analyze !== undefined) formData.append('auto_analyze', String(options.auto_analyze));

    const response = await apiClient.post(`/v2/projects/${projectId}/evidence/batch-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
    return response.data;
  },

  batchAnalyze: async (
    projectId: number,
    evidenceIds?: number[],
    allUnanalyzed?: boolean
  ): Promise<{ success: boolean; data: { batch_id: string; total: number } }> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/evidence/batch-analyze`, {
      evidence_ids: evidenceIds,
      all_unanalyzed: allUnanalyzed,
    });
    return response.data;
  },

  batchAutoMap: async (
    projectId: number,
    evidenceIds: number[]
  ): Promise<{ success: boolean; data: { batch_id: string; total: number } }> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/evidence/batch-auto-map`, {
      evidence_ids: evidenceIds,
    });
    return response.data;
  },

  batchDelete: async (
    projectId: number,
    evidenceIds: number[]
  ): Promise<{ success: boolean; data: { deleted_count: number; affected_controls?: string[] } }> => {
    const response = await apiClient.delete(`/v2/projects/${projectId}/evidence/batch-delete`, {
      data: { evidence_ids: evidenceIds },
    });
    return response.data;
  },

  batchConfirmMappings: async (
    projectId: number,
    evidenceIds: number[],
    minConfidence?: number
  ): Promise<{ success: boolean; data: { confirmed_count: number } }> => {
    const response = await apiClient.put(`/v2/projects/${projectId}/evidence/batch-confirm-mappings`, {
      evidence_ids: evidenceIds,
      min_confidence: minConfidence ?? 0.7,
    });
    return response.data;
  },

  batchAssess: async (
    projectId: number,
    standardIds?: string[]
  ): Promise<{ success: boolean; data: { standards_assessed: number; results: any[] } }> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/evidence/batch-assess`, {
      standard_ids: standardIds,
    });
    return response.data;
  },

  getBatchStatus: async (
    projectId: number,
    batchId: string
  ): Promise<{
    success: boolean;
    data: {
      batch_id: string;
      operation: string;
      status: 'running' | 'completed' | 'failed';
      total: number;
      completed: number;
      failed: number;
      items: Array<{ id: number; name: string; status: string; error?: string }>;
      avg_seconds_per_file?: number;
    };
  }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/evidence/batch-status/${batchId}`);
    return response.data;
  },

  // Get architecture evidence (assets, countermeasures, data flows)
  getArchitectureEvidence: async (projectId: number): Promise<{
    success: boolean;
    data: {
      summary: {
        total_assets: number;
        security_tools: number;
        countermeasures: {
          total: number;
          implemented: number;
          planned: number;
        };
        data_flows: number;
      };
      security_tools: Array<{
        id: string;
        name: string;
        category: string;
        description: string;
        created_at: string;
      }>;
      infrastructure_assets: Array<{
        id: string;
        name: string;
        asset_type: string;
        description: string;
        created_at: string;
      }>;
      countermeasures: {
        implemented: Array<{
          id: string;
          name: string;
          type: string;
          description: string;
          nist_controls: string;
          effectiveness_rating: string;
          created_at: string;
        }>;
        planned: Array<{
          id: string;
          name: string;
          type: string;
          description: string;
          nist_controls: string;
          effectiveness_rating: string;
          created_at: string;
        }>;
      };
      data_flows: Array<{
        id: string;
        name: string;
        description: string;
        classification: string;
        created_at: string;
      }>;
    };
  }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/evidence/architecture`);
    return response.data;
  },
};

// Evidence mapping interface
export interface EvidenceMapping {
  id: number;
  evidence_id: number;
  standard_id: string;
  requirement_id: string;
  mapping_type: 'ai_suggested' | 'manual' | 'confirmed';
  relevance_score: number;
  evidence_type: 'DIRECT' | 'INDIRECT' | 'SUPPORTING';
  reasoning: string;
  requirement_name?: string;
  category?: string;
  requirement_description?: string;
  confirmed_by?: number;
  confirmed_at?: string;
  created_at: string;
  updated_at: string;
  // Evidence lifecycle step 2: present only when the server flag is on.
  lifecycle_penalty?: DocPenaltyWire;
}

// F5 #4 â€” returned by the single-control mapping endpoints when the control was
// re-graded from its evidence (null when nothing was mapped / no standard link).
export interface ControlRegrade {
  status: 'compliant' | 'partial' | 'non_compliant';
  confidence: number; // 0..1
  notes: string;
}

// ============================================================
// Compliance V2 API (Unified Model using dodcar.project_standards)
// ============================================================

export interface ComplianceV2Project {
  project_id: number;
  project_name: string;
  description: string;
  industry_sector: string | null;
  client_name: string | null;
  engagement_type: string;
  organization_id: number | null;
  module_compliance: boolean;
  standards: Array<{
    project_standard_id: number;
    standard_id: string;
    target_level: string | null;
    assessment_status: string;
    overall_score: number | null;
    total_assessments: number;
    compliant_count: number;
    compliance_percentage: number;
  }>;
}

export interface ComplianceV2Assessment {
  assessment_id: number;
  requirement_id: string;
  status: string;
  evidence: string | null;
  notes: string | null;
  priority: string | null;
  confidence_score: number | null;
  assessed_at: string | null;
  project_standard_id: number;
  requirement_name: string | null;
  requirement_description: string | null;
  category: string | null;
  critical_risk: string | null;
}

export interface ComplianceV2Summary {
  standards: Array<{
    standard_id: string;
    standard_name: string;
    target_level: string | null;
    assessment_status: string;
    overall_score: string | null;
    total_requirements: string;
    compliant: string;
    partial: string;
    gaps: string;
    not_assessed: string;
    /** NULL when nothing is scored yet. Kept in the data; never shown as a score (Q8, 2026-10-29). */
    compliance_percentage: string | number | null;
    /** Q8 facts (2026-10-29). Optional: an older backend omits them. */
    applicable?: number;
    evaluated?: number;
    evaluated_pct?: number | null;
    open_gaps?: number;
    critical_open_gaps?: number | null;
    severity_basis?: 'sprs' | 'rated' | 'unrated';
  }>;
  totals: {
    total: number;
    compliant: number;
    partial: number;
    gaps: number;
    not_assessed: number;
    /** Kept in the data; never shown as a score (Q8). */
    compliance_percentage: number;
    /** Q8 facts line: "N standards tracked · N open gaps (N critical) · N% evaluated". */
    standards_tracked?: number;
    applicable?: number;
    evaluated?: number;
    evaluated_pct?: number | null;
    open_gaps?: number;
    /** null = no standard is rated or SPRS-weighted. */
    critical_open_gaps?: number | null;
  };
}

// Custom Standard Types
export interface CustomStandard {
  id: number;
  standard_id: string;
  standard_name: string;
  version: string;
  description: string;
  jurisdiction: string;
  applicable_sectors: string[];
  is_custom: boolean;
  created_by: string | null;
  total_requirements: number;
  created_at: string;
  updated_at: string;
}

export interface CustomRequirement {
  requirement_id: string;
  requirement_name: string;
  category: string;
  description: string;
  priority?: string;
}

export interface ImportStandardResult {
  standard: {
    id: number;
    standard_id: string;
    standard_name: string;
  };
  requirements_imported: number;
}

export const complianceV2API = {
  // Get all projects with compliance module enabled
  getProjects: async (): Promise<ComplianceV2Project[]> => {
    const response = await apiClient.get('/v2/compliance/projects');
    return response.data.data;
  },

  // Get single project compliance details
  getProject: async (projectId: number): Promise<ComplianceV2Project> => {
    const response = await apiClient.get(`/v2/compliance/projects/${projectId}`);
    return response.data.data;
  },

  // Get compliance summary for a project
  getSummary: async (projectId: number): Promise<ComplianceV2Summary> => {
    const response = await apiClient.get(`/v2/compliance/summary/${projectId}`);
    return response.data.data;
  },

  // Add a standard to a project
  addStandard: async (projectId: number, standardId: string, targetLevel?: string): Promise<any> => {
    const response = await apiClient.post(`/v2/compliance/projects/${projectId}/standards`, {
      standard_id: standardId,
      target_level: targetLevel,
    });
    return response.data.data;
  },

  // Get assessments for a project standard
  getAssessments: async (
    projectStandardId: number,
    filters?: { status?: string; category?: string }
  ): Promise<ComplianceV2Assessment[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.category) params.append('category', filters.category);
    const queryString = params.toString();
    const url = `/v2/compliance/assessments/${projectStandardId}${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response.data.data;
  },

  // Update a single assessment
  updateAssessment: async (
    assessmentId: number,
    updates: {
      status?: string;
      evidence?: string;
      notes?: string;
      priority?: string;
      confidence_score?: number;
      // Task #11 Slice 3a (2026-05-24): reviewer + workflow state on the
      // single-PUT path (test-only today; live path is saveUnifiedAssessments).
      reviewer_id?: number | null;
      review_status?:
        | 'unassigned'
        | 'assigned'
        | 'in_progress'
        | 'review_pending'
        | 'signed_off'
        | 'returned_for_rework'
        | null;
      // Task #11 Slice 3b (2026-05-24): due date.
      due_at?: string | null;
    }
  ): Promise<ComplianceV2Assessment> => {
    const response = await apiClient.put(`/v2/compliance/assessments/${assessmentId}`, updates);
    return response.data.data;
  },

  // Bulk update assessments
  bulkUpdateAssessments: async (
    projectStandardId: number,
    assessments: Array<{
      assessment_id: number;
      status?: string;
      evidence?: string;
      notes?: string;
    }>
  ): Promise<{ updated: number }> => {
    const response = await apiClient.put(
      `/v2/compliance/assessments/${projectStandardId}/bulk`,
      { assessments }
    );
    return response.data.data;
  },

  // Get available standards
  getAvailableStandards: async (): Promise<
    Array<{
      standard_id: string;
      standard_name: string;
      version: string;
      total_requirements: number;
    }>
  > => {
    const response = await apiClient.get('/v2/compliance/standards');
    return response.data.data;
  },

  // ==================== CUSTOM STANDARDS ====================

  // Get all standards (with is_custom flag)
  getAllStandards: async (filter?: 'all' | 'custom' | 'builtin'): Promise<CustomStandard[]> => {
    const params = filter && filter !== 'all' ? `?filter=${filter}` : '';
    const response = await apiClient.get(`/v2/compliance/standards${params}`);
    return response.data.data;
  },

  // Get single standard with requirements. Optional projectId scopes the
  // requirement list by the project's target_level (e.g., a CMMC 2.0 project
  // with target_level=Level 1 returns the 17 L1 controls instead of all 110).
  // Backward-compatible: omit projectId to get the full catalog.
  getStandardWithRequirements: async (
    standardId: string,
    opts: { projectId?: number } = {}
  ): Promise<{
    standard: CustomStandard;
    requirements: CustomRequirement[];
    requirement_count: number;
  }> => {
    const qs = opts.projectId ? `?projectId=${opts.projectId}` : '';
    const response = await apiClient.get(`/v2/compliance/standards/${standardId}${qs}`);
    return response.data.data;
  },

  // Create a new custom standard
  createCustomStandard: async (standardData: {
    standard_id: string;
    standard_name: string;
    version: string;
    description?: string;
    jurisdiction?: string;
    applicable_sectors?: string[];
    requirements?: CustomRequirement[];
    created_by?: string;
  }): Promise<ImportStandardResult> => {
    const response = await apiClient.post('/v2/compliance/standards', standardData);
    return response.data.data;
  },

  // Add requirements to existing standard
  addRequirementsToStandard: async (
    standardId: string,
    requirements: CustomRequirement[]
  ): Promise<{ standard_id: string; requirements_added: number }> => {
    const response = await apiClient.post(
      `/v2/compliance/standards/${standardId}/requirements`,
      { requirements }
    );
    return response.data.data;
  },

  // Import custom standard from CSV or JSON
  importCustomStandard: async (
    format: 'csv' | 'json',
    standardInfo: {
      standard_id: string;
      standard_name: string;
      version: string;
      description?: string;
      jurisdiction?: string;
      applicable_sectors?: string[];
      created_by?: string;
    },
    fileContent: string
  ): Promise<ImportStandardResult> => {
    const response = await apiClient.post('/v2/compliance/standards/import', {
      format,
      data: fileContent,
      standard_info: standardInfo,
    });
    return response.data.data;
  },

  // Delete custom standard (only custom standards can be deleted)
  deleteCustomStandard: async (standardId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/v2/compliance/standards/${standardId}`);
    return response.data;
  },

  // Download CSV template for requirements import
  getRequirementsCSVTemplate: async (): Promise<string> => {
    const response = await apiClient.get('/v2/compliance/standards/template/csv');
    return response.data;
  },

  // Download JSON template for requirements import
  getRequirementsJSONTemplate: async (): Promise<object> => {
    const response = await apiClient.get('/v2/compliance/standards/template/json');
    return response.data;
  },

  // Cross-framework propagation: propagate all manual assessments to mapped standards
  propagateAll: async (projectId: number): Promise<{ propagated_count: number; standards_processed: number }> => {
    const response = await apiClient.post(`/v2/compliance/projects/${projectId}/propagate-all`);
    return response.data.data;
  },

  // Cross-framework coverage: get per-standard manual vs inherited counts
  getCrossFrameworkCoverage: async (projectId: number): Promise<import('../types/compliance').CrossFrameworkCoverage> => {
    const response = await apiClient.get(`/v2/compliance/projects/${projectId}/cross-framework-coverage`);
    return response.data.data;
  },
};

// ==================== DOCUMENT TEMPLATES API (PRISM Docs) ====================
// Template-based document generation

export interface DocumentTemplate {
  id: number;
  template_key: string;
  name: string;
  description: string;
  category: string;
  content_markdown?: string;
  variables_schema?: Record<string, any>;
  applicable_standards: string[];
  applicable_industries: string[] | null;
  version: string;
  is_system_template: boolean;
  created_at: string;
  updated_at: string;
}

export interface GeneratedDocument {
  id: number;
  project_id: number;
  template_id: number;
  title: string;
  description?: string;
  content_markdown: string;
  variable_values: Record<string, any>;
  status: 'draft' | 'final' | 'archived';
  last_exported_at?: string;
  last_export_format?: string;
  template_name?: string;
  template_key?: string;
  project_name?: string;
  created_at: string;
  updated_at: string;
}

export interface TemplatePreview {
  template_key: string;
  template_name: string;
  project_id: number;
  content_markdown: string;
  variables_used: Record<string, any>;
}

export interface DocumentPreview {
  title: string;
  markdown: string;
  created_at?: string;
}

// AI Enhancement Types
export interface SectionImprovement {
  section_name: string;
  original_excerpt: string;
  improved_excerpt: string;
  improvement_type: 'clarity' | 'completeness' | 'technical_accuracy' | 'compliance_language' | 'formatting';
  rationale: string;
}

export interface DocumentEnhancementResult {
  template_key: string;
  project_id: number;
  original_length: number;
  enhanced_content: string;
  improvements_made: string[];
  sections_improved: SectionImprovement[];
  quality_score: {
    before: number;
    after: number;
  };
  processing_time_ms: number;
}

export interface AIServiceStatus {
  available: boolean;
  provider: string | null;
  model?: string;
  message: string;
}

/**
 * Upload DISA STIG checklist (.ckl) files for a project.
 *
 * ⚠ Goes through `apiClient` deliberately. This upload used to be a bare `fetch` that built
 * its own header from `localStorage.getItem('token')` — a key only the SSO redirect branch of
 * Login.tsx ever writes, while every other sign-in path stores `prism_token`. The header went
 * out as `Bearer null` and the server answered 403, so the feature had never worked: prod's
 * `stig_imports` table held 0 rows on 2026-09-22. The interceptor is the ONE place that knows
 * where the session lives (sessionStorage OR localStorage) — do not hand-roll it again.
 *
 * ⚠ `Content-Type: multipart/form-data` MUST be passed explicitly. `apiClient` is created with
 * a default `Content-Type: application/json`, and axios only derives the multipart boundary when
 * the header is NOT already set — so an instance default silently wins, the body arrives
 * labelled as JSON, multer parses nothing, and the route answers 400 "No CKL files uploaded".
 * That is exactly what happened on the first attempt at this fix (2026-09-22): it traded a
 * broken auth path for a broken body. The other two uploads in this file (`/oscal/validate`,
 * contract-analysis) set it the same way — follow them, not the axios docs for a bare instance.
 */
export const importCklFiles = async (projectId: number, files: FileList | File[]) => {
  const formData = new FormData();
  Array.from(files).forEach((f) => formData.append('files', f));
  const response = await apiClient.post(`/ckl/projects/${projectId}/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const documentTemplatesAPI = {
  // Get all available templates
  getTemplates: async (): Promise<{ success: boolean; data: DocumentTemplate[] }> => {
    const response = await apiClient.get('/v2/templates');
    return response.data;
  },

  // Get a specific template by key
  getTemplate: async (templateKey: string): Promise<{ success: boolean; data: DocumentTemplate }> => {
    const response = await apiClient.get(`/v2/templates/${templateKey}`);
    return response.data;
  },

  // Preview populated template (returns markdown)
  previewTemplate: async (templateKey: string, projectId: number): Promise<{ success: boolean; data: TemplatePreview }> => {
    const response = await apiClient.post(`/v2/templates/${templateKey}/preview/${projectId}`);
    return response.data;
  },

  // Generate and save document from template
  generateDocument: async (
    templateKey: string,
    projectId: number,
    title?: string
  ): Promise<{ success: boolean; data: GeneratedDocument; message: string }> => {
    const response = await apiClient.post(`/v2/templates/${templateKey}/generate/${projectId}`, { title });
    return response.data;
  },

  // Export template as DOCX (direct download)
  exportTemplate: async (templateKey: string, projectId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v2/templates/${templateKey}/export/${projectId}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Get all generated documents for a project
  getGeneratedDocuments: async (projectId: number): Promise<{ success: boolean; data: GeneratedDocument[] }> => {
    const response = await apiClient.get(`/v2/templates/projects/${projectId}/documents`);
    return response.data;
  },

  // Export saved document as DOCX
  exportSavedDocument: async (documentId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v2/templates/documents/${documentId}/export`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Export template as PDF (direct download)
  exportTemplatePdf: async (templateKey: string, projectId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v2/templates/${templateKey}/export-pdf/${projectId}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Export saved document as PDF
  exportSavedDocumentPdf: async (documentId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v2/templates/documents/${documentId}/export-pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Export markdown content directly as DOCX (for enhanced documents)
  exportContent: async (contentMarkdown: string, title: string, projectName?: string): Promise<Blob> => {
    const response = await apiClient.post('/v2/templates/export-content', {
      content_markdown: contentMarkdown,
      title,
      project_name: projectName,
    }, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Export markdown content directly as PDF (for enhanced documents)
  exportContentPdf: async (contentMarkdown: string, title: string, projectName?: string): Promise<Blob> => {
    const response = await apiClient.post('/v2/templates/export-content-pdf', {
      content_markdown: contentMarkdown,
      title,
      project_name: projectName,
    }, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Check AI service availability
  getAIStatus: async (): Promise<{ success: boolean; data: AIServiceStatus }> => {
    const response = await apiClient.get('/v2/templates/ai-status');
    return response.data;
  },

  // Enhance document with AI (longer timeout for chunked LLM processing)
  enhanceDocument: async (
    templateKey: string,
    projectId: number,
    contentMarkdown: string
  ): Promise<{ success: boolean; data: DocumentEnhancementResult }> => {
    const response = await apiClient.post(
      `/v2/templates/${templateKey}/enhance/${projectId}`,
      { content_markdown: contentMarkdown },
      { timeout: 600000 } // 5 minutes for chunked AI processing (multiple sections)
    );
    return response.data;
  },

  // Improve a specific section with AI
  improveSection: async (
    sectionContent: string,
    sectionName: string,
    improvementType: 'clarity' | 'completeness' | 'technical_accuracy' | 'compliance_language',
    context?: {
      documentType?: string;
      projectName?: string;
      industry?: string;
    }
  ): Promise<{
    success: boolean;
    data: {
      section_name: string;
      improvement_type: string;
      original_content: string;
      improved_content: string;
      changes_made: string[];
      rationale: string;
    };
  }> => {
    const response = await apiClient.post(
      '/v2/templates/improve-section',
      {
        section_content: sectionContent,
        section_name: sectionName,
        improvement_type: improvementType,
        document_type: context?.documentType,
        project_name: context?.projectName,
        industry: context?.industry,
      },
      { timeout: 600000 } // 5 minutes for section improvement
    );
    return response.data;
  },

  // Get procurement templates (system + user forks) for a base key
  getProcurementTemplates: async (baseKey: string): Promise<{ success: boolean; data: DocumentTemplate[] }> => {
    const response = await apiClient.get(`/v2/templates/procurement/${baseKey}`);
    return response.data;
  },

  // Fork a system template into a user-editable copy
  forkTemplate: async (templateKey: string, name?: string): Promise<{ success: boolean; data: DocumentTemplate }> => {
    const response = await apiClient.post(`/v2/templates/${templateKey}/fork`, { name });
    return response.data;
  },

  // Update a custom (non-system) template
  updateCustomTemplate: async (
    templateId: number,
    updates: { name?: string; description?: string; content_markdown?: string }
  ): Promise<{ success: boolean; data: DocumentTemplate }> => {
    const response = await apiClient.put(`/v2/templates/${templateId}`, updates);
    return response.data;
  },

  // Delete a custom template (soft-delete)
  deleteCustomTemplate: async (templateId: number): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/v2/templates/${templateId}`);
    return response.data;
  },

  // Get all generated documents across projects (global Documents page)
  getAllDocuments: async (): Promise<{ success: boolean; data: GeneratedDocument[] }> => {
    const response = await apiClient.get('/v2/templates/all-documents');
    return response.data;
  },
};

// ============================================================
// Compliance Packages API (Phase 8.2)
// Assessment submission bundles for CMMC L2, SOC 2, NIST 800-171
// ============================================================

export interface PackageType {
  id: number;
  package_key: string;
  name: string;
  description: string;
  standard_id: number | null;
  version: string;
  icon: string;
  color: string;
  estimated_documents: number;
  estimated_policies: number;
  estimated_procedures: number;
  is_active: boolean;
  has_assessment_prep?: boolean;
  assessment_prep_control_count?: number;
}

// Document source types from DocumentResolutionService
export type DocumentSource =
  | 'ato_journey'
  | 'template'
  | 'system_model'
  | 'evidence'
  | 'generated'
  | 'grc_import'
  | 'manual';

export interface DocumentSourceDetails {
  // For evidence source
  evidence_id?: number;
  evidence_title?: string;
  evidence_file_name?: string;
  match_confidence?: number;

  // For system_model source
  export_type?: 'network_diagram' | 'data_flow_diagram' | 'asset_inventory' | 'system_boundary';
  asset_count?: number;
  data_flow_count?: number;

  // For template source
  template_name?: string;

  // For generated/template with file
  generated?: boolean;
  file_name?: string;
  linked?: boolean;
  exported?: boolean;

  // For generated source
  generated_doc_id?: number;
  generated_at?: string;

  // For grc_import source
  grc_platform?: string;
  grc_document_id?: string;

  // For ato_journey source
  ato_step_number?: number;
  ato_artifact_type?: string;
  ato_filename?: string;
  ato_generated_at?: string;
  ato_notes?: string;
}

/** ATO Journey state surfaced alongside SSP-row resolution. Drives the
 *  smart-CTA UX (three states: ATO done / in-progress / not-started)
 *  without a second API round-trip. Populated only for SSP rows. */
export interface AtoJourneyState {
  started: boolean;
  current_step: number; // 1-6, or 0 if not started
  completed_steps: number[];
  ssp_artifact_registered: boolean;
}

export interface RequiredDocument {
  id: number;
  package_type_id: number;
  document_type: 'core' | 'policy' | 'procedure' | 'evidence' | 'supporting';
  template_key: string | null;
  name: string;
  description: string | null;
  is_required: boolean;
  sort_order: number;
  folder_path: string;
  file_name_template: string | null;
  control_families: string[] | null;

  // Resolution info (from DocumentResolutionService)
  source?: DocumentSource;
  source_details?: DocumentSourceDetails;
  can_generate?: boolean;
  can_export?: boolean;
  can_link?: boolean;
  is_satisfied?: boolean;

  // ATO Journey state (populated for SSP rows only)
  ato_journey_state?: AtoJourneyState;
}

export interface PackageReadiness {
  overall_score: number;
  documents: {
    total: number;
    ready: number;
    missing: { id: number; name: string; template_key: string | null }[];
    score: number;
  };
  policies: {
    total: number;
    ready: number;
    missing: { id: number; name: string; template_key: string | null }[];
    score: number;
  };
  procedures: {
    total: number;
    ready: number;
    missing: { id: number; name: string; template_key: string | null }[];
    score: number;
  };
  evidence: {
    total_required: number;
    mapped: number;
    coverage_percent: number;
    score: number;
  };
  gaps: {
    open_poam_items: number;
    critical_gaps: number;
    // False for CMMC Level 1 â€” POA&Ms are N/A (pass/fail self-assessment).
    poam_applicable?: boolean;
  };
  blockers: string[];
  warnings: string[];
}

export interface ProjectPackage {
  id: number;
  project_id: number;
  package_type_id: number;
  status: 'draft' | 'calculating' | 'generating' | 'ready' | 'exported' | 'error';
  readiness_score: number;
  readiness_details: PackageReadiness | null;
  generation_started_at: string | null;
  generation_completed_at: string | null;
  exported_at: string | null;
  export_file_path: string | null;
  package_key?: string;
  type_name?: string;
  type_description?: string;
  icon?: string;
  color?: string;
  estimated_documents?: number;
  estimated_policies?: number;
  estimated_procedures?: number;
  created_at?: string;
  updated_at?: string;
}

// Generated document item result
export interface GeneratedPackageItem {
  document_id: number;
  document_name: string;
  template_key: string | null;
  status: 'generated' | 'skipped' | 'failed' | 'exported';
  source: 'template' | 'system_model' | 'evidence' | 'manual';
  file_name?: string;
  error_message?: string;
  needs_review: boolean;
  auto_approved: boolean;
}

// Review queue item
export interface ReviewQueueItem {
  id: number;
  package_id: number;
  document_id: number;
  document_name: string;
  document_type: string;
  template_key: string | null;
  status: string;
  source_type: string;
  file_name: string | null;
  generated_at: string | null;
  review_status: 'pending' | 'generated' | 'approved' | 'rejected' | 'skipped';
  reviewed_at: string | null;
  reviewed_by: number | null;
  review_notes: string | null;
  auto_approved: boolean;
}

export const compliancePackagesAPI = {
  // Get all available package types
  getPackageTypes: async (): Promise<PackageType[]> => {
    const response = await apiClient.get('/v2/packages/types');
    return response.data.data;
  },

  // Get specific package type with required documents
  getPackageType: async (packageKey: string): Promise<{
    packageType: PackageType;
    required_documents: {
      core: RequiredDocument[];
      policies: RequiredDocument[];
      procedures: RequiredDocument[];
      evidence: RequiredDocument[];
      supporting: RequiredDocument[];
    };
    total_documents: number;
  }> => {
    const response = await apiClient.get(`/v2/packages/types/${packageKey}`);
    return response.data.data;
  },

  // Get all packages for a project
  getProjectPackages: async (projectId: number): Promise<{
    packages: ProjectPackage[];
    available_types: PackageType[];
  }> => {
    const response = await apiClient.get(`/v2/packages/projects/${projectId}/packages`);
    return response.data.data;
  },

  // Get or create a specific package for a project
  getOrCreatePackage: async (projectId: number, typeKey: string): Promise<{
    package: ProjectPackage;
    package_type: PackageType;
    required_documents: RequiredDocument[];
  }> => {
    const response = await apiClient.get(`/v2/packages/projects/${projectId}/packages/${typeKey}`);
    return response.data.data;
  },

  // Calculate readiness score for a package
  calculateReadiness: async (projectId: number, typeKey: string): Promise<PackageReadiness> => {
    const response = await apiClient.post(`/v2/packages/projects/${projectId}/packages/${typeKey}/calculate-readiness`);
    return response.data.data;
  },

  // Generate package documents with mode support
  generatePackage: async (
    projectId: number,
    typeKey: string,
    options?: {
      mode?: 'guided' | 'quick';
      regenerate_existing?: boolean;
      document_ids?: number[];
    }
  ): Promise<{
    message: string;
    package_id: number;
    mode: 'guided' | 'quick';
    items: GeneratedPackageItem[];
    summary: {
      total: number;
      generated: number;
      skipped: number;
      failed: number;
      needs_review: number;
    };
  }> => {
    const response = await apiClient.post(`/v2/packages/projects/${projectId}/packages/${typeKey}/generate`, {
      mode: options?.mode || 'guided',
      regenerate_existing: options?.regenerate_existing || false,
      document_ids: options?.document_ids,
    });
    return response.data.data;
  },

  // Get review queue for a package
  getReviewQueue: async (projectId: number, typeKey: string): Promise<{
    package_id: number;
    status: string;
    mode: string;
    progress: {
      total: number;
      generated: number;
      reviewed: number;
      approved: number;
    };
    can_finalize: boolean;
    items: ReviewQueueItem[];
    summary: {
      total: number;
      pending_review: number;
      approved: number;
      rejected: number;
    };
  }> => {
    const response = await apiClient.get(`/v2/packages/projects/${projectId}/packages/${typeKey}/review-queue`);
    return response.data.data;
  },

  // Review (approve/reject) a single document
  reviewItem: async (itemId: number, action: 'approve' | 'reject', notes?: string): Promise<{ message: string }> => {
    const response = await apiClient.put(`/v2/packages/items/${itemId}/review`, {
      action,
      notes,
    });
    return response.data;
  },

  // Bulk review multiple documents
  bulkReviewItems: async (
    itemIds: number[],
    action: 'approve' | 'reject',
    notes?: string
  ): Promise<{
    processed: number;
    succeeded: number;
    failed: number;
    unapprovable?: Array<{ item_id: number; reason: string }>;
    other_failures?: Array<{ item_id: number; error: string }>;
  }> => {
    const response = await apiClient.post('/v2/packages/items/bulk-review', {
      item_ids: itemIds,
      action,
      notes,
    });
    return response.data.data;
  },

  // Get package generation status
  getPackageStatus: async (projectId: number, typeKey: string): Promise<{
    package_id: number;
    status: string;
    mode: string;
    progress: {
      total: number;
      generated: number;
      reviewed: number;
      approved: number;
    };
    can_finalize: boolean;
  }> => {
    const response = await apiClient.get(`/v2/packages/projects/${projectId}/packages/${typeKey}/status`);
    return response.data.data;
  },

  // Get package items with resolution status
  getPackageItems: async (projectId: number, typeKey: string): Promise<{
    core: RequiredDocument[];
    policies: RequiredDocument[];
    procedures: RequiredDocument[];
    supporting: RequiredDocument[];
    stats: {
      total: number;
      satisfied: number;
      from_template: number;
      from_system_model: number;
      from_evidence: number;
      from_generated: number;
      manual_required: number;
    };
  }> => {
    const response = await apiClient.get(`/v2/packages/projects/${projectId}/packages/${typeKey}/items`);
    return response.data.data;
  },

  // Export endpoints - generate artifacts from system model
  exportNetworkDiagram: async (projectId: number): Promise<any> => {
    const response = await apiClient.get(`/v2/packages/projects/${projectId}/exports/network-diagram`);
    return response.data.data;
  },

  exportDataFlowDiagram: async (projectId: number): Promise<any> => {
    const response = await apiClient.get(`/v2/packages/projects/${projectId}/exports/data-flow-diagram`);
    return response.data.data;
  },

  exportAssetInventory: async (projectId: number): Promise<any> => {
    const response = await apiClient.get(`/v2/packages/projects/${projectId}/exports/asset-inventory`);
    return response.data.data;
  },

  exportSystemBoundary: async (projectId: number): Promise<any> => {
    const response = await apiClient.get(`/v2/packages/projects/${projectId}/exports/system-boundary`);
    return response.data.data;
  },

  // Export package as ZIP file - triggers download
  exportPackageAsZip: async (projectId: number, typeKey: string): Promise<void> => {
    const token = sessionStorage.getItem('prism_token') || localStorage.getItem('prism_token');
    const url = `${API_BASE_URL}/v2/packages/projects/${projectId}/packages/${typeKey}/export`;

    // Use fetch to get the blob with auth header
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to export package');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'compliance_package.zip';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create blob and trigger download
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  // Link evidence document to a package required document
  linkEvidence: async (
    projectId: number,
    typeKey: string,
    requiredDocumentId: number,
    evidenceId: number
  ): Promise<{ success: boolean; item_id: number }> => {
    const response = await apiClient.post(
      `/v2/packages/projects/${projectId}/packages/${typeKey}/link-evidence`,
      {
        required_document_id: requiredDocumentId,
        evidence_id: evidenceId,
      }
    );
    return response.data.data;
  },

  // Remove an evidence link from a package required document
  unlinkEvidence: async (
    projectId: number,
    typeKey: string,
    requiredDocumentId: number
  ): Promise<{ success: boolean; removed: boolean; evidence_id: number | null }> => {
    const response = await apiClient.delete(
      `/v2/packages/projects/${projectId}/packages/${typeKey}/items/${requiredDocumentId}/link`
    );
    return response.data.data;
  },

  // Export system model document and mark as satisfied
  exportSystemDoc: async (
    projectId: number,
    typeKey: string,
    requiredDocumentId: number,
    exportType: 'network_diagram' | 'data_flow_diagram' | 'asset_inventory' | 'system_boundary'
  ): Promise<{ item_id: number; file_name: string; export_data: any }> => {
    const response = await apiClient.post(
      `/v2/packages/projects/${projectId}/packages/${typeKey}/export-system-doc`,
      {
        required_document_id: requiredDocumentId,
        export_type: exportType,
      }
    );
    return response.data.data;
  },

  // Get document content for a package item
  getItemContent: async (itemId: number): Promise<{ content: string; file_name: string }> => {
    const response = await apiClient.get(`/v2/packages/items/${itemId}/content`);
    return response.data.data;
  },

  // Enhance a package item document with AI
  enhanceItem: async (itemId: number): Promise<{
    enhanced_content: string;
    improvements_made: string[];
    quality_score: { before: number; after: number } | null;
  }> => {
    const response = await apiClient.post(`/v2/packages/items/${itemId}/enhance`, {}, {
      timeout: 600000, // 5 minutes for AI processing
    });
    return response.data.data;
  },

  // ============================================
  // Assessment Prep APIs
  // ============================================

  // Get all assessment prep controls with attestations
  getAssessmentPrepControls: async (
    projectId: number,
    typeKey: string
  ): Promise<{
    controls: AssessmentPrepControl[];
    by_family: Record<string, AssessmentPrepControl[]>;
    total_controls: number;
    package_id: number | null;
  }> => {
    const response = await apiClient.get(
      `/v2/packages/projects/${projectId}/packages/${typeKey}/assessment-prep`
    );
    return response.data.data;
  },

  // Get assessment prep summary
  getAssessmentPrepSummary: async (
    projectId: number,
    typeKey: string
  ): Promise<AssessmentPrepSummary> => {
    const response = await apiClient.get(
      `/v2/packages/projects/${projectId}/packages/${typeKey}/assessment-prep/summary`
    );
    return response.data.data;
  },

  // Auto-attest controls based on evidence library
  autoAttestControls: async (
    projectId: number,
    typeKey: string
  ): Promise<{
    message: string;
    controls_updated: number;
    total_controls: number;
    evidence_count: number;
    attestations_set: {
      documentation_ready: number;
      implementation_verified: number;
      evidence_collected: number;
    };
  }> => {
    const response = await apiClient.post(
      `/v2/packages/projects/${projectId}/packages/${typeKey}/assessment-prep/auto-attest`
    );
    return response.data.data;
  },

  // Generate POA&M items from gaps based on Assessment Prep attestations
  generatePOAMFromGaps: async (
    projectId: number,
    typeKey: string
  ): Promise<{
    message: string;
    created: number;
    updated: number;
    skipped: number;
    total_items: number;
    items: Array<{
      control_id: string;
      control_name: string;
      action: 'created' | 'updated' | 'skipped';
      priority: string;
      reason?: string;
    }>;
  }> => {
    const response = await apiClient.post(
      `/v2/packages/projects/${projectId}/packages/${typeKey}/assessment-prep/generate-poam`
    );
    return response.data.data;
  },

  // Update attestation for a single control
  updateAssessmentPrep: async (
    projectId: number,
    typeKey: string,
    controlId: string,
    attestation: {
      documentation_ready?: boolean;
      implementation_verified?: boolean;
      evidence_collected?: boolean;
      interview_prepared?: boolean;
      evidence_items_checked?: string[];
      notes?: string;
      confidence_score?: number;
    }
  ): Promise<any> => {
    const response = await apiClient.put(
      `/v2/packages/projects/${projectId}/packages/${typeKey}/assessment-prep/${encodeURIComponent(controlId)}`,
      attestation
    );
    return response.data.data;
  },

  // Generate AI interview questions for a control
  generateInterviewQuestions: async (
    projectId: number,
    typeKey: string,
    controlId: string
  ): Promise<{
    questions: Array<{
      question: string;
      guidance: string;
      expected_evidence: string[];
      follow_ups: string[];
    }>;
    tokens_used: number;
    model: string;
  }> => {
    const response = await apiClient.post(
      `/v2/packages/projects/${projectId}/packages/${typeKey}/assessment-prep/${encodeURIComponent(controlId)}/generate-questions`
    );
    return response.data.data;
  },

  // Bulk update attestations
  bulkUpdateAssessmentPrep: async (
    projectId: number,
    typeKey: string,
    updates: Array<{
      control_id: string;
      documentation_ready?: boolean;
      implementation_verified?: boolean;
      evidence_collected?: boolean;
      interview_prepared?: boolean;
      evidence_items_checked?: string[];
      notes?: string;
    }>
  ): Promise<{ updated: number; control_ids: string[] }> => {
    const response = await apiClient.post(
      `/v2/packages/projects/${projectId}/packages/${typeKey}/assessment-prep/bulk-update`,
      { updates }
    );
    return response.data.data;
  },
};

// Assessment Prep Types
export interface AssessmentPrepControl {
  id: number;
  control_id: string;
  control_name: string;
  control_family: string;
  control_family_name: string;
  evidence_checklist: string[];
  interview_questions: string[];
  verification_steps: string[];
  attestation: {
    documentation_ready: boolean;
    implementation_verified: boolean;
    evidence_collected: boolean;
    interview_prepared: boolean;
    evidence_items_checked: string[];
    notes: string | null;
    confidence_score: number | null;
    ai_generated_questions: Array<{
      question: string;
      guidance: string;
      expected_evidence: string[];
      follow_ups: string[];
    }> | null;
  } | null;
}

export interface AssessmentPrepSummary {
  total_controls: number;
  controls_met: number;  // Pre-interview: docs + evidence + implementation
  controls_ready: number;  // Fully complete including interview
  controls_in_progress: number;
  controls_not_started: number;
  readiness_percent: number;
  confidence_level: 'high' | 'medium' | 'low';
  by_category: {
    documentation_ready: number;
    implementation_verified: number;
    evidence_collected: number;
    interview_prepared: number;
  };
  by_family: Record<string, {
    family_name: string;
    total: number;
    met: number;
    ready: number;
    in_progress: number;
  }>;
  overall_confidence: number;
}

// Entitlement API
export const entitlementAPI = {
  getMe: async () => {
    const response = await apiClient.get('/v2/entitlements/me');
    return response.data.data;
  },

  upgrade: async (targetTier: string, paymentToken?: string) => {
    const response = await apiClient.post('/v2/entitlements/upgrade', {
      target_tier: targetTier,
      payment_token: paymentToken,
    });
    return response.data;
  },

  // updateOrg REMOVED 2026-09-11 with the route it called. `PUT /v2/entitlements/:orgId`
  // let any org admin set any organization's tier; entitlement changes go through
  // `adminAPI.updateOrgEntitlements`, which hits the superadmin-gated /admin route.
};

// Billing API â€” self-serve standard add-on (Stripe checkout)
export const billingAPI = {
  status: async (): Promise<{ enabled: boolean }> => {
    const res = await apiClient.get('/v2/billing/status');
    return { enabled: !!res.data?.enabled };
  },
  checkout: async (standardId: string, successUrl: string, cancelUrl: string): Promise<{ url: string }> => {
    const res = await apiClient.post('/v2/billing/checkout', {
      standard_id: standardId, success_url: successUrl, cancel_url: cancelUrl,
    });
    const url = res.data?.url;
    if (!url) throw new Error('Checkout response missing url');
    return { url };
  },
};

// Framework Requests API — telemetry: frameworks a customer looked for and did not find
export const frameworkRequestsAPI = {
  create: async (
    projectId: number,
    body: { requested_name: string; note: string | null; sector_filter: string | null }
  ): Promise<{ id: number }> => {
    const res = await apiClient.post(`/v2/projects/${projectId}/framework-requests`, body);
    return { id: res.data?.id };
  },
};

// Iris Onboarding API
export const irisOnboardingAPI = {
  getStatus: async () => {
    const response = await apiClient.get('/v2/iris/onboarding/status');
    return response.data.data;
  },

  getQuestions: async () => {
    const response = await apiClient.get('/v2/iris/onboarding/questions');
    return response.data.data;
  },

  submitInterview: async (answers: Record<string, string>) => {
    const response = await apiClient.post('/v2/iris/onboarding/interview', { answers });
    return response.data.data;
  },

  getRecommendation: async () => {
    const response = await apiClient.get('/v2/iris/onboarding/recommendation');
    return response.data.data;
  },
};

// Trust Center API
export interface TrustCenterConfig {
  enabled: boolean;
  slug: string;
  headline: string;
  description: string;
  logo_url: string;
  project_ids: number[];
  show_standards: boolean;
  show_evidence: boolean;
  show_security_tools: boolean;
  show_packages: boolean;
  contact_email: string;
  updated_at: string | null;
}

export interface TrustCenterProject {
  name: string;
  industry_sector: string | null;
  status: string;
  asset_count: number;
  technique_count: number;
  /** Omitted by the API when the org's "Evidence Vault" section is switched off. */
  evidence_count?: number;
  /**
   * ONLY standards with a recorded, current certification (Pat, 2026-10-29). Never a PRISM
   * score, status or percentage; uncertified and lapsed standards are absent.
   */
  standards: TrustCenterCertifiedStandard[];
}

export interface TrustCenterCertifiedStandard {
  standard_id: string;
  name: string;
  certifying_body: string | null;
  certified_at: string;
  certified_until: string | null;
  certification_reference: string | null;
}

/** A project standard as the settings page sees it: certification fields may all be null. */
export interface TrustCenterStandardCertification {
  standard_id: string;
  name: string;
  certified_at: string | null;
  certified_until: string | null;
  certifying_body: string | null;
  certification_reference: string | null;
}

export interface TrustCenterPosture {
  organization: {
    name: string;
    headline: string;
    description: string;
    logo_url: string | null;
    contact_email: string | null;
    updated_at: string | null;
  };
  sections: {
    show_standards: boolean;
    show_evidence: boolean;
    show_security_tools: boolean;
  };
  projects: TrustCenterProject[];
}

export const trustCenterAPI = {
  // Public â€” uses raw fetch() to avoid 401â†’login redirect interceptor
  getPublicPosture: async (slug: string): Promise<TrustCenterPosture> => {
    const baseUrl = import.meta.env.VITE_API_URL || '/api';
    const response = await fetch(`${baseUrl.replace(/\/api$/, '')}/api/trust-center/${slug}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Not found' }));
      throw new Error(err.error || `HTTP ${response.status}`);
    }
    const json = await response.json();
    return json.data;
  },

  // Authenticated â€” config management
  getConfig: async (): Promise<TrustCenterConfig> => {
    const response = await apiClient.get('/v2/trust-center/config');
    return response.data.data;
  },

  updateConfig: async (config: Partial<TrustCenterConfig>): Promise<TrustCenterConfig> => {
    const response = await apiClient.put('/v2/trust-center/config', config);
    return response.data.data;
  },

  getCertifications: async (projectId: number): Promise<TrustCenterStandardCertification[]> => {
    const response = await apiClient.get(`/v2/trust-center/projects/${projectId}/certifications`);
    return response.data.data;
  },

  /** Org admins only (the server checks the database role). */
  recordCertification: async (
    projectId: number,
    standardId: string,
    fields: {
      certified_at: string;
      certified_until: string | null;
      certifying_body: string;
      certification_reference: string | null;
    }
  ) => {
    const response = await apiClient.put(
      `/v2/trust-center/projects/${projectId}/standards/${encodeURIComponent(standardId)}/certification`,
      fields
    );
    return response.data.data;
  },

  /** Org admins only. */
  clearCertification: async (projectId: number, standardId: string) => {
    const response = await apiClient.delete(
      `/v2/trust-center/projects/${projectId}/standards/${encodeURIComponent(standardId)}/certification`
    );
    return response.data.data;
  },
};

// ============================================================
// Evidence Collection Integrations API
// ============================================================

export interface ConnectorProviderField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'select';
  required: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
}

export interface ConnectorProvider {
  provider: string;
  displayName: string;
  credentialFields: ConnectorProviderField[];
}

export interface ConnectorInfo {
  connectorType: string;
  displayName: string;
  description: string;
  icon: string;
  providers: ConnectorProvider[];
}

export interface IntegrationCredential {
  id: number;
  organization_id: number;
  connector_type: string;
  provider: string;
  display_name: string;
  is_active: boolean;
  last_test_at: string | null;
  last_test_status: string | null;
  last_test_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface SyncHistoryRecord {
  id: number;
  credential_id: number;
  project_id: number;
  sync_type: string;
  status: string;
  items_found: number;
  items_created: number;
  items_updated: number;
  items_skipped: number;
  items_failed: number;
  error_log: string[] | null;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  connector_type: string;
  provider: string;
  credential_name: string;
  project_name: string;
  /** The credential's org (absent from an older backend). */
  organization_id?: number;
}

export interface IntegrationOrganization {
  id: number;
  name: string;
}

export interface SyncResultData {
  syncId: number;
  status: string;
  itemsFound: number;
  itemsCreated: number;
  itemsUpdated: number;
  itemsSkipped: number;
  itemsFailed: number;
  errors: string[];
  durationMs: number;
}

export const integrationsAPI = {
  getConnectors: async (): Promise<{ success: boolean; data: ConnectorInfo[] }> => {
    const response = await apiClient.get('/v2/integrations/connectors');
    return response.data;
  },

  getCredentials: async (): Promise<{ success: boolean; data: IntegrationCredential[] }> => {
    const response = await apiClient.get('/v2/integrations/credentials');
    return response.data;
  },

  /** Orgs whose credentials the caller may manage: own org first, then a vCISO practice's clients. */
  getOrganizations: async (): Promise<{ success: boolean; data: IntegrationOrganization[] }> => {
    const response = await apiClient.get('/v2/integrations/organizations');
    return response.data;
  },

  saveCredential: async (data: {
    connectorType: string;
    provider: string;
    displayName: string;
    credentials: Record<string, any>;
    /** Store the credential for this org (must be one the caller manages). Omit for own org. */
    organizationId?: number;
  }): Promise<{ success: boolean; data: IntegrationCredential }> => {
    const response = await apiClient.post('/v2/integrations/credentials', data);
    return response.data;
  },

  deleteCredential: async (id: number): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/v2/integrations/credentials/${id}`);
    return response.data;
  },

  testConnection: async (id: number): Promise<{ success: boolean; data: { success: boolean; message: string } }> => {
    const response = await apiClient.post(`/v2/integrations/credentials/${id}/test`);
    return response.data;
  },

  triggerSync: async (credentialId: number, projectId: number): Promise<{ success: boolean; data: SyncResultData }> => {
    const response = await apiClient.post('/v2/integrations/sync', { credentialId, projectId }, {
      timeout: 120000, // 2 min for sync operations
    });
    return response.data;
  },

  getSyncHistory: async (params?: {
    projectId?: number;
    credentialId?: number;
    limit?: number;
  }): Promise<{ success: boolean; data: SyncHistoryRecord[] }> => {
    const response = await apiClient.get('/v2/integrations/sync-history', { params });
    return response.data;
  },

  getSyncDetail: async (id: number): Promise<{ success: boolean; data: SyncHistoryRecord }> => {
    const response = await apiClient.get(`/v2/integrations/sync-history/${id}`);
    return response.data;
  },
};

// Corporate Project Rollup API (Phase 8C)
export const corporateRollupAPI = {
  getRollup: async () => {
    const response = await apiClient.get('/v2/organization/rollup');
    return response.data.data;
  },
  getCrossProjectGaps: async () => {
    const response = await apiClient.get('/v2/organization/cross-project-gaps');
    return response.data.data;
  },
  exportExcel: async (): Promise<Blob> => {
    const response = await apiClient.get('/v2/organization/rollup/export', {
      responseType: 'blob',
    });
    return response.data;
  },
  syncToFoundations: async () => {
    const response = await apiClient.post('/v2/organization/rollup/sync-to-foundations');
    return response.data;
  },
};

// MSSP Portfolio API
export const msspPortfolioAPI = {
  getManagedClients: async () => {
    const response = await apiClient.get('/v2/mssp/managed-clients');
    return response.data.data;
  },
  getPortfolioRollup: async () => {
    const response = await apiClient.get('/v2/mssp/portfolio-rollup');
    return response.data.data;
  },
  getCrossClientGaps: async () => {
    const response = await apiClient.get('/v2/mssp/cross-client-gaps');
    return response.data.data;
  },
  exportPortfolio: async (): Promise<Blob> => {
    const response = await apiClient.get('/v2/mssp/portfolio-rollup/export', {
      responseType: 'blob',
    });
    return response.data;
  },
  addManagedClient: async (data: { name: string; industry?: string; size?: string; initial_project_name?: string }) => {
    const response = await apiClient.post('/v2/mssp/managed-clients', data);
    return response.data.data;
  },
  scheduleAssessment: async (data: {
    client_organization_id: number;
    framework_standard_id?: string;
    assessor_user_id?: number;
    target_date: string;
    notes?: string;
  }) => {
    const response = await apiClient.post('/v2/mssp/assessments/schedule', data);
    return response.data.data;
  },
  getScheduledAssessments: async () => {
    const response = await apiClient.get('/v2/mssp/assessments/schedule');
    return response.data.data;
  },
};

// Contract/RFP Analysis API
export const contractAnalysisAPI = {
  upload: async (projectId: number, file: File, documentType: string = 'contract') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    const response = await apiClient.post(
      `/v2/projects/${projectId}/contract-analysis/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  triggerExtraction: async (projectId: number, analysisId: number) => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}/extract`
    );
    return response.data;
  },

  list: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/contract-analysis`);
    return response.data;
  },

  get: async (projectId: number, analysisId: number) => {
    const response = await apiClient.get(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}`
    );
    return response.data;
  },

  updateRequirement: async (projectId: number, analysisId: number, reqId: number, updates: any) => {
    const response = await apiClient.put(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}/requirements/${reqId}`,
      updates
    );
    return response.data;
  },

  addRequirement: async (projectId: number, analysisId: number, data: any) => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}/requirements`,
      data
    );
    return response.data;
  },

  deleteRequirement: async (projectId: number, analysisId: number, reqId: number) => {
    const response = await apiClient.delete(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}/requirements/${reqId}`
    );
    return response.data;
  },

  completeReview: async (projectId: number, analysisId: number) => {
    const response = await apiClient.put(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}/review-complete`
    );
    return response.data;
  },

  deleteAnalysis: async (projectId: number, analysisId: number) => {
    const response = await apiClient.delete(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}`
    );
    return response.data;
  },

  triggerMapping: async (projectId: number, analysisId: number) => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}/map`
    );
    return response.data;
  },

  getMappings: async (projectId: number, analysisId: number) => {
    const response = await apiClient.get(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}/mappings`
    );
    return response.data;
  },

  getGapReport: async (projectId: number, analysisId: number) => {
    const response = await apiClient.get(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}/gap-report`
    );
    return response.data;
  },

  exportPDF: async (projectId: number, analysisId: number): Promise<Blob> => {
    const response = await apiClient.get(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}/export/pdf`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  exportExcel: async (projectId: number, analysisId: number): Promise<Blob> => {
    const response = await apiClient.get(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}/export/excel`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  importAsStandard: async (projectId: number, analysisId: number, data: { standard_id: string; standard_name: string; version?: string; description?: string }) => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/contract-analysis/${analysisId}/import-as-standard`,
      data
    );
    return response.data;
  },
};

// â”€â”€ Continuous Monitoring API (Track A â€” Drift Detection) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const monitoringAPI = {
  // Baselines
  captureBaseline: async (projectId: number, standardId: string, label?: string) => {
    const response = await apiClient.post(`/v2/monitoring/projects/${projectId}/baselines`, {
      standard_id: standardId,
      label,
    });
    return response.data;
  },

  listBaselines: async (projectId: number, standardId?: string) => {
    const params = standardId ? { standard_id: standardId } : {};
    const response = await apiClient.get(`/v2/monitoring/projects/${projectId}/baselines`, { params });
    return response.data;
  },

  getBaseline: async (projectId: number, baselineId: number) => {
    const response = await apiClient.get(`/v2/monitoring/projects/${projectId}/baselines/${baselineId}`);
    return response.data;
  },

  // Drift events
  getUnacknowledgedDrift: async (projectId: number, standardId?: string) => {
    const params = standardId ? { standard_id: standardId } : {};
    const response = await apiClient.get(`/v2/monitoring/projects/${projectId}/drift`, { params });
    return response.data;
  },

  getDriftHistory: async (projectId: number, limit?: number, offset?: number) => {
    const response = await apiClient.get(`/v2/monitoring/projects/${projectId}/drift/history`, {
      params: { limit, offset },
    });
    return response.data;
  },

  getDriftSummary: async (projectId: number, standardId: string) => {
    const response = await apiClient.get(`/v2/monitoring/projects/${projectId}/drift/summary`, {
      params: { standard_id: standardId },
    });
    return response.data;
  },

  acknowledgeDrift: async (projectId: number, eventId: number, notes?: string) => {
    const response = await apiClient.post(
      `/v2/monitoring/projects/${projectId}/drift/${eventId}/acknowledge`,
      { notes }
    );
    return response.data;
  },

  bulkAcknowledgeDrift: async (projectId: number, eventIds: number[], notes?: string) => {
    const response = await apiClient.post(
      `/v2/monitoring/projects/${projectId}/drift/acknowledge-bulk`,
      { event_ids: eventIds, notes }
    );
    return response.data;
  },

  checkNow: async (projectId: number, standardId: string) => {
    const response = await apiClient.post(`/v2/monitoring/projects/${projectId}/drift/check-now`, {
      standard_id: standardId,
    });
    return response.data;
  },
};

// â”€â”€ SPRS Score Tracker API â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export interface EvidencePostureStandard {
  standardId: string;
  grade: number; // 0..1 â€” depth Ã— breadth (the headline posture)
  value: number; // 0..1 â€” DEPTH (confidence-weighted grounding of present evidence)
  coverageRate: number; // 0..1 â€” BREADTH (present applicable / total applicable)
  band: string | null; // 6-label band; null when cold-start (present=false)
  present: boolean;
  confidence: number;
  applicableCount: number;
  presentCount: number;
  requirementCount: number;
  missing: number;
  naExcluded: number;
  // Evidence lifecycle step 2: present only when the server flag is on.
  paperDocuments?: { evidenceId: number; fileName: string; lines: string[] }[];
  policyDocumentCount?: number;
}

// Evidence-aggregation kernel â€” two-axis evidence-posture grade/band per project-standard.
export const evidencePostureAPI = {
  get: async (projectId: number, standard?: string) => {
    const q = standard ? `?standard=${encodeURIComponent(standard)}` : '';
    const response = await apiClient.get(`/v2/projects/${projectId}/evidence-posture${q}`);
    return response.data as { standards: EvidencePostureStandard[]; calibration: string };
  },
};

// Evidence lifecycle step 2 - waivers (org-admin only on writes; 404 when the flag is off).
export interface LifecycleOverride {
  id: number;
  evidence_id: number;
  condition: string;
  reason: string;
  [key: string]: unknown;
}

export const lifecycleOverridesAPI = {
  list: async (projectId: number, evidenceId: number): Promise<{ overrides: LifecycleOverride[] }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/lifecycle-overrides?evidence_id=${evidenceId}`);
    return response.data;
  },
  create: async (
    projectId: number,
    body: { evidence_id: number; condition: string; reason: string }
  ): Promise<{ override: LifecycleOverride }> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/lifecycle-overrides`, body);
    return response.data;
  },
};

export const sprsAPI = {
  getScore: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/sprs/score`);
    return response.data;
  },

  getGaps: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/sprs/gaps`);
    return response.data;
  },

  getHistory: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/sprs/history`);
    return response.data;
  },

  getGapToThreshold: async (projectId: number, target: number = 0) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/sprs/gap-to-threshold?target=${target}`);
    return response.data;
  },

  createScenario: async (projectId: number, data: { name: string; description?: string; controlOverrides: Array<{ controlId: string; newStatus: string }> }) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/sprs/scenarios`, data);
    return response.data;
  },

  getScenarios: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/sprs/scenarios`);
    return response.data;
  },

  simulateScenario: async (projectId: number, scenarioId: number) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/sprs/scenarios/${scenarioId}/simulate`);
    return response.data;
  },

  getDibcacRisk: async (projectId: number, segment?: string) => {
    const url = segment
      ? `/v2/projects/${projectId}/sprs/dibcac-risk?segment=${segment}`
      : `/v2/projects/${projectId}/sprs/dibcac-risk`;
    const response = await apiClient.get(url);
    return response.data;
  },

  submitScore: async (projectId: number, data: { submissionType: string; affirmationText: string; checklistCompleted?: Record<string, boolean>; notes?: string }) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/sprs/submissions`, data);
    return response.data;
  },

  getSubmissions: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/sprs/submissions`);
    return response.data;
  },

  getPoamProjection: async (projectId: number) => {
    const response = await apiClient.get(`/v2/projects/${projectId}/sprs/poam-projection`);
    return response.data;
  },

  exportScorecard: async (projectId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/sprs/scorecard-pdf`, {
      responseType: 'blob',
      timeout: 30000,
    });
    return response.data;
  },
};

// â”€â”€ RMF Journey API (ATO Package Generation â€” 6-step wizard) â”€â”€â”€â”€â”€
export type RmfStepNumber = 1 | 2 | 3 | 4 | 5 | 6;
export type RmfStepStatus = 'not_started' | 'in_progress' | 'complete' | 'blocked';

export interface RmfStepArtifactRef {
  type: string;
  document_id?: number;
  filename?: string;
  generated_at?: string;
  notes?: string;
}

export interface RmfGateEvaluation {
  satisfied: boolean;
  missing: string[];
  satisfied_conditions: string[];
}

export interface RmfStepDetail {
  step: RmfStepNumber;
  name: string;
  goal: string;
  status: RmfStepStatus;
  inputs_required: string[];
  prism_actions: string[];
  artifacts_produced: string[];
  gate_to_next: string[];
  gate_evaluation: RmfGateEvaluation;
  artifacts_registered: RmfStepArtifactRef[];
  started_at: string | null;
  completed_at: string | null;
}

export interface RmfJourneyState {
  project_id: number;
  current_step: RmfStepNumber;
  completed_steps: RmfStepNumber[];
  step_artifacts: Record<string, RmfStepArtifactRef[]>;
  step_started_at: Record<string, string>;
  step_completed_at: Record<string, string>;
  overrides: Array<{ step: number; user_id: number; justification: string; at: string }>;
  created_at: string;
  updated_at: string;
}

export interface RmfAdvanceResult {
  advanced: boolean;
  from_step: RmfStepNumber;
  to_step: RmfStepNumber | null;
  gate_evaluation: RmfGateEvaluation;
  message: string;
}

export const rmfJourneyAPI = {
  getState: async (projectId: number): Promise<RmfJourneyState> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/rmf/state`);
    return response.data;
  },
  initialize: async (projectId: number): Promise<RmfJourneyState> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/rmf/initialize`);
    return response.data;
  },
  getSteps: async (projectId: number): Promise<{ steps: RmfStepDetail[] }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/rmf/steps`);
    return response.data;
  },
  getStep: async (projectId: number, step: RmfStepNumber): Promise<RmfStepDetail> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/rmf/steps/${step}`);
    return response.data;
  },
  advance: async (projectId: number): Promise<RmfAdvanceResult> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/rmf/advance`);
    return response.data;
  },
  override: async (projectId: number, justification: string): Promise<RmfAdvanceResult> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/rmf/override`, { justification });
    return response.data;
  },
  registerArtifact: async (
    projectId: number,
    step: RmfStepNumber,
    artifact: { type: string; document_id?: number; filename?: string; notes?: string }
  ) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/rmf/steps/${step}/artifacts`, artifact);
    return response.data;
  },
  listStaleArtifacts: async (
    projectId: number
  ): Promise<{ stale: StaleArtifactEntry[] }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/rmf/stale-artifacts`);
    return response.data;
  },
};

// â”€â”€ HIPAA Security Rule Journey (T6) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// 5-step Risk-Analysis-centric Journey. Mirrors the RMF journey API shape;
// the step bodies embed existing surfaces (RarWorkflow, StandardAssessment).
export type HipaaStepNumber = 1 | 2 | 3 | 4 | 5;
export type HipaaStepStatus = 'not_started' | 'in_progress' | 'complete' | 'blocked';

export interface HipaaStepArtifactRef {
  type: string;
  document_id?: number;
  filename?: string;
  generated_at?: string;
  notes?: string;
}

export interface HipaaGateEvaluation {
  satisfied: boolean;
  missing: string[];
  satisfied_conditions: string[];
}

export interface HipaaStepDetail {
  step: HipaaStepNumber;
  name: string;
  goal: string;
  status: HipaaStepStatus;
  inputs_required: string[];
  prism_actions: string[];
  artifacts_produced: string[];
  gate_to_next: string[];
  gate_evaluation: HipaaGateEvaluation;
  artifacts_registered: HipaaStepArtifactRef[];
  started_at: string | null;
  completed_at: string | null;
}

export interface HipaaJourneyState {
  project_id: number;
  current_step: HipaaStepNumber;
  completed_steps: HipaaStepNumber[];
  step_artifacts: Record<string, HipaaStepArtifactRef[]>;
  step_started_at: Record<string, string>;
  step_completed_at: Record<string, string>;
  overrides: Array<{ step: number; user_id: number; justification: string; at: string }>;
  created_at: string;
  updated_at: string;
}

export interface HipaaAdvanceResult {
  advanced: boolean;
  from_step: HipaaStepNumber;
  to_step: HipaaStepNumber | null;
  gate_evaluation: HipaaGateEvaluation;
  message: string;
}

// â”€â”€ ITAR Journey (2-step: Jurisdiction/USML Determination â†’ Technology Control Plan) â”€â”€
// ITAR OWNS its substance: step_inputs holds the determination conclusion/rationale
// + the TCP section inputs; gates read them. Mirrors the journey-API shape, but the
// GET state returns per-step details + gates inline (no separate getSteps call).
export type ItarStepNumber = 1 | 2;

export interface ItarGate {
  satisfied: boolean;
  missing: string[];
}

export interface ItarStepDetail {
  step: ItarStepNumber;
  key: 'determination' | 'tcp';
  title: string;
  gate: ItarGate;
  inputs: Record<string, any>;
  completed: boolean;
  current: boolean;
}

export interface ItarJourneyState {
  project_id: number;
  current_step: ItarStepNumber;
  overrides: Array<{ step: number; justification: string; at: string }>;
  steps: ItarStepDetail[];
}

export interface ItarAdvanceResult {
  advanced: boolean;
  current_step: ItarStepNumber;
  reason?: string;
}

export const itarJourneyAPI = {
  getState: async (projectId: number): Promise<ItarJourneyState> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/itar/state`);
    return response.data;
  },
  saveInputs: async (
    projectId: number,
    step: ItarStepNumber,
    inputs: Record<string, any>
  ): Promise<ItarJourneyState> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/itar/steps/${step}/inputs`,
      inputs
    );
    return response.data;
  },
  advance: async (projectId: number): Promise<ItarAdvanceResult> => {
    // Refused advance is a 409 carrying the same result body; surface it via catch.
    const response = await apiClient.post(`/v2/projects/${projectId}/itar/advance`, {});
    return response.data;
  },
  override: async (projectId: number, justification: string): Promise<ItarAdvanceResult> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/itar/override`, {
      justification,
    });
    return response.data;
  },
  // Streams a single DOCX on success; 422 { error } when a step gate is unsatisfied.
  generatePackage: async (projectId: number): Promise<Blob> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/itar/package`,
      {},
      { responseType: 'blob', timeout: 120000 }
    );
    return new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
  },
};

export const hipaaJourneyAPI = {
  getState: async (projectId: number): Promise<HipaaJourneyState> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/hipaa/state`);
    return response.data;
  },
  initialize: async (projectId: number): Promise<HipaaJourneyState> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/hipaa/initialize`);
    return response.data;
  },
  getSteps: async (projectId: number): Promise<{ steps: HipaaStepDetail[] }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/hipaa/steps`);
    return response.data;
  },
  getStep: async (projectId: number, step: HipaaStepNumber): Promise<HipaaStepDetail> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/hipaa/steps/${step}`);
    return response.data;
  },
  advance: async (projectId: number): Promise<HipaaAdvanceResult> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/hipaa/advance`);
    return response.data;
  },
  override: async (projectId: number, justification: string): Promise<HipaaAdvanceResult> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/hipaa/override`, {
      justification,
    });
    return response.data;
  },
  registerArtifact: async (
    projectId: number,
    step: HipaaStepNumber,
    artifact: { type: string; document_id?: number; filename?: string; notes?: string }
  ) => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/hipaa/steps/${step}/artifacts`,
      artifact
    );
    return response.data;
  },
  // One-click package: ZIP of Risk Analysis + Risk Management Plan + Safeguard
  // Documentation DOCX. Returns a Blob ready for URL.createObjectURL download;
  // the server registers the hipaa_package artifact (satisfies the Step 5 gate).
  exportPackage: async (projectId: number): Promise<Blob> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/hipaa/package/export`,
      {},
      { responseType: 'blob', timeout: 120000 }
    );
    return new Blob([response.data], { type: 'application/zip' });
  },
};

export interface StaleArtifactEntry {
  step: RmfStepNumber;
  type: string;
  document_id?: number;
  filename?: string;
  generated_at?: string;
  notes?: string;
  is_stale: true;
}

// â”€â”€ SOC 2 SSP Journey API (AICPA Section III â€” 6-step wizard) â”€â”€â”€â”€
// ── CMMC Journey API (Task 9) ─────────────────────────────────────
// 6-step Journey (Scope → SSP → Self-Assessment → SPRS Score/Self-Attestation →
// POA&M/Confirm-All-Met → Package & Submit). Mirrors hipaaJourneyAPI shape.
// Backend: backend/src/routes/cmmcJourney.ts; backend/src/services/cmmcJourneyService.ts
export type CmmcStepNumber = 1 | 2 | 3 | 4 | 5 | 6;
export interface CmmcJourneyState {
  project_id: number;
  target_level: 1 | 2 | 3;
  current_step: CmmcStepNumber;
  completed_steps: CmmcStepNumber[];
  step_artifacts: Record<string, any[]>;
  submitted_at: string | null;
}
// Mirrors the backend CmmcStepDetail as built (Task 6): the step content fields
// are `key`/`title`/`description` (NOT name/goal/inputs_required/…).
export interface CmmcStepDetail {
  step: CmmcStepNumber;
  key: string;
  title: string;
  description: string;
  status: 'locked' | 'active' | 'complete';
  gate_evaluation: { satisfied: boolean; missing: string[]; detail?: string };
  artifacts_registered: any[];
  started_at: string | null;
  completed_at: string | null;
}

// ── SSP archive (Reports + CMMC journey downloads) ─────────────────────────
export interface SspVersionListItem {
  id: number;
  versionNo: number;
  source: 'cmmc_journey' | 'compliance_packages' | 'oscal_export';
  standardId: string | null;
  createdAt: string;
  createdBy: { id: number; name: string } | null;
  formats: Array<{ format: 'docx' | 'pdf' | 'oscal_zip'; byteSize: number; fileName: string }>;
  isLatestForSource: boolean;
  supersededByVersionNo: number | null;
  outOfDate: true | null;
}

/**
 * A failed `responseType: 'blob'` request hands back the JSON error body as a Blob, so the
 * server's message is invisible ("Request failed with status code 404"). Read it out and
 * rethrow an Error carrying the server's sentence; anything unparseable rethrows the original.
 */
async function throwBlobError(err: unknown): Promise<never> {
  const response = (err as { response?: { data?: unknown } } | null)?.response;
  const data = response?.data;
  let serverMessage: string | null = null;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(data);
      });
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed.error === 'string' && parsed.error) serverMessage = parsed.error;
    } catch {
      // not JSON: keep the original error
    }
  }
  if (serverMessage === null) throw err;
  const out = new Error(serverMessage) as Error & { response?: unknown };
  out.response = { ...response, data: { error: serverMessage } };
  throw out;
}

function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const sspArchiveAPI = {
  list: async (projectId: number): Promise<SspVersionListItem[]> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/ssp-archive`);
    return response.data.versions;
  },
  download: async (
    projectId: number,
    versionId: number,
    format: 'docx' | 'pdf' | 'oscal_zip',
    fileName: string
  ): Promise<void> => {
    let res;
    try {
      res = await apiClient.get(`/v2/projects/${projectId}/ssp-archive/${versionId}/${format}`, {
        responseType: 'blob',
      });
    } catch (err) {
      return throwBlobError(err);
    }
    saveBlob(res.data, fileName);
  },
};

export const cmmcJourneyAPI = {
  getState: async (projectId: number): Promise<CmmcJourneyState> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cmmc/state`);
    return response.data;
  },
  getSteps: async (projectId: number): Promise<CmmcStepDetail[]> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cmmc/steps`);
    // Route returns { steps: [...] } (mirrors rmfJourney); unwrap to the array.
    return response.data.steps;
  },
  initialize: async (projectId: number): Promise<CmmcJourneyState> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/cmmc/initialize`);
    return response.data;
  },
  advance: async (projectId: number) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/cmmc/advance`);
    return response.data;
  },
  override: async (projectId: number, justification: string) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/cmmc/override`, {
      justification,
    });
    return response.data;
  },
  submit: async (projectId: number) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/cmmc/submit`);
    return response.data;
  },
  sprsSummaryUrl: (projectId: number, fmt: 'docx' | 'pdf' | 'csv'): string =>
    `${API_BASE_URL}/v2/projects/${projectId}/cmmc/sprs-summary.${fmt}`,
  generateSsp: async (projectId: number): Promise<{ filename: string; versionNo: number }> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/cmmc/ssp/generate`);
    return response.data;
  },
  sspUrl: (projectId: number, fmt: 'docx' | 'pdf'): string =>
    `${API_BASE_URL}/v2/projects/${projectId}/cmmc/ssp.${fmt}`,
  // Authenticated file downloads. A raw <a href> browser navigation does NOT carry
  // the Bearer token, so the route's authenticateToken 401s ("Access token required").
  // XHR via apiClient carries it → stream the blob to a browser download instead.
  downloadSsp: async (projectId: number, fmt: 'docx' | 'pdf'): Promise<void> => {
    let res;
    try {
      res = await apiClient.get(`/v2/projects/${projectId}/cmmc/ssp.${fmt}`, {
        responseType: 'blob',
      });
    } catch (err) {
      return throwBlobError(err);
    }
    saveBlob(res.data, `SSP-${projectId}.${fmt}`);
  },
  downloadSprsSummary: async (projectId: number, fmt: 'docx' | 'pdf' | 'csv'): Promise<void> => {
    const res = await apiClient.get(`/v2/projects/${projectId}/cmmc/sprs-summary.${fmt}`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CMMC-Summary-${projectId}.${fmt}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

// ── SOC 2 SSP Journey API (AICPA Section III — 6-step wizard) ────
// Sibling client to rmfJourneyAPI. Spec: SOC2_SSP_JOURNEY_SCOPING_SPEC.md v1.1
// Backend: backend/src/routes/soc2Journey.ts; backend/src/services/soc2JourneyService.ts
export type Soc2StepNumber = 1 | 2 | 3 | 4 | 5 | 6;
export type Soc2StepStatus = 'not_started' | 'in_progress' | 'complete' | 'blocked';
export type Soc2ReportType = 'type_1' | 'type_2';

export interface Soc2StepArtifactRef {
  type: string;
  document_id?: number;
  filename?: string;
  generated_at?: string;
  notes?: string;
  is_stale?: boolean;
}

export interface Soc2GateEvaluation {
  satisfied: boolean;
  missing: string[];
  satisfied_conditions: string[];
}

export interface Soc2StepDetail {
  step: Soc2StepNumber;
  name: string;
  goal: string;
  status: Soc2StepStatus;
  inputs_required: string[];
  prism_actions: string[];
  artifacts_produced: string[];
  gate_to_next: string[];
  gate_evaluation: Soc2GateEvaluation;
  artifacts_registered: Soc2StepArtifactRef[];
  started_at: string | null;
  completed_at: string | null;
}

export interface Soc2JourneyState {
  project_id: number;
  current_step: Soc2StepNumber;
  completed_steps: Soc2StepNumber[];
  step_artifacts: Record<string, Soc2StepArtifactRef[]>;
  step_started_at: Record<string, string>;
  step_completed_at: Record<string, string>;
  overrides: Array<{ step: number; user_id: number; justification: string; at: string }>;
  created_at: string;
  updated_at: string;
}

export interface Soc2AdvanceResult {
  advanced: boolean;
  from_step: Soc2StepNumber;
  to_step: Soc2StepNumber | null;
  gate_evaluation: Soc2GateEvaluation;
  message: string;
}

export type Soc2TscCategory =
  | 'security'
  | 'availability'
  | 'processing_integrity'
  | 'confidentiality'
  | 'privacy';

export interface Soc2Scope {
  project_id: number;
  tsc_categories: Soc2TscCategory[];
  report_type: Soc2ReportType;
  audit_period_start: string | null;
  audit_period_end: string | null;
  system_name: string | null;
  boundary_description: string | null;
}

export interface Soc2ScopeInput {
  tsc_categories?: Soc2TscCategory[];
  report_type?: Soc2ReportType;
  audit_period_start?: string | null;
  audit_period_end?: string | null;
  system_name?: string | null;
  boundary_description?: string | null;
}

export type Soc2ComponentCategory =
  | 'infrastructure'
  | 'software'
  | 'people'
  | 'data'
  | 'processes';

export interface Soc2ServiceCommitment {
  key: string;
  title: string;
  narrative: string;
}

export interface Soc2SystemComponent {
  id: number;
  project_id: number;
  category: Soc2ComponentCategory;
  name: string;
  description: string | null;
  is_subservice_provider: boolean;
}

export interface Soc2SystemDescription {
  project_id: number;
  service_narrative: string | null;
  service_commitments: Soc2ServiceCommitment[];
  boundary_statement: string | null;
  significant_changes: string | null;
  components: Soc2SystemComponent[];
}

export interface Soc2SystemDescriptionInput {
  service_narrative?: string | null;
  boundary_statement?: string | null;
  significant_changes?: string | null;
}

export interface Soc2ServiceCommitmentInput {
  title: string;
  narrative: string;
}

export interface Soc2SystemComponentInput {
  category: Soc2ComponentCategory;
  name: string;
  description?: string | null;
  is_subservice_provider?: boolean;
}

export type Soc2RiskImpact = 'low' | 'medium' | 'high';

export interface Soc2Risk {
  id: number;
  project_id: number;
  catalog_id: number | null;
  tsc_category: Soc2TscCategory;
  title: string;
  description: string | null;
  impact_rating: Soc2RiskImpact | null;
}

export interface Soc2RiskInput {
  tsc_category: Soc2TscCategory;
  title: string;
  description?: string | null;
  impact_rating?: Soc2RiskImpact | null;
  catalog_id?: number | null;
}

// Privacy Impact Assessment (GDPR Art. 35 + CCPA). Surfaces in Soc2RiskAssessmentWorkflow
// when project scope includes 'privacy'. Single row per project; partial-update merge.
export interface Soc2Pia {
  project_id: number;
  processing_purposes: string | null;
  data_subjects: string | null;
  personal_data_categories: string | null;
  lawful_basis: string | null;
  retention_period: string | null;
  security_measures: string | null;
  international_transfers: string | null;
  risks_to_data_subjects: string | null;
  mitigations: string | null;
  dpia_required: boolean;
  prior_consultation_required: boolean;
  ccpa_categories: string | null;
  ccpa_sale_or_share: boolean;
  last_reviewed_at: string | null;
}

export interface Soc2PiaInput {
  processing_purposes?: string | null;
  data_subjects?: string | null;
  personal_data_categories?: string | null;
  lawful_basis?: string | null;
  retention_period?: string | null;
  security_measures?: string | null;
  international_transfers?: string | null;
  risks_to_data_subjects?: string | null;
  mitigations?: string | null;
  dpia_required?: boolean;
  prior_consultation_required?: boolean;
  ccpa_categories?: string | null;
  ccpa_sale_or_share?: boolean;
}

export interface Soc2TscCriterion {
  requirement_id: number;
  tsc_code: string;
  tsc_category: Soc2TscCategory;
  title: string | null;
}

export interface Soc2Control {
  id: number;
  project_id: number;
  control_name: string;
  tsc_criteria: string[];
  implementation_narrative: string | null;
  operating_effectiveness_narrative: string | null;
  requirement_id: number | null;
}

export interface Soc2ControlInput {
  control_name: string;
  requirement_id: number;
  tsc_criteria?: string[];
  implementation_narrative?: string | null;
  operating_effectiveness_narrative?: string | null;
}

export interface Soc2ControlsBundle {
  criteria: Soc2TscCriterion[];
  controls: Soc2Control[];
}

export type Soc2ComplementaryKind = 'cuec' | 'csoc';

export interface Soc2ComplementaryControl {
  id: number;
  project_id: number;
  kind: Soc2ComplementaryKind;
  subservice_provider_id: number | null;
  title: string;
  description: string | null;
}

export interface Soc2ComplementaryControlInput {
  kind: Soc2ComplementaryKind;
  subservice_provider_id?: number | null;
  title: string;
  description?: string | null;
}

export const soc2JourneyAPI = {
  getState: async (projectId: number): Promise<Soc2JourneyState> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/soc2/state`);
    return response.data;
  },
  initialize: async (projectId: number): Promise<Soc2JourneyState> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/soc2/initialize`);
    return response.data;
  },
  getSteps: async (projectId: number): Promise<{ steps: Soc2StepDetail[] }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/soc2/steps`);
    return response.data;
  },
  getStep: async (projectId: number, step: Soc2StepNumber): Promise<Soc2StepDetail> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/soc2/steps/${step}`);
    return response.data;
  },
  advance: async (projectId: number): Promise<Soc2AdvanceResult> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/soc2/advance`);
    return response.data;
  },
  override: async (projectId: number, justification: string): Promise<Soc2AdvanceResult> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/soc2/override`, { justification });
    return response.data;
  },
  syncFromComplianceHub: async (projectId: number): Promise<{ created: number; skipped: number }> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/soc2/sync-from-compliance-hub`);
    return response.data;
  },
  registerArtifact: async (
    projectId: number,
    step: Soc2StepNumber,
    artifact: { type: string; document_id?: number; filename?: string; notes?: string; source_iris_draft_ids?: number[] }
  ) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/soc2/artifacts`, { step, ...artifact });
    return response.data;
  },
  checkSubstance: async (
    projectId: number,
    artifactType: string
  ): Promise<{ ok: boolean; artifact_type: string; reason?: string }> => {
    try {
      const response = await apiClient.get(
        `/v2/projects/${projectId}/soc2/substance/${encodeURIComponent(artifactType)}`
      );
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 422) return err.response.data;
      throw err;
    }
  },
  getScope: async (projectId: number): Promise<Soc2Scope | null> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/soc2/scope`);
    return (response.data?.scope ?? null) as Soc2Scope | null;
  },
  upsertScope: async (projectId: number, input: Soc2ScopeInput): Promise<Soc2Scope> => {
    const response = await apiClient.put(`/v2/projects/${projectId}/soc2/scope`, input);
    return response.data.scope as Soc2Scope;
  },
  getSystemDescription: async (projectId: number): Promise<Soc2SystemDescription> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/soc2/system-description`);
    return response.data.description as Soc2SystemDescription;
  },
  upsertSystemDescription: async (
    projectId: number,
    input: Soc2SystemDescriptionInput
  ): Promise<Soc2SystemDescription> => {
    const response = await apiClient.put(
      `/v2/projects/${projectId}/soc2/system-description`,
      input
    );
    return response.data.description as Soc2SystemDescription;
  },
  addCommitment: async (
    projectId: number,
    input: Soc2ServiceCommitmentInput
  ): Promise<Soc2ServiceCommitment> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/soc2/commitments`, input);
    return response.data.commitment as Soc2ServiceCommitment;
  },
  updateCommitment: async (
    projectId: number,
    key: string,
    input: Soc2ServiceCommitmentInput
  ): Promise<Soc2ServiceCommitment> => {
    const response = await apiClient.put(
      `/v2/projects/${projectId}/soc2/commitments/${encodeURIComponent(key)}`,
      input
    );
    return response.data.commitment as Soc2ServiceCommitment;
  },
  deleteCommitment: async (projectId: number, key: string): Promise<void> => {
    await apiClient.delete(
      `/v2/projects/${projectId}/soc2/commitments/${encodeURIComponent(key)}`
    );
  },
  addComponent: async (
    projectId: number,
    input: Soc2SystemComponentInput
  ): Promise<Soc2SystemComponent> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/soc2/components`, input);
    return response.data.component as Soc2SystemComponent;
  },
  updateComponent: async (
    projectId: number,
    componentId: number,
    input: Soc2SystemComponentInput
  ): Promise<Soc2SystemComponent> => {
    const response = await apiClient.put(
      `/v2/projects/${projectId}/soc2/components/${componentId}`,
      input
    );
    return response.data.component as Soc2SystemComponent;
  },
  deleteComponent: async (projectId: number, componentId: number): Promise<void> => {
    await apiClient.delete(`/v2/projects/${projectId}/soc2/components/${componentId}`);
  },
  listRisks: async (projectId: number): Promise<Soc2Risk[]> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/soc2/risks`);
    return (response.data?.risks ?? []) as Soc2Risk[];
  },
  addRisk: async (projectId: number, input: Soc2RiskInput): Promise<Soc2Risk> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/soc2/risks`, input);
    return response.data.risk as Soc2Risk;
  },
  updateRisk: async (
    projectId: number,
    riskId: number,
    input: Soc2RiskInput
  ): Promise<Soc2Risk> => {
    const response = await apiClient.put(
      `/v2/projects/${projectId}/soc2/risks/${riskId}`,
      input
    );
    return response.data.risk as Soc2Risk;
  },
  deleteRisk: async (projectId: number, riskId: number): Promise<void> => {
    await apiClient.delete(`/v2/projects/${projectId}/soc2/risks/${riskId}`);
  },
  getPia: async (projectId: number): Promise<Soc2Pia | null> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/soc2/pia`);
    return (response.data?.pia ?? null) as Soc2Pia | null;
  },
  upsertPia: async (projectId: number, input: Soc2PiaInput): Promise<Soc2Pia> => {
    const response = await apiClient.put(`/v2/projects/${projectId}/soc2/pia`, input);
    return response.data.pia as Soc2Pia;
  },
  getControlsBundle: async (projectId: number): Promise<Soc2ControlsBundle> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/soc2/controls`);
    return {
      criteria: (response.data?.criteria ?? []) as Soc2TscCriterion[],
      controls: (response.data?.controls ?? []) as Soc2Control[],
    };
  },
  addControl: async (projectId: number, input: Soc2ControlInput): Promise<Soc2Control> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/soc2/controls`, input);
    return response.data.control as Soc2Control;
  },
  updateControl: async (
    projectId: number,
    controlId: number,
    input: Soc2ControlInput
  ): Promise<Soc2Control> => {
    const response = await apiClient.put(
      `/v2/projects/${projectId}/soc2/controls/${controlId}`,
      input
    );
    return response.data.control as Soc2Control;
  },
  deleteControl: async (projectId: number, controlId: number): Promise<void> => {
    await apiClient.delete(`/v2/projects/${projectId}/soc2/controls/${controlId}`);
  },
  listComplementary: async (projectId: number): Promise<Soc2ComplementaryControl[]> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/soc2/complementary`);
    return (response.data?.items ?? []) as Soc2ComplementaryControl[];
  },
  addComplementary: async (
    projectId: number,
    input: Soc2ComplementaryControlInput
  ): Promise<Soc2ComplementaryControl> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/soc2/complementary`, input);
    return response.data.item as Soc2ComplementaryControl;
  },
  updateComplementary: async (
    projectId: number,
    ccId: number,
    input: Soc2ComplementaryControlInput
  ): Promise<Soc2ComplementaryControl> => {
    const response = await apiClient.put(
      `/v2/projects/${projectId}/soc2/complementary/${ccId}`,
      input
    );
    return response.data.item as Soc2ComplementaryControl;
  },
  deleteComplementary: async (projectId: number, ccId: number): Promise<void> => {
    await apiClient.delete(`/v2/projects/${projectId}/soc2/complementary/${ccId}`);
  },
  generateSectionIii: async (
    projectId: number,
    format: 'docx' | 'pdf' = 'docx'
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/soc2/section-iii/generate?format=${format}`,
      {},
      { responseType: 'blob', timeout: 120000 }
    );
    const cd: string =
      (response.headers as any)?.['content-disposition'] ||
      (response.headers as any)?.get?.('content-disposition') ||
      '';
    const match = cd.match(/filename="?([^";]+)"?/i);
    const filename =
      (match && match[1]) ||
      `project_${projectId}_SOC2_SectionIII_${new Date().toISOString().slice(0, 10)}.${format}`;
    return { blob: response.data as Blob, filename };
  },
  generatePackage: async (
    projectId: number
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/soc2/package/generate`,
      {},
      { responseType: 'blob', timeout: 180000 }
    );
    const cd: string =
      (response.headers as any)?.['content-disposition'] ||
      (response.headers as any)?.get?.('content-disposition') ||
      '';
    const match = cd.match(/filename="?([^";]+)"?/i);
    const filename =
      (match && match[1]) ||
      `project_${projectId}_SOC2_Type2_Package_${new Date().toISOString().slice(0, 10)}.zip`;
    return { blob: response.data as Blob, filename };
  },
};

// â”€â”€ FIPS 199 Categorization API (RMF Step 1) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export type Fips199Impact = 'low' | 'moderate' | 'high';

export interface Fips199InfoType {
  id: string;
  category: string;
  subcategory: string | null;
  name: string;
  description: string | null;
  default_c_impact: Fips199Impact;
  default_i_impact: Fips199Impact;
  default_a_impact: Fips199Impact;
  is_common: boolean;
  display_order: number;
  source_reference: string | null;
}

export interface Fips199InfoTypeSelection {
  id: number;
  project_id: number;
  info_type_id: string;
  c_impact: Fips199Impact;
  i_impact: Fips199Impact;
  a_impact: Fips199Impact;
  override_rationale: string | null;
  notes: string | null;
  name?: string;
  category?: string;
  subcategory?: string | null;
  default_c_impact?: Fips199Impact;
  default_i_impact?: Fips199Impact;
  default_a_impact?: Fips199Impact;
}

export interface Fips199Categorization {
  project_id: number;
  system_name: string | null;
  system_acronym: string | null;
  system_type: string | null;
  operational_status: string | null;
  mission_purpose: string | null;
  boundary_description: string | null;
  system_owner_name: string | null;
  system_owner_title: string | null;
  system_owner_email: string | null;
  system_owner_phone: string | null;
  authorizing_official: string | null;
  authorizing_official_title: string | null;
  authorizing_official_email: string | null;
  isso_name: string | null;
  isso_email: string | null;
  // Added in migration 130 to close SSP Â§5 personnel [GAP]s.
  issm_name: string | null;
  issm_email: string | null;
  sca_name: string | null;
  sca_email: string | null;
  sysadmin_name: string | null;
  sysadmin_email: string | null;
  privacy_officer_name: string | null;
  privacy_officer_email: string | null;
  // Added in migration 147 to close the four residual SSP [GAP]s
  // (Â§9.3 user_population, Â§12 organization_policies, Â§14/Â§15 plan_approval_date).
  user_population: string | null;
  organization_policies: string | null;
  plan_approval_date: string | null;
  classification: string | null;
  document_version: string | null;
  security_responsibility_statement: string | null;
  ongoing_authorization_statement: string | null;
  cato_enabled: boolean | null;
  overall_c_impact: Fips199Impact | null;
  overall_i_impact: Fips199Impact | null;
  overall_a_impact: Fips199Impact | null;
  overall_impact: Fips199Impact | null;
  rollup_justification: string | null;
  cui_in_scope: boolean;
  privacy_data_in_scope: boolean;
  cloud_hosted: boolean;
  status: 'draft' | 'approved';
  approved_at: string | null;
  memo_document_id: number | null;
  memo_generated_at: string | null;
}

export interface Fips199Summary {
  categorization: Fips199Categorization | null;
  selections: Fips199InfoTypeSelection[];
  rollup: {
    c_impact: Fips199Impact | null;
    i_impact: Fips199Impact | null;
    a_impact: Fips199Impact | null;
    overall: Fips199Impact | null;
    has_selections: boolean;
  };
}

export const fips199API = {
  listInfoTypes: async (): Promise<{ info_types: Fips199InfoType[]; total: number }> => {
    const response = await apiClient.get('/v2/fips199/info-types');
    return response.data;
  },
  getSummary: async (projectId: number): Promise<Fips199Summary> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/fips199`);
    return response.data;
  },
  updateCategorization: async (
    projectId: number,
    updates: Partial<Fips199Categorization>
  ): Promise<Fips199Categorization> => {
    const response = await apiClient.put(`/v2/projects/${projectId}/fips199`, updates);
    return response.data;
  },
  addSelection: async (
    projectId: number,
    data: {
      info_type_id: string;
      c_impact?: Fips199Impact;
      i_impact?: Fips199Impact;
      a_impact?: Fips199Impact;
      override_rationale?: string;
      notes?: string;
    }
  ): Promise<Fips199InfoTypeSelection & { conflict_ids?: number[] }> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/fips199/selections`, data);
    return response.data;
  },
  updateSelection: async (
    projectId: number,
    infoTypeId: string,
    data: {
      c_impact?: Fips199Impact;
      i_impact?: Fips199Impact;
      a_impact?: Fips199Impact;
      override_rationale?: string;
      notes?: string;
    }
  ): Promise<Fips199InfoTypeSelection & { conflict_ids?: number[] }> => {
    const response = await apiClient.put(
      `/v2/projects/${projectId}/fips199/selections/${infoTypeId}`,
      data
    );
    return response.data;
  },
  removeSelection: async (
    projectId: number,
    infoTypeId: string
  ): Promise<{ success: boolean; conflict_ids?: number[] }> => {
    const response = await apiClient.delete(
      `/v2/projects/${projectId}/fips199/selections/${infoTypeId}`
    );
    return response.data;
  },
  generateMemo: async (projectId: number, format: 'docx' | 'pdf' = 'docx'): Promise<Blob> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/fips199/memo?format=${format}`,
      {},
      { responseType: 'blob', timeout: 30000 }
    );
    return response.data;
  },
  previewMemo: async (projectId: number): Promise<{ variables: Record<string, any> }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/fips199/memo/preview`);
    return response.data;
  },
};

// â”€â”€ FedRAMP CRM Inheritance API (RMF Step 2) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export type CrmInheritanceType = 'fully_inherited' | 'shared' | 'customer';

export interface PlatformCrmImport {
  id: number;
  provider_key: string;
  provider_name: string;
  fedramp_package_id: string | null;
  fedramp_level: string | null;
  ato_identifier: string | null;
  version: string;
  version_date: string | null;
  baseline_standard_id: string;
  baseline_revision: string | null;
  source_file_name: string | null;
  source_url: string | null;
  notes: string | null;
  organization_id: number | null;
  is_system_catalog: boolean;
  is_active: boolean;
  imported_by: number | null;
  imported_at: string;
  entries_count?: number;
}

export interface CrmControlEntry {
  id: number;
  crm_import_id: number;
  control_id: string;
  control_title: string | null;
  inheritance_type: CrmInheritanceType;
  provider_implementation: string | null;
  customer_responsibility: string | null;
  customer_effort: string | null;
  evidence_reference: string | null;
  notes: string | null;
}

export interface ControlInheritance {
  id: number;
  project_id: number;
  crm_import_id: number;
  crm_entry_id: number;
  control_id: string;
  effective_type: CrmInheritanceType;
  is_overridden: boolean;
  override_rationale: string | null;
  overridden_by: number | null;
  overridden_at: string | null;
  applied_at: string;
  control_title?: string;
  provider_implementation?: string | null;
  customer_responsibility?: string | null;
  customer_effort?: string | null;
  provider_name?: string;
  provider_key?: string;
  fedramp_level?: string | null;
  ato_identifier?: string | null;
}

export interface CrmApplyResult {
  applied: boolean;
  crm_import_id: number;
  provider_name: string;
  controls_applied: number;
  assessments_updated: number;
  fully_inherited_count: number;
  shared_count: number;
  customer_count: number;
  message: string;
}

export interface CrmInheritanceSummary {
  applied_crms: PlatformCrmImport[];
  inheritance: ControlInheritance[];
  counts: {
    total: number;
    fully_inherited: number;
    shared: number;
    customer: number;
    overridden: number;
  };
}

export const crmInheritanceAPI = {
  listImports: async (): Promise<{ imports: PlatformCrmImport[]; total: number }> => {
    const response = await apiClient.get('/v2/crm/imports');
    return response.data;
  },
  getImport: async (importId: number): Promise<{ import: PlatformCrmImport; entries: CrmControlEntry[] }> => {
    const response = await apiClient.get(`/v2/crm/imports/${importId}`);
    return response.data;
  },
  uploadImport: async (payload: {
    provider_key: string;
    provider_name: string;
    version: string;
    fedramp_package_id?: string;
    fedramp_level?: string;
    ato_identifier?: string;
    baseline_standard_id?: string;
    baseline_revision?: string;
    source_file_name?: string;
    source_url?: string;
    notes?: string;
    entries: Array<{
      control_id: string;
      control_title?: string;
      inheritance_type: string;
      provider_implementation?: string;
      customer_responsibility?: string;
      customer_effort?: string;
      evidence_reference?: string;
      notes?: string;
    }>;
  }): Promise<PlatformCrmImport> => {
    const response = await apiClient.post('/v2/crm/imports', payload);
    return response.data;
  },
  getProjectSummary: async (projectId: number): Promise<CrmInheritanceSummary> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/crm`);
    return response.data;
  },
  applyToProject: async (projectId: number, crmImportId: number): Promise<CrmApplyResult> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/crm/apply`, { crm_import_id: crmImportId });
    return response.data;
  },
  unapplyFromProject: async (projectId: number, crmImportId: number): Promise<{ removed: number }> => {
    const response = await apiClient.delete(`/v2/projects/${projectId}/crm/apply/${crmImportId}`);
    return response.data;
  },
  overrideControl: async (
    projectId: number,
    controlId: string,
    effective_type: CrmInheritanceType,
    rationale: string
  ): Promise<ControlInheritance> => {
    const response = await apiClient.put(
      `/v2/projects/${projectId}/crm/controls/${encodeURIComponent(controlId)}/override`,
      { effective_type, rationale }
    );
    return response.data;
  },
  clearOverride: async (projectId: number, controlId: string): Promise<ControlInheritance> => {
    const response = await apiClient.delete(
      `/v2/projects/${projectId}/crm/controls/${encodeURIComponent(controlId)}/override`
    );
    return response.data;
  },
};

// â”€â”€ CUI Scope + Overlay API (RMF Step 2 â€” Phase 2 Items 7+8) â”€â”€â”€â”€
export type CmmcScopeCategory =
  | 'cui'
  | 'security_protection'
  | 'contractor_risk_managed'
  | 'specialized'
  | 'out_of_scope';

export type CmmcScopeCategorySource = 'user' | 'iris' | 'default';

export interface CuiScopeStatus {
  project_id: number;
  cui_in_scope: boolean;
  privacy_data_in_scope: boolean;
  cloud_hosted: boolean;
  overall_impact: 'low' | 'moderate' | 'high' | null;
  total_assets: number;
  category_counts: Record<CmmcScopeCategory | 'uncategorized', number>;
  has_cui_assets: boolean;
  scoping_incomplete: boolean;
}

export interface CuiScopeAsset {
  id: number;
  name: string;
  asset_type: string | null;
  is_external: boolean;
  dod_tier: string | null;
  criticality: string | null;
  cmmc_scope_category: CmmcScopeCategory | null;
  cmmc_scope_category_source: CmmcScopeCategorySource | null;
  cmmc_scope_category_rationale: string | null;
  cmmc_scope_category_decided_at: string | null;
}

export type OverlayMode = 'inherent' | 'additive' | 'not_applicable';

export interface OverlayTrace {
  source_requirement_id: string;
  target_requirement_id: string;
  // STRM relationship per NIST IR 8477 (mig 172). Backend renamed from mapping_type.
  strm_relationship:
    | 'subset_of'
    | 'intersects_with'
    | 'equal'
    | 'superset_of'
    | 'no_relationship';
  mapping_strength: number | null;
  mapping_notes: string | null;
  already_in_baseline?: boolean;
}

export interface OverlayEvaluation {
  project_id: number;
  mode: OverlayMode;
  reason: string;
  source_standard_id: string | null;
  target_standard_id: string | null;
  cui_in_scope: boolean;
  traceability_count: number;
  baseline_covered_count: number;
  baseline_new_count: number;
  traces: OverlayTrace[];
}

export const cuiScopeAPI = {
  getStatus: async (projectId: number): Promise<CuiScopeStatus> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cui-scope`);
    return response.data;
  },
  listAssets: async (projectId: number): Promise<{ assets: CuiScopeAsset[] }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cui-scope/assets`);
    return response.data;
  },
  categorizeAsset: async (
    projectId: number,
    assetId: number,
    payload: {
      category: CmmcScopeCategory | null;
      source?: CmmcScopeCategorySource;
      // null = clear the rationale (distinct from undefined = leave unchanged)
      rationale?: string | null;
    }
  ): Promise<CuiScopeAsset> => {
    const response = await apiClient.patch(
      `/v2/projects/${projectId}/cui-scope/assets/${assetId}`,
      payload
    );
    return response.data;
  },
  bulkCategorize: async (
    projectId: number,
    payload: {
      asset_ids: number[];
      category: CmmcScopeCategory;
      source?: CmmcScopeCategorySource;
      rationale?: string;
    }
  ): Promise<{ updated: number }> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/cui-scope/assets/bulk`,
      payload
    );
    return response.data;
  },
  evaluateOverlay: async (projectId: number): Promise<OverlayEvaluation> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/cui-scope/overlay`);
    return response.data;
  },
  exportScopeDocument: async (projectId: number, format: 'docx' | 'pdf'): Promise<Blob> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/cui-scope/scope-document/export?format=${format}`,
      {},
      { responseType: 'blob' }
    );
    return response.data;
  },
  runAutoCategorization: async (
    projectId: number
  ): Promise<{
    total_assets: number;
    applied: number;
    skipped_user_set: number;
    by_category: Record<CmmcScopeCategory, number>;
  }> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/cui-scope/auto-categorize`,
      {}
    );
    return response.data;
  },
};

// â”€â”€ Security Assessment Plan + Report API (RMF Step 4) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export type AssessmentMethod = 'E' | 'I' | 'T';
export type AssessmentPlanStatus = 'draft' | 'active' | 'complete';

export interface AssessmentPlan {
  id: number;
  project_id: number;
  project_standard_id: number | null;
  plan_name: string;
  version: string;
  status: AssessmentPlanStatus;
  assessor_name: string | null;
  assessor_role: string | null;
  assessor_organization: string | null;
  assessor_email: string | null;
  assessment_start_date: string | null;
  assessment_end_date: string | null;
  introduction: string | null;
  rules_of_engagement: string | null;
  roles_and_responsibilities: string | null;
  reporting_instructions: string | null;
  activated_at: string | null;
  completed_at: string | null;
  sap_generated_at: string | null;
  sar_generated_at: string | null;
  controls_count?: number;
  standard_id?: string | null;
}

export interface AssessmentPlanControl {
  id: number;
  plan_id: number;
  control_id: string;
  methods: AssessmentMethod[];
  expected_evidence: string | null;
  scheduled_date: string | null;
  notes: string | null;
  requirement_name?: string | null;
  category?: string | null;
}

export interface AssessmentPlanDetail extends AssessmentPlan {
  controls: AssessmentPlanControl[];
}

export interface SarFinding {
  control_id: string;
  requirement_name: string;
  status: string;
  status_label: string;
  methods: string;
  notes: string;
  evidence: string;
  finding_class: 'passed' | 'partial' | 'failed' | 'not_assessed';
}

export interface SarPreview {
  variables: {
    document_version: string;
    generation_date: string;
    system_name: string;
    plan_name: string;
    standard_id: string;
    assessor_name: string;
    findings: SarFinding[];
    summary: {
      total_in_scope: number;
      passed: number;
      partial: number;
      failed: number;
      not_assessed: number;
      pass_rate: number;
    };
    [key: string]: any;
  };
}

export const assessmentPlanAPI = {
  listPlans: async (projectId: number): Promise<{ plans: AssessmentPlan[]; total: number }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/assessment/plans`);
    return response.data;
  },
  getActivePlan: async (projectId: number): Promise<{ plan: AssessmentPlanDetail | null }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/assessment/plans/active`);
    return response.data;
  },
  createPlan: async (projectId: number, data: Partial<AssessmentPlan>): Promise<AssessmentPlan> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/assessment/plans`, data);
    return response.data;
  },
  getPlanDetail: async (projectId: number, planId: number): Promise<AssessmentPlanDetail> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/assessment/plans/${planId}`);
    return response.data;
  },
  updatePlan: async (projectId: number, planId: number, updates: Partial<AssessmentPlan>): Promise<AssessmentPlan> => {
    const response = await apiClient.put(`/v2/projects/${projectId}/assessment/plans/${planId}`, updates);
    return response.data;
  },
  activatePlan: async (projectId: number, planId: number): Promise<AssessmentPlan> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/assessment/plans/${planId}/activate`);
    return response.data;
  },
  completePlan: async (projectId: number, planId: number): Promise<AssessmentPlan> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/assessment/plans/${planId}/complete`);
    return response.data;
  },
  deletePlan: async (projectId: number, planId: number): Promise<void> => {
    await apiClient.delete(`/v2/projects/${projectId}/assessment/plans/${planId}`);
  },
  scopeAll: async (projectId: number, planId: number): Promise<{ added: number }> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/assessment/plans/${planId}/scope-all`);
    return response.data;
  },
  upsertControl: async (
    projectId: number,
    planId: number,
    controlId: string,
    data: { methods?: AssessmentMethod[]; expected_evidence?: string; scheduled_date?: string | null; notes?: string }
  ): Promise<AssessmentPlanControl> => {
    const response = await apiClient.put(
      `/v2/projects/${projectId}/assessment/plans/${planId}/controls/${encodeURIComponent(controlId)}`,
      data
    );
    return response.data;
  },
  removeControl: async (projectId: number, planId: number, controlId: string): Promise<void> => {
    await apiClient.delete(
      `/v2/projects/${projectId}/assessment/plans/${planId}/controls/${encodeURIComponent(controlId)}`
    );
  },
  generateSap: async (projectId: number, planId: number, format: 'docx' | 'pdf' = 'docx'): Promise<Blob> => {
    // Body is `{}` not `null` â€” axios 1.x serializes `null` to the literal string
    // "null" which body-parser strict mode rejects as invalid JSON.
    const response = await apiClient.post(
      `/v2/projects/${projectId}/assessment/plans/${planId}/sap?format=${format}`,
      {},
      { responseType: 'blob', timeout: 60000 }
    );
    return response.data;
  },
  generateSar: async (projectId: number, planId: number, format: 'docx' | 'pdf' = 'docx'): Promise<Blob> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/assessment/plans/${planId}/sar?format=${format}`,
      {},
      { responseType: 'blob', timeout: 60000 }
    );
    return response.data;
  },
  previewSar: async (projectId: number, planId: number): Promise<SarPreview> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/assessment/plans/${planId}/sar/preview`);
    return response.data;
  },
};

// â”€â”€ Iris Drafting Layer API (user above the loop, Phase 1.5) â”€â”€â”€â”€â”€
export type IrisDraftStatus = 'pending' | 'accepted' | 'edited' | 'rejected';
export type IrisDecisionMode = 'individual' | 'bulk_threshold';
export type IrisConfidenceSource = 'iris_self' | 'consistency' | 'heuristic' | 'template_fallback';
export type IrisTargetType =
  | 'sap_plan_metadata'
  | 'sap_control_methods'
  | 'ssp_impl_narrative'
  | 'sar_finding_narrative'
  | 'ato_decision_memo'
  | 'conmon_plan'
  | 'fips199_suggestions'
  | 'fips199_info_type_suggestion'
  | 'rar_threat_applicability'
  | 'rar_response_recommendation'
  | 'poam_finding'
  | 'control_selection_rationale'
  | 'soc2_scope_recommendation'
  | 'soc2_system_description'
  | 'soc2_service_commitments'
  | 'soc2_components_inventory'
  | 'soc2_risk_narrative'
  | 'soc2_control_description'
  | 'soc2_control_mapping_suggestion'
  | 'soc2_cuec_inventory'
  | 'soc2_csoc_inventory'
  | 'itar_determination_rationale'
  | 'itar_tcp_section';

// Phase 1.5A drafts carry { content, field, plan_id }.
// Phase 1.5B drafts carry { control_id, methods, rationale, plan_id, ... }.
// Phase 1.5C drafts carry { control_id, narrative, project_standard_id, baseline_notes, ... }.
// Phase 1.5D drafts carry { control_id, finding_narrative, finding_class, plan_id,
//                           methods, status, evidence_titles, implementation_context, ... }.
// Phase 1.5F drafts carry { field, content, system_name, overall_impact, ao_name, ... } â€” 5 fields per memo.
// Phase 1.5G drafts carry { section, content, system_name, overall_impact, ... } â€” 5 sections per plan.
// Kept as a union so components can narrow per target_type.
export type IrisDraftContent =
  | { content: string; field?: string; plan_id?: number; [key: string]: any }
  | {
      control_id: string;
      methods: Array<'E' | 'I' | 'T'>;
      rationale: string;
      plan_id: number;
      requirement_name?: string | null;
      category?: string | null;
      priority?: string | null;
      [key: string]: any;
    };

export interface IrisDraft {
  id: number;
  project_id: number;
  target_type: IrisTargetType;
  target_id: string | null;
  draft_content: IrisDraftContent;
  confidence: number;
  confidence_source: IrisConfidenceSource;
  status: IrisDraftStatus;
  decision_mode: IrisDecisionMode | null;
  decision_threshold: number | null;
  user_decision_at: string | null;
  user_id: number | null;
  edit_content: { content?: string; methods?: Array<'E' | 'I' | 'T'> } | null;
  reject_reason: string | null;
  generated_at: string;
  iris_model_id: string | null;
  generation_batch_id: string | null;
}

export interface IrisGenerateResult {
  batch_id: string;
  drafts: IrisDraft[];
  /**
   * 'mixed' = a multi-family batch where some families reached Iris and some
   * fell back to the deterministic template. Treat it as needing the SAME
   * scrutiny as template_fallback — some of these drafts are 30%-confidence
   * template output. Per-draft confidence_source says which.
   */
  source: 'iris' | 'template_fallback' | 'mixed' | 'precondition_missing';
  /**
   * When source='precondition_missing', a human-readable reason the generate
   * was refused. Per asset-creation principle + RMF spec refuse-to-draft
   * default. Callers should render this as a UI hint rather than treating
   * the empty drafts array as a normal "no work to do" state.
   */
  precondition_reason?: string;
}

export const irisDraftAPI = {
  generate: async (
    projectId: number,
    targetType: IrisTargetType,
    targetContext: Record<string, any>,
    options?: { signal?: AbortSignal }
  ): Promise<IrisGenerateResult> => {
    // Iris generate is long-running â€” varies by target:
    //   1.5A plan metadata        : ~20-40s
    //   1.5B per-control methods  : up to 180s (one call for 110 controls)
    //   1.5C SSP narratives       : up to 300s (family-chunked wall budget)
    //   1.5D SAR finding narratives: up to 300s (same)
    //   1.5F ATO memo / 1.5G ConMon: ~30-60s
    // Axios socket must outlive the longest backend path. 360s covers the
    // 5-minute chunked cases with a minute of network + persistence overhead.
    // options.signal lets callers wire AbortController for user-Cancel /
    // hard-timeout patterns (e.g., Fips199Workflow's 90s overlay timeout).
    const response = await apiClient.post(
      `/v2/projects/${projectId}/iris/drafts/generate`,
      { target_type: targetType, target_context: targetContext },
      { timeout: 360000, signal: options?.signal }
    );
    return response.data;
  },
  list: async (
    projectId: number,
    targetType: IrisTargetType,
    includeDecided = false
  ): Promise<{ drafts: IrisDraft[]; total: number }> => {
    const response = await apiClient.get(
      `/v2/projects/${projectId}/iris/drafts?target_type=${targetType}&include_decided=${includeDecided}`
    );
    return response.data;
  },
  accept: async (projectId: number, draftId: number): Promise<IrisDraft> => {
    const response = await apiClient.put(`/v2/projects/${projectId}/iris/drafts/${draftId}`, { action: 'accept' });
    return response.data;
  },
  edit: async (
    projectId: number,
    draftId: number,
    editContent: {
      content?: string;
      methods?: Array<'E' | 'I' | 'T'>;
      rationale?: string;
      narrative?: string;
      finding_narrative?: string;
      is_applicable?: boolean; // rar_threat_applicability
      [key: string]: any;
    }
  ): Promise<IrisDraft> => {
    const response = await apiClient.put(`/v2/projects/${projectId}/iris/drafts/${draftId}`, {
      action: 'edit',
      edit_content: editContent,
    });
    return response.data;
  },
  reject: async (projectId: number, draftId: number, rejectReason?: string): Promise<IrisDraft> => {
    const response = await apiClient.put(`/v2/projects/${projectId}/iris/drafts/${draftId}`, {
      action: 'reject',
      reject_reason: rejectReason,
    });
    return response.data;
  },
  bulkAccept: async (
    projectId: number,
    targetType: IrisTargetType,
    threshold: number
  ): Promise<{ accepted: number; drafts: IrisDraft[] }> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/iris/drafts/bulk-accept`, {
      target_type: targetType,
      threshold,
      user_confirmed_review: true,
    });
    return response.data;
  },
};

// â”€â”€ OSCAL Export API (Item 2 â€” RMF Step 5) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export interface OscalArtifact {
  type: 'ssp' | 'component_definition' | 'poam' | 'assessment_plan' | 'assessment_results';
  uuid: string;
  filename: string;
  source: string;
}

export interface OscalSummary {
  project_id: number;
  project_name: string;
  oscal_version: string;
  artifact_count: number;
  component_count: number;
  ssp_control_count: number;
  poam_item_count: number;
  assessment_plan_present: boolean;
  assessment_results_present: boolean;
  artifacts: OscalArtifact[];
  cross_links: {
    ssp_uuid: string;
    poam_uuid: string | null;
    assessment_plan_uuid: string | null;
    assessment_results_uuid: string | null;
    component_uuids: string[];
  };
}

export const oscalExportAPI = {
  getSummary: async (projectId: number): Promise<OscalSummary> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/oscal/summary`);
    return response.data;
  },
  exportPackage: async (projectId: number): Promise<Blob> => {
    // Body is `{}` not `null` â€” axios 1.x serializes `null` to "null" which
    // body-parser strict mode rejects. Same fix as generateSap / generateSar.
    const response = await apiClient.post(`/v2/projects/${projectId}/oscal/export`, {}, {
      responseType: 'blob',
      timeout: 60000,
    });
    return response.data;
  },
};

// â”€â”€ Phase 1.5F / 1.5G â€” Single-artifact RMF exports â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Pass {} rather than null as the body â€” axios serializes `null` to the
// literal string "null" with Content-Type application/json, which body-parser
// strict mode rejects as invalid JSON.
export const atoMemoAPI = {
  export: async (projectId: number, format: 'docx' | 'pdf' = 'docx'): Promise<Blob> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/rmf/ato-memo/export?format=${format}`,
      {},
      { responseType: 'blob', timeout: 60000 }
    );
    return response.data;
  },
};

export const conmonPlanAPI = {
  export: async (projectId: number, format: 'docx' | 'pdf' = 'docx'): Promise<Blob> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/rmf/conmon-plan/export?format=${format}`,
      {},
      { responseType: 'blob', timeout: 60000 }
    );
    return response.data;
  },
};

export const poamAPI = {
  export: async (projectId: number, format: 'docx' | 'pdf' = 'docx'): Promise<Blob> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/poam/export?format=${format}`,
      {},
      { responseType: 'blob', timeout: 60000 }
    );
    return response.data;
  },
  cmmcExport: async (projectId: number, format: 'docx' | 'pdf' = 'docx'): Promise<Blob> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/poam/cmmc-export?format=${format}`,
      {},
      { responseType: 'blob', timeout: 60000 }
    );
    return response.data;
  },
};

// â”€â”€ Phase 2 Item 5 slice 1 â€” Risk Assessment Report API â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export type QualScale = 'very_low' | 'low' | 'moderate' | 'high' | 'very_high';

export interface RiskAssessment {
  id: number;
  project_id: number;
  project_standard_id: number | null;
  version: string;
  status: 'draft' | 'active' | 'complete';
  assessment_scope: string | null;
  assessment_approach: 'qualitative' | 'quantitative' | 'semi_quantitative';
  risk_model: string | null;
  assumptions: string | null;
  constraints: string | null;
  classification: string | null;
  assessment_date: string | null;
  generated_at: string | null;
  rar_document_generated_at: string | null;
}

export interface AssessmentHeaderUpdatePayload {
  assessment_scope?: string;
  risk_model?: string;
  assumptions?: string;
  constraints?: string;
  classification?: string;
}

export type RiskResponse = 'accept' | 'avoid' | 'mitigate' | 'transfer';

export interface RiskRegisterEntry {
  finding_id: number;
  threat_event: {
    id: number;
    event_key: string;
    name: string;
    description: string | null;
    mitre_technique_id: string | null;
  };
  threat_source: {
    id: number;
    source_key: string;
    name: string;
    class: 'adversarial' | 'accidental' | 'structural' | 'environmental';
  };
  is_applicable: boolean | null;
  applicability_rationale: string | null;
  likelihood_of_initiation: QualScale | null;
  likelihood_of_impact: QualScale | null;
  overall_likelihood: QualScale | null;
  impact_level: QualScale | null;
  risk_level: QualScale | null;
  // Slice 2 fields
  overall_likelihood_override: QualScale | null;
  impact_level_override: QualScale | null;
  override_rationale: string | null;
  risk_response: RiskResponse | null;
  response_rationale: string | null;
  poam_opened_at: string | null;
  poam_milestone_target: string | null;
  poam_remediation_plan: string | null;
  poam_closed_at: string | null;
  poam_closure_note: string | null;
}

export interface FindingUpdatePayload {
  overall_likelihood_override?: QualScale | null;
  impact_level_override?: QualScale | null;
  override_rationale?: string | null;
  risk_response?: RiskResponse | null;
  response_rationale?: string | null;
  poam_opened?: boolean;
  poam_milestone_target?: string | null;
  poam_remediation_plan?: string | null;
  poam_closed?: boolean;
  poam_closure_note?: string | null;
}

export interface RarGenerateResult {
  assessment_id: number;
  created: boolean;
  findings_seeded: number;
  findings_scored: number;
  applicability: {
    batch_id: string;
    drafts_count: number;
    source: 'iris' | 'template_fallback';
  };
}

export const rarAPI = {
  // Generate (or regenerate) â€” seeds the RA, scores deterministically, and
  // kicks Iris applicability drafting. Can take ~2 min for the Iris call on
  // a cold GPU; reuse AIProgressOverlay.
  generate: async (projectId: number): Promise<RarGenerateResult> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/rmf/rar/generate`,
      {},
      { timeout: 300000 }
    );
    return response.data;
  },
  get: async (
    projectId: number
  ): Promise<{ assessment: RiskAssessment | null; register: RiskRegisterEntry[] }> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/rmf/rar`);
    return response.data;
  },
  export: async (projectId: number, format: 'docx' | 'pdf' = 'docx'): Promise<Blob> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/rmf/rar/export?format=${format}`,
      {},
      { responseType: 'blob', timeout: 60000 }
    );
    return response.data;
  },
  // Edit the header/methodology fields on the active assessment. Omitted keys
  // are skipped server-side; '' clears a field.
  updateHeader: async (
    projectId: number,
    update: AssessmentHeaderUpdatePayload
  ): Promise<void> => {
    await apiClient.patch(`/v2/projects/${projectId}/rmf/rar`, update);
  },
  // Slice 2 â€” per-finding update: scoring overrides, risk response, POA&M
  updateFinding: async (
    projectId: number,
    findingId: number,
    update: FindingUpdatePayload
  ): Promise<void> => {
    await apiClient.patch(
      `/v2/projects/${projectId}/rmf/rar/findings/${findingId}`,
      update
    );
  },
};

// â”€â”€ Scoping & Tailoring (Sub-Initiative 6) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export type ScopingProposalAction = 'mark_na' | 'revoke_na';
export type ScopingProposalStatus =
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'edited'
  | 'superseded';

export interface ScopingProposal {
  id: number;
  project_id: number;
  project_standard_id: number;
  requirement_id: string;
  predicate_id: string;
  proposal: ScopingProposalAction;
  architectural_rationale: string;
  asset_facts_snapshot: Record<string, any>;
  status: ScopingProposalStatus;
  decided_by: number | null;
  decided_at: string | null;
  decision_notes: string | null;
  source_event_id: number | null;
  created_at: string;
  standard_id: string;
  requirement_name: string | null;
  category: string | null;
}

export interface ScopingDecisionResult {
  proposal_id: number;
  proposal: ScopingProposalAction;
  status: ScopingProposalStatus;
  requirement_id: string;
  project_standard_id: number;
  control_status_changed: boolean;
}

export const scopingAPI = {
  list: async (
    projectId: number,
    includeDecided = false
  ): Promise<{ proposals: ScopingProposal[]; total: number }> => {
    const q = includeDecided ? '?include_decided=true' : '';
    const res = await apiClient.get(`/v2/projects/${projectId}/scoping/proposals${q}`);
    return res.data;
  },

  decide: async (
    projectId: number,
    proposalId: number,
    body: {
      action: 'accept' | 'edit' | 'reject';
      edited_rationale?: string;
      decision_notes?: string;
    }
  ): Promise<ScopingDecisionResult> => {
    const res = await apiClient.put(
      `/v2/projects/${projectId}/scoping/proposals/${proposalId}`,
      body
    );
    return res.data;
  },

  bulkAccept: async (
    projectId: number,
    body: {
      user_confirmed_review: boolean;
      predicate_ids?: string[];
      source_event_id?: number;
    }
  ): Promise<{ accepted: number; results: ScopingDecisionResult[] }> => {
    const res = await apiClient.post(
      `/v2/projects/${projectId}/scoping/proposals/bulk-accept`,
      body
    );
    return res.data;
  },
};

// ---------- Iris Quality (Phase A AI Eval Harness) ----------

export interface IrisQualityRun {
  id: number;
  eval_set_version: string;
  model: string;
  model_provider: string;
  source: string;
  case_count: number;
  primary_metric: number | null;
  completed_at: string;
  notes: string | null;
}

export interface IrisQualityTask {
  task: string;
  status: 'within' | 'exceeded' | 'insufficient_data' | 'no_runs';
  latest: IrisQualityRun | null;
  baseline: {
    avg_primary_metric: number;
    sample_count: number;
    runs_used: number[];
    model: string;
    window: number;
  } | null;
  drift: {
    delta: number;
    delta_percent: number;
    exceeded: boolean;
    threshold: number;
  } | null;
  trend: Array<{
    id: number;
    primary_metric: number | null;
    completed_at: string;
    model: string;
  }>;
}

export interface IrisQualityResponse {
  checked_at: string;
  drift_threshold: number;
  baseline_window: number;
  overall_status: 'within' | 'exceeded' | 'insufficient_data';
  tasks: IrisQualityTask[];
}

export const irisQualityAPI = {
  fetch: async (threshold?: number): Promise<IrisQualityResponse> => {
    const params = threshold ? { threshold } : {};
    const r = await apiClient.get('/admin/iris/quality', { params });
    return r.data.data as IrisQualityResponse;
  },
};

// â”€â”€ Conflict resolution (AUTHORITATIVE_SOURCE_PRECEDENCE_SPEC) â”€â”€â”€â”€â”€â”€â”€â”€

export interface FieldConflict {
  id: number;
  project_id: number;
  table_schema: string;
  table_name: string;
  row_id: number;
  field_name: string;
  field_family: string;
  current_value: string | null;
  current_source: 'iris' | 'user' | 'derived' | 'imported' | 'legacy';
  current_source_at: string;
  current_source_ref: string | null;
  new_value: string;
  new_source: 'iris' | 'user' | 'derived' | 'imported' | 'legacy';
  new_source_at: string;
  new_source_ref: string | null;
  downstream_impact: Array<{ artifact_type: string; reason: string }>;
  status: 'pending' | 'accepted_new' | 'kept_existing' | 'cancelled';
  created_at: string;
  resolved_at: string | null;
  resolved_by: number | null;
  resolution_notes: string | null;
}

export const conflictsAPI = {
  listPending: async (projectId: number): Promise<FieldConflict[]> => {
    const r = await apiClient.get(`/v2/projects/${projectId}/conflicts/pending`);
    return r.data.conflicts || [];
  },
  resolve: async (
    projectId: number,
    conflictId: number,
    decision: 'accept_new' | 'keep_existing',
    notes?: string
  ): Promise<FieldConflict> => {
    const r = await apiClient.post(
      `/v2/projects/${projectId}/conflicts/${conflictId}/resolve`,
      { decision, notes }
    );
    return r.data.conflict;
  },
};

// Iris Task Queue â€” Cloud 7 Strategic substrate.
// Background-task persistence; notification center polls list() and renders
// in-flight progress bars + completed-with-link items. No client-side create
// path â€” tasks are enqueued internally by other route handlers.
export type IrisTaskStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface IrisTask {
  id: number;
  project_id: number | null;
  user_id: number | null;
  organization_id: number;
  task_type: string;
  status: IrisTaskStatus;
  payload: Record<string, any>;
  result: Record<string, any> | null;
  progress: number;
  progress_message: string | null;
  error_message: string | null;
  link_to: string | null;
  dismissed_at: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export const irisTasksAPI = {
  list: async (): Promise<IrisTask[]> => {
    const r = await apiClient.get('/v2/iris-tasks');
    return r.data.data || [];
  },
  dismiss: async (taskId: number): Promise<void> => {
    await apiClient.post(`/v2/iris-tasks/${taskId}/dismiss`);
  },
};

// Org users â€” minimal endpoint backing the assignee picker on the
// StandardAssessment page (Cloud 7 Notes slice 2). Returns active users in
// the requesting user's organization. Tenant-isolated at the backend.
export interface OrgUserSummary {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
}
export const orgUsersAPI = {
  list: async (): Promise<OrgUserSummary[]> => {
    const r = await apiClient.get('/v2/org-users');
    return r.data.data || [];
  },

  /** Organisation admins only: every user, inactive included, plus how sign-in works here. */
  manage: async (): Promise<ManagedUsersResponse> => {
    const r = await apiClient.get('/v2/org-users/manage');
    return r.data.data;
  },

  /**
   * Create a user in the caller's organisation. Deliberately NOT authAPI.register, which stores any
   * token it is given as the CURRENT session — an admin creating someone must never be signed in as them.
   */
  create: async (input: NewOrgUser): Promise<void> => {
    const body: Record<string, string> = {
      email: input.email,
      first_name: input.first_name,
      last_name: input.last_name,
      role: input.role,
    };
    if (input.password) body.password = input.password;
    if (input.entra_object_id) body.entra_object_id = input.entra_object_id;
    await apiClient.post('/auth/register', body);
  },

  deactivate: async (userId: number): Promise<void> => {
    await apiClient.put(`/v2/org-users/${userId}/deactivate`);
  },

  reactivate: async (userId: number): Promise<void> => {
    await apiClient.put(`/v2/org-users/${userId}/reactivate`);
  },

  setEntraObjectId: async (userId: number, objectId: string): Promise<void> => {
    await apiClient.put(`/v2/org-users/${userId}/entra-identity`, { object_id: objectId });
  },

  clearEntraObjectId: async (userId: number): Promise<void> => {
    await apiClient.delete(`/v2/org-users/${userId}/entra-identity`);
  },

  // Appliance only (spec 2026-09-29-appliance-administration §3): these routes 404 on hosted PRISM. The server
  // re-checks everything — an active admin, the same organisation, never yourself.
  changeRole: async (userId: number, role: string): Promise<void> => {
    await apiClient.put(`/v2/org-users/${userId}/role`, { role });
  },

  suspend: async (userId: number, reason: string): Promise<void> => {
    await apiClient.post(`/v2/org-users/${userId}/suspend`, { reason });
  },

  unsuspend: async (userId: number, reason: string): Promise<void> => {
    await apiClient.post(`/v2/org-users/${userId}/unsuspend`, { reason });
  },

  // reason: why the session is being started (NOVEC S8) — required by the server.
  viewAs: async (userId: number, reason: string): Promise<ViewAsStart> => {
    const r = await apiClient.post(`/v2/org-users/${userId}/view-as`, { reason });
    return r.data.data;
  },

  activity: async (params: { category: ActivityCategory; before: string | null }): Promise<OrgActivityPage> => {
    const r = await apiClient.get('/v2/org-users/activity', {
      params: params.before ? { category: params.category, before: params.before } : { category: params.category },
    });
    return r.data.data;
  },

  irisHealth: async (): Promise<IrisHealth> => {
    const r = await apiClient.get('/v2/org-users/iris-health');
    return r.data.data;
  },
};

export interface ManagedUser {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  is_active: boolean;
  is_superadmin: boolean;
  last_login: string | null;
  created_at: string;
  entra_object_id: string | null;
  /** Reported by /manage since appliance administration; absent from an older backend. */
  is_suspended?: boolean;
}

export interface ManagedUsersResponse {
  users: ManagedUser[];
  sign_in: {
    password_login: boolean;
    entra_enabled: boolean;
    entra_object_id_supported: boolean;
    entra_object_id_required: boolean;
  };
}

export interface NewOrgUser {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  password?: string;
  entra_object_id?: string;
}

// Appliance administration (spec 2026-09-29-appliance-administration §5, §6).
export type ActivityCategory = 'all' | 'user_management' | 'view_as' | 'sign_in';

export interface OrgActivityRow {
  id: string;
  at: string;
  action: string;
  category: 'user_management' | 'view_as' | 'sign_in' | 'other';
  actor: { user_id: number | null; email: string | null; label: string };
  target: { type: string | null; id: string | null; email: string | null };
  changes: Record<string, unknown> | null;
}

export interface OrgActivityPage {
  rows: OrgActivityRow[];
  next_before: string | null;
}

export interface IrisHealth {
  checked_at: string;
  status: 'healthy' | 'degraded' | 'down';
  reason: string;
  gpu: { reachable: boolean; model: string; model_loaded: boolean; check_ms: number; error: string | null };
  queue: { in_flight: number; waiting: number; concurrency: number; is_paused: boolean };
  calls_24h: {
    total: number;
    succeeded: number;
    success_rate: number | null;
    p50_ms: number | null;
    max_ms: number | null;
    top_errors: { error_class: string; count: number }[];
  };
}

export interface ApplianceLicence {
  mode: 'active' | 'expiring' | 'read_only';
  reason?: string | null;
  licensee?: string | null;
  licence_id?: string | null;
  expires_at?: string | null;
  days_remaining?: number | null;
  read_only_enforced: boolean;
}

export interface ViewAsStart {
  token: string;
  user: {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
    role: string;
    organization_id: number;
  };
  impersonation_session_id: string;
  mode: 'readonly';
}

export const applianceAPI = {
  /** GET /api/appliance/licence — rejects with 404 on hosted PRISM. */
  licence: async (): Promise<ApplianceLicence> => {
    const r = await apiClient.get('/appliance/licence');
    return r.data.data;
  },
};

// Task #11 Slice 3c â€” per-control comment thread + @-mention.
export interface AssessmentComment {
  id: number;
  body: string | null;
  author: { id: number | null; name: string };
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
  is_deleted: boolean;
  is_own: boolean;
}
export interface MentionableUser {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string | null;
  handle: string;
  display_name: string;
}
// Task #11 Slice 3d â€” per-control audit timeline.
export interface ActivityEvent {
  id: string;
  action: string;
  actor: { id: number | null; name: string };
  created_at: string;
  changes: Record<string, any> | null;
}
export interface ActivityCursor {
  before_ts: string;
  before_id: string;
}
export interface ActivityPage {
  data: ActivityEvent[];
  has_more: boolean;
  next_cursor: ActivityCursor | null;
}
export const commentsAPI = {
  list: async (assessmentId: number): Promise<AssessmentComment[]> => {
    const r = await apiClient.get(`/v2/compliance/assessments/${assessmentId}/comments`);
    return r.data.data || [];
  },
  create: async (assessmentId: number, body: string): Promise<AssessmentComment> => {
    const r = await apiClient.post(`/v2/compliance/assessments/${assessmentId}/comments`, { body });
    return r.data.data;
  },
  edit: async (assessmentId: number, commentId: number, body: string): Promise<AssessmentComment> => {
    const r = await apiClient.patch(
      `/v2/compliance/assessments/${assessmentId}/comments/${commentId}`,
      { body }
    );
    return r.data.data;
  },
  remove: async (assessmentId: number, commentId: number): Promise<void> => {
    await apiClient.delete(`/v2/compliance/assessments/${assessmentId}/comments/${commentId}`);
  },
  mentionableUsers: async (projectId: number): Promise<MentionableUser[]> => {
    const r = await apiClient.get(`/v2/compliance/projects/${projectId}/mentionable-users`);
    return r.data.data || [];
  },
  activity: async (assessmentId: number, cursor?: ActivityCursor | null): Promise<ActivityPage> => {
    const params = new URLSearchParams({ limit: '50' });
    if (cursor) {
      params.set('before_ts', cursor.before_ts);
      params.set('before_id', cursor.before_id);
    }
    const r = await apiClient.get(
      `/v2/compliance/assessments/${assessmentId}/activity?${params.toString()}`
    );
    return { data: r.data.data || [], has_more: !!r.data.has_more, next_cursor: r.data.next_cursor ?? null };
  },
};

// ---------------------------------------------------------------------------
// Parallax TTX API â€” flag-gated on PARALLAX_TTX_ENABLED (backend)
// ---------------------------------------------------------------------------
export const parallaxTtxAPI = {
  createSession: (projectId: number, body: any = {}) =>
    apiClient.post(`/v2/projects/${projectId}/parallax-ttx/sessions`, body).then(r => r.data),
  getSession: (projectId: number, sid: number) =>
    apiClient.get(`/v2/projects/${projectId}/parallax-ttx/sessions/${sid}`).then(r => r.data),
  capture: (projectId: number, sid: number, order: number, body: { belief: number; stage?: string; controlId?: string }) =>
    apiClient.post(`/v2/projects/${projectId}/parallax-ttx/sessions/${sid}/injects/${order}/capture`, body).then(r => r.data),
  reveal: (projectId: number, sid: number, order: number) =>
    apiClient.post(`/v2/projects/${projectId}/parallax-ttx/sessions/${sid}/injects/${order}/reveal`, {}).then(r => r.data),
  draftAfterAction: (projectId: number, sid: number) =>
    apiClient.post(`/v2/projects/${projectId}/parallax-ttx/sessions/${sid}/after-action`, {}).then(r => r.data),
  finalize: (projectId: number, sid: number) =>
    apiClient.post(`/v2/projects/${projectId}/parallax-ttx/sessions/${sid}/finalize`, {}).then(r => r.data),
};

export { apiClient };
export default apiClient;

// ── RMF Pre-Adjudication API (Task 4 — T1 + T3 + T4 client) ─────────────────
// Backend: backend/src/routes/rmfPreAdjudication.ts
// Flag-gated: backend routes 404 when RMF_PREADJUDICATION_ENABLED is off.
// The View handles 404 gracefully — do NOT throw from here on 404.

export interface RmfPackage {
  package_id: number;
  project_id: number;
  framework_id: string | null;
  baseline: string | null;
  name: string | null;
  created_at: string;
  evidence_count: number;
}

export interface RmfIntakeResult {
  package_id: number;
  project_id: number;
  framework_id: string | null;
  baseline: string | null;
  name: string | null;
  attached_count: number;
  created_at: string;
}

/** Per-control row from the JSON readiness endpoint. */
export interface RmfControlRow {
  control_id: string;
  control_name: string;
  verdict: 'full' | 'partial' | 'none' | string;
  confidence: number;
  is_gap: boolean;
  source_label: string;
  remediation: string;
}

/** Structured readiness data from GET .../readiness */
export interface RmfReadinessResult {
  band: 'ready' | 'conditional' | 'not_ready';
  weighted_score: number;
  breakdown: {
    sufficient: number;
    partial: number;
    insufficient: number;
    not_applicable: number;
    applicable_count: number;
  };
  controls: RmfControlRow[];
}

export const rmfPreAdjAPI = {
  // TODO follow-on: intake UI — not wired to a View action yet; packages are
  // currently created externally (CLI / Evidence Locker flow).
  // intake: async (...) => { ... }

  /**
   * List all authorization packages for this project.
   * GET /projects/:projectId/rmf-packages
   * Throws on non-404 errors; 404 is handled by the View (flag off).
   */
  listPackages: async (projectId: number, signal?: AbortSignal): Promise<RmfPackage[]> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/rmf-packages`, { signal });
    return response.data.data ?? [];
  },

  /**
   * Fetch structured readiness data (band + breakdown + per-control table).
   * GET /projects/:projectId/rmf-packages/:packageId/readiness
   *
   * This is the REAL data source for the band card and per-control table.
   * Reuses assembleReport server-side — no Iris re-run, no writes.
   */
  getReadiness: async (
    projectId: number,
    packageId: number,
    signal?: AbortSignal
  ): Promise<RmfReadinessResult> => {
    const response = await apiClient.get(
      `/v2/projects/${projectId}/rmf-packages/${packageId}/readiness`,
      { signal, timeout: 120000 }
    );
    return response.data as RmfReadinessResult;
  },

  /**
   * Download the pre-adjudication readiness report DOCX.
   * POST /projects/:projectId/rmf-packages/:packageId/report
   * Body: {} (required — see CLAUDE.md body-parser note)
   * Returns a Blob for download; band is available in the JSON readiness
   * endpoint — use getReadiness() for structured data.
   */
  downloadReportDocx: async (
    projectId: number,
    packageId: number,
    signal?: AbortSignal
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/rmf-packages/${packageId}/report`,
      {},
      { responseType: 'blob', timeout: 120000, signal }
    );

    const filename = `preadj-report-${packageId}.docx`;
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    return { blob, filename };
  },
};

// ── AI Conformance Journey ──────────────────────────────────────────────────
// Thin binding for the gated 6-step AI posture journey (Task 7 routes under
// /api/v2/projects/:id/ai-journey). Response shapes reuse the generic
// JourneyState/JourneyStepDetail/AdvanceResult types (journey/types.ts) —
// this journey has no target_level and getSteps returns the array directly
// (unlike soc2/rmf's { steps: [...] } wrapper), so no journey-specific
// interfaces are needed here.
// AI Conformance Journey wire types.
//
// The three-valued fields are typed `| null` ON PURPOSE and must stay that way:
// `inventory_source: null` is UNRECORDED (never 'extracted') and a
// determination's `reachable: null` is NOT DETERMINED (never false). Widening
// either to a non-nullable type would push a `??` default into every consumer,
// and a defaulted "no CUI reaches this system" is the false negative that fails
// a real CMMC assessment.
export type AiInventorySource = 'extracted' | 'declared' | 'discovered';

// `response` is `| null` on purpose and must stay that way: null is NOBODY HAS
// ANSWERED, never 'no'. The server emits the key for every category, answered
// or not, so a `??` default here would turn "not yet attested" into an
// attestation — the expensive direction to be wrong in on this surface.
export interface AiChallengeCategory {
  key: string;
  label: string;
  prompt: string;
  response: 'yes' | 'no' | 'unsure' | null;
  notes: string | null;
}

// Which regulated-data question step 4 asks, derived on the SERVER from the
// union of the project's module flags and its ATTACHED STANDARDS — either
// signal is sufficient, so a project with `hipaa-security` attached gets the
// PHI question even with `module_hipaa` off. `cui` for CMMC/800-171, `itar` for
// ITAR, `phi` for HIPAA, and `generic` only when NEITHER signal matches
// anything. Never chosen by the client: the write endpoint refuses a regime the
// project does not carry.
export type AiRegimeKey = 'cui' | 'itar' | 'phi' | 'generic';

export interface AiRegime {
  key: AiRegimeKey;
  label: string;
  /** Contains a literal `{system}` placeholder — substitute the system name. */
  questionTemplate: string;
  justificationPrompt: string;
  basis: string;
}

// A regime with NO entry in an asset's `determinations` is NOT DETERMINED, the
// same as one whose `reachable` is null. Neither is "no".
export interface AiAssetDetermination {
  regime: AiRegimeKey;
  reachable: boolean | null;
  justification: string | null;
  determined_at: string | null;
}

export interface AiJourneyAsset {
  asset_id: number;
  name: string;
  inventory_source: AiInventorySource | null;
  business_owner: string | null;
  decision_authority: string | null;
  human_oversight_mechanism: string | null;
  escalation_triggers: string | null;
  ai_lifecycle_stage: string | null;
  determinations: AiAssetDetermination[];
}

// The regimes ride WITH the assets on purpose: step 4 renders one card per
// (system, regime), and from two separate fetches a question that has not
// arrived yet looks exactly like a question that does not apply.
export interface AiJourneyAssetsResponse {
  regimes: AiRegime[];
  assets: AiJourneyAsset[];
}

export interface AiCuiPath {
  aiAssetId: number;
  aiAssetName: string;
  counterpartAssetId: number | null;
  counterpartName: string | null;
  dataClassification: string;
  direction: 'inbound' | 'outbound';
}

export type AiReachState = 'yes' | 'no' | 'not determined';

export interface AiPostureReportResponse {
  report: {
    asOf: string;
    regimes: { key: AiRegimeKey; label: string }[];
    inventory: {
      assetId: number;
      name: string;
      provenance: AiInventorySource | 'unrecorded';
      /** One entry per APPLICABLE regime, answered or not. */
      determinations: { regime: AiRegimeKey; label: string; state: AiReachState }[];
    }[];
    counts: {
      systems: number;
      provenanceUnrecorded: number;
      // Per regime and never summed: "CUI can reach X" and "PHI can reach X"
      // are distinct findings, and one total would let an answered regime
      // stand in for an unanswered one.
      byRegime: { regime: AiRegimeKey; label: string; reachable: number; notDetermined: number }[];
    };
  };
  poam: {
    source: 'challenge' | 'characterization' | 'reachability' | 'conformance';
    title: string;
    detail: string;
  }[];
}

export const aiJourneyAPI = {
  getState: async (projectId: number): Promise<JourneyState> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/ai-journey/state`);
    return response.data;
  },
  getSteps: async (projectId: number): Promise<JourneyStepDetail[]> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/ai-journey/steps`);
    return response.data;
  },
  initialize: async (projectId: number): Promise<JourneyState> => {
    const response = await apiClient.post(`/v2/projects/${projectId}/ai-journey/initialize`, {});
    return response.data;
  },
  // A 409 here is a normal refusal (gate not satisfied for the current
  // step), not a failure — the route returns 200 when it advances and 409
  // with the same { advanced: false, current_step, gate: { missing },
  // message } body otherwise. Without validateStatus, axios's default
  // (2xx-only) would reject the promise on 409 and JourneyShell's
  // handleAdvance would show a generic "Advance failed" instead of the
  // gate's actual blocking reasons. Letting 409 resolve like a normal
  // response routes it through JourneyShell's existing
  // `if (res.advanced) ... else setError(res.gate?.missing...)` branch.
  advance: async (projectId: number): Promise<AdvanceResult> => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/ai-journey/advance`,
      {},
      { validateStatus: (status) => (status >= 200 && status < 300) || status === 409 }
    );
    return response.data;
  },
  // The challenge list AND this project's recorded answers, in one response —
  // a category rendered before its answer arrives reads exactly like an
  // unanswered one, so the two must never come from separate fetches.
  getChallengeCategories: async (projectId: number): Promise<AiChallengeCategory[]> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/ai-journey/challenge-categories`);
    return response.data;
  },
  // The read the step 2/3/4 inputs bind to. The gate's `missing` list names
  // assets but carries no ids, so this is the only way to put an input next to
  // one. It also carries the project's applicable regimes, which is what step 4
  // renders its per-system questions from. `inventory_source` arrives NULL when
  // nobody recorded it, and a regime the user has not judged has NO entry in
  // `determinations` — never coalesce either on the way in.
  getAssets: async (projectId: number): Promise<AiJourneyAssetsResponse> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/ai-journey/assets`);
    return response.data;
  },
  // Step 2 write path: records HOW a system got into the inventory. NULL
  // (unrecorded) is what this clears; there is deliberately no way to write
  // NULL back.
  triageAsset: async (
    projectId: number,
    assetId: number,
    body: { inventory_source: 'extracted' | 'declared' | 'discovered' }
  ) => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/ai-journey/assets/${assetId}/triage`,
      body
    );
    return response.data;
  },
  // The evidence a step 4 determination is judged against: CUI-classified data
  // flows with an AI asset at one end. An empty array is a real answer ("no
  // path found"), not a loading state.
  getCuiPaths: async (projectId: number): Promise<AiCuiPath[]> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/ai-journey/cui-paths`);
    return response.data;
  },
  answerChallenge: async (
    projectId: number,
    body: { category_key: string; response: string; notes?: string }
  ) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/ai-journey/challenge`, body);
    return response.data;
  },
  saveStepInputs: async (projectId: number, step: number, body: Record<string, unknown>) => {
    const response = await apiClient.post(`/v2/projects/${projectId}/ai-journey/steps/${step}/inputs`, body);
    return response.data;
  },
  // Step 3 write path. A partial body is legitimate — the server accepts any
  // subset of the five accountability fields, so a caller can fill them in over
  // several passes. All five must eventually be set for the step 3 gate to pass.
  characterizeAsset: async (
    projectId: number,
    assetId: number,
    body: {
      business_owner?: string;
      decision_authority?: string;
      human_oversight_mechanism?: string;
      escalation_triggers?: string;
      ai_lifecycle_stage?: string;
    }
  ) => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/ai-journey/assets/${assetId}/characterize`,
      body
    );
    return response.data;
  },
  // Step 4 write path, one determination per (system, regime). `reachable` is a
  // real boolean, never a string and never omitted: an absent or NULL
  // determination means NOT DETERMINED, and the server refuses anything that
  // would record a determination nobody actually made. `regime` must be one the
  // project carries — the server rejects any other with a 400, because a
  // determination for a regime nobody assesses is not a fact.
  determineReachability: async (
    projectId: number,
    assetId: number,
    body: { regime: AiRegimeKey; reachable: boolean; justification: string }
  ) => {
    const response = await apiClient.post(
      `/v2/projects/${projectId}/ai-journey/assets/${assetId}/reachability`,
      body
    );
    return response.data;
  },
  // Step 6 terminal artifact: the AI Posture Report plus the AI POA&M derived
  // from the same gate evaluations the wizard blocks on.
  getReport: async (projectId: number): Promise<AiPostureReportResponse> => {
    const response = await apiClient.get(`/v2/projects/${projectId}/ai-journey/report`);
    return response.data;
  },
};

// ── PRISM help guide (spec 2026-10-25-prism-help-guide-design) ──────────

export interface HelpSearchBody {
  clientSearchId: string;
  query: string;
  bestSlug: string | null;
  openedSlug: string | null;
}

export interface HelpTrends {
  windowStarts: string[];
  topQuestions: { query: string; count: number; bestSlug: string | null; weekly: number[] }[];
  noMatch: { query: string; count: number; weekly: number[] }[];
  byOrg: { organizationId: number; name: string; isTest: boolean; searches: number; noMatch: number }[];
}

export const helpAPI = {
  getGuide: async (): Promise<string> => {
    const r = await apiClient.get('/help');
    return r.data.data.markdown as string;
  },
  saveSearch: async (body: HelpSearchBody): Promise<void> => {
    await apiClient.post('/help/searches', body);
  },
  getTrends: async (includeInternal: boolean): Promise<HelpTrends> => {
    const r = await apiClient.get('/admin/help/trends', { params: { includeInternal } });
    return r.data.data as HelpTrends;
  },
};
